import React, { useState, useEffect, useCallback, useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useBackNavigation } from '../utils/backNavigation';
import { resolveStudioLogoUrl } from '../utils/studioLogo';
import { appendRange, knitRanges } from '../utils/timeRanges';
import TimeRangeAudit from '../components/TimeRangeAudit';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  IconButton,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Switch,
  TextField,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Fade,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  MoreVert,
  ContentCopy,
  Email,
  Phone,
  LocationOn,
  Schedule,
  Event,
  Class,
  Star,
  People,
  Payments,
  Add,
  Person,
  Delete,
  Payment,
  Business,
  Description,
  Settings,
  Close,
  AttachMoney,
  Campaign,
  Image as ImageIcon,
  AddPhotoAlternate,
  EventBusy,
  AccountTree,
  Announcement as AnnouncementIcon,
  Send,
  Visibility,
  DeleteOutline,
  CalendarMonth,
  Tune,
  KeyboardArrowDown,
  ChevronLeft,
  Search,
  CloudUpload,
  Groups,
  Collections,
  QuestionAnswer,
  BeachAccess,
  ArrowUpward,
  ArrowDownward,
  Inventory,
  CheckCircleOutline,
  Save,
  FileDownloadOutlined,
  AddLocationAlt,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { colors } from '../theme/theme';
import { useAuth } from '../contexts/AuthContext';
import BranchesTab from '../components/BranchesTab';
import StudioPacksTab from '../components/StudioPacksTab';
import CreateSessionsDialog from '../components/CreateSessionsDialog';
import CopyDayDialog from '../components/CopyDayDialog';
import BulkDeleteSessionDialog from '../components/BulkDeleteSessionDialog';
import BulkSessionWorkspace from '../components/BulkSessionWorkspace';
import StudioUsersPage from './StudioUsersPage';
import EventFormDialog from '../components/EventFormDialog';
import SessionFormDialog from '../components/SessionFormDialog';
import InstructorFormDialog from '../components/InstructorFormDialog';
import ClassFormDialog from '../components/ClassFormDialog';
import CreateClassAtLocationsDialog from '../components/multiLocation/CreateClassAtLocationsDialog';
import ImportClassesDialog from '../components/multiLocation/ImportClassesDialog';
import ImportSessionsDialog from '../components/multiLocation/ImportSessionsDialog';
import { useMultiLocationAvailable } from '../components/multiLocation/shared';
import StudioFormDialog from '../components/StudioFormDialog';
import AdvertisementFormDialog from '../components/AdvertisementFormDialog';
import CardImageWithPlaceholder from '../components/CardImageWithPlaceholder';
import { TabListLoading, TabGridLoading } from '../components/TabLoadingSkeletons';
import { isOwnerRole, isStudioScopedRole } from '../utils/roles';
import { useMyLocations, locationsWithRole } from '../hooks/useMyLocations';
import { LocationProfileBar, LocationProfileLanding } from '../components/studioProfile/LocationProfiles';
import { studioIdOf, splitInstructorLists } from '../utils/studioOf';
import AttendanceSettingsCard from '../components/attendance/AttendanceSettingsCard';

interface Studio {
  _id: string;
  studioName: string;
  email: string;
  phoneNumber: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  images?: string[];
  description: string;
  isActive: boolean;
  premiumStudio: boolean;
  studioType: 'wellness' | 'workout' | 'event';
  isSubscribed: boolean;
  subscriptionEndDate?: string;
  rating: number;
  totalReviews?: number;
  workingHoursFrom?: string;
  workingHoursTo?: string;
  location?: {
    type: string;
    coordinates: [number, number]; // [longitude, latitude]
  };
  studioPermissions: {
    isAdmin: boolean;
    allowClassCreation: boolean;
    allowEventCreation: boolean;
    canCreateManualBookings: boolean;
    enableSessionTypes?: boolean;
    studioQrEnabled?: boolean;
    oneDayOneSession?: boolean;
    oneSessionPerTimeSlot?: boolean;
    enableWellness?: boolean;
    enableMerchandise?: boolean;
    enableSeatSelection?: boolean;
    enableBulkPackBooking?: boolean;
    enableGuestBooking?: boolean;
    enableMessaging?: boolean;
    enableReferrals?: boolean;
    enableMilestones?: boolean;
    listOnSweatSocial?: boolean;
    enableNoShowPackPenalty?: boolean;
  };
  freezePolicy?: { enabled: boolean; maxDaysPerMonth: number; rolloverFreezedays?: boolean; usePackDurationFreeze?: boolean };
  merchandisePolicy?: { allowUserCancellation?: boolean; cancellableUntilStatus?: string; refundMode?: string };
  whitelabelConfig?: {
    appName?: string;
    deepLinkScheme?: string;
    /** Master switch for this studio's public booking website. */
    enableBookingWebsite?: boolean;
    /** Subdomain label; falls back to appSource with separators stripped. */
    bookingSlug?: string;
    /** The studio's own host for the booking website (book.example.com). */
    bookingDomain?: string;
    appSource?: string;
    /** Opening day — packs bought before it cannot start earlier. */
    packStartFloorDate?: string | null;
  };
  isGstRegistered?: boolean;
  gstNumber?: string | null;
  gstType?: 'exclusive' | 'inclusive';
  accountNumber?: string;
  ifscCode?: string;
  accountHolderName?: string;
  bankDetailsPopupFrequency?: 'one-time' | 'weekly' | 'monthly';
  lastBankDetailsPopupShown?: string;
  enableTrialClasses?: boolean;
  packOnlyBooking?: boolean;
  /** The brand this studio belongs to, if any. Replaces isBranchParent/parentStudio. */
  brand?: { _id: string; name: string; slug?: string; appSource?: string } | null;
  branchName?: string | null;
  branchCode?: string | null;
  branchIsActive?: boolean;
  createdAt: string;
}

interface StudioEvent {
  _id: string;
  eventName: string;
  date: string;
  price: number;
  slots: number;
  images: string[];
  description: string;
  skillLevel: string;
  interestedUsers?: any[];
}

interface Session {
  _id: string;
  className: string;
  date: string;
  price: number;
  slots: number;
  availableSlots?: number;
  duration: number;
  instructor?: {
    name: string;
  };
  classType?: {
    _id: string;
    title: string;
  };
}

interface ClassItem {
  _id: string;
  className: string;
  description: string;
  image?: string;
  duration: number;
  price: number;
  instructor?: {
    name: string;
    image?: string;
  };
  classType?: {
    title: string;
  };
  payAtStudio: boolean;
}

interface Instructor {
  _id: string;
  name: string;
  email?: string;
  phoneNumber: string;
  image?: string;
  gender: 'Male' | 'Female' | 'Other';
  description?: string;
  isDisabled?: boolean;
}

const getISTDateString = (date: Date = new Date()): string => {
  const ist = new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  return `${ist.getFullYear()}-${String(ist.getMonth() + 1).padStart(2, '0')}-${String(ist.getDate()).padStart(2, '0')}`;
};

/**
 * The IST calendar day of an instant, as the yyyy-mm-dd a date input wants.
 * The stored floor is midnight IST; reading it back with the browser's local
 * zone would show the previous day for anyone west of India.
 */
function istDayKeyFromIso(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const ist = new Date(d.getTime() + 5.5 * 60 * 60 * 1000);
  return ist.toISOString().slice(0, 10);
}

const StudioDetailPage: React.FC = () => {
  const { studioId } = useParams<{ studioId: string }>();
  // This page is always about ONE studio: studio roles pin its session/event
  // reads to it, so "All locations" (`__all__`) never widens them.
  const pagePin = apiService.isStudio() ? studioId : undefined;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const handleBack = useBackNavigation('/studios');
  const { admin } = useAuth();
  const isStudioRole = isStudioScopedRole(admin?.role);
  const isSuperAdmin = admin?.role === 'super_admin';
  
  const handleEventClick = (eventId: string) => {
    navigate(`/events/${eventId}`);
  };
  
  const handleSessionClick = (sessionId: string) => {
    navigate(`/sessions/${sessionId}`);
  };
  
  const handleClassClick = (classId: string) => {
    navigate(`/classes/${classId}`);
  };
  const [activeTab, setActiveTab] = useState(0);
  // Deep-link: ?tab=settings (from the header Settings icon) opens the Settings tab.
  useEffect(() => {
    if (searchParams.get('tab') === 'settings') setActiveTab(17);
  }, [searchParams]);

  // ── A PROFILE FOR SOMEONE WHO RUNS SEVERAL LOCATIONS ────────────────────
  //
  // An owner or manager holding two or more locations gets a location-first
  // profile (see studioProfile/LocationProfiles): under "All locations" they
  // choose which location to manage; on one location a bar says which it is.
  // The tabs that only repeat a side-panel page (Events, Sessions, Classes,
  // Packs, Instructors, Staff, Locations) are left out for them — those pages
  // already cover every location. Single-location studios, instructors and
  // platform admins get exactly the page they had.
  const { locations: myLocations, loaded: myLocationsLoaded, roleByStudio, selectedLocationId } = useMyLocations();
  const manageableIds = locationsWithRole(roleByStudio, 'manager');
  const manageableLocations = myLocations.filter((l) => (manageableIds ? manageableIds.includes(l._id) : true));
  const brandProfile = isStudioRole && admin?.role !== 'instructor' && manageableLocations.length >= 2;
  const REPEATED_TABS = [1, 2, 3, 18, 4, 9, 10];
  const hiddenForBrand = (tab: number) => brandProfile && REPEATED_TABS.includes(tab);
  useEffect(() => {
    if (hiddenForBrand(activeTab)) setActiveTab(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandProfile, activeTab]);
  const [studio, setStudio] = useState<Studio | null>(null);
  const [events, setEvents] = useState<StudioEvent[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [daySessions, setDaySessions] = useState<Session[]>([]);
  const [monthSessions, setMonthSessions] = useState<Record<string, Session[]>>({});
  // Lazily-loaded cache for individual past dates — never bulk-fetched as part of a
  // month load, only when that specific past date is actually clicked.
  const [pastDaySessions, setPastDaySessions] = useState<Record<string, Session[]>>({});
  const [isLoadingDaySessions, setIsLoadingDaySessions] = useState(false);
  const [daySessionsError, setDaySessionsError] = useState<string | null>(null);
  const [classes, setClasses] = useState<ClassItem[]>([]);
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [, setIsLoadingSessions] = useState(false);
  const [isLoadingClasses, setIsLoadingClasses] = useState(true);
  const [isLoadingInstructors, setIsLoadingInstructors] = useState(false);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [, setSessionsError] = useState<string | null>(null);
  const [classesError, setClassesError] = useState<string | null>(null);
  const [instructorsError, setInstructorsError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Dialog states
  const [createEventDialogOpen, setCreateEventDialogOpen] = useState(false);
  const [createSessionDialogOpen, setCreateSessionDialogOpen] = useState(false);
  // This studio's brand has 2+ locations: offer creating at several of them
  // and importing from another. Single-location studios see nothing new.
  const multiLocation = useMultiLocationAvailable(studioId);
  const [multiOpen, setMultiOpen] = useState<null | 'class' | 'session' | 'importClasses' | 'importSessions'>(null);
  const [createInstructorDialogOpen, setCreateInstructorDialogOpen] = useState(false);
  const [createClassDialogOpen, setCreateClassDialogOpen] = useState(false);
  const [editStudioDialogOpen, setEditStudioDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<StudioEvent | null>(null);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [selectedClass, setSelectedClass] = useState<ClassItem | null>(null);
  const [selectedInstructor, setSelectedInstructor] = useState<Instructor | null>(null);
  // Removing an instructor. `removeWork` is only set once the BACKEND has
  // refused — its presence is what turns the confirm into a handover, so the
  // picker never appears for an instructor with an empty schedule.
  const [removeInstructorOpen, setRemoveInstructorOpen] = useState(false);
  // Opt-IN, deliberately unchecked: messaging people who already paid is the
  // studio's call, not a side effect of tidying up the instructor list.
  const [removeNotifyMembers, setRemoveNotifyMembers] = useState(false);
  const [instructorToRemove, setInstructorToRemove] = useState<Instructor | null>(null);
  const [isRemovingInstructor, setIsRemovingInstructor] = useState(false);
  const [isEnablingInstructor, setIsEnablingInstructor] = useState(false);
  const [removeWork, setRemoveWork] = useState<{
    sessionCount: number; classCount: number; eventCount: number;
    wellnessPoolCount: number; serviceCount: number; bookingCount: number;
    // Distinct people, not bookings — one member with six bookings is one
    // person to notify, and that is the number the checkbox is about.
    memberCount: number;
    nextSessionAt: string | null;
  } | null>(null);
  const [removeReplacementId, setRemoveReplacementId] = useState('');
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [instructorAnchorEl, setInstructorAnchorEl] = useState<null | HTMLElement>(null);
  const [classAnchorEl, setClassAnchorEl] = useState<null | HTMLElement>(null);
  const [eventAnchorEl, setEventAnchorEl] = useState<null | HTMLElement>(null);
  const [sessionAnchorEl, setSessionAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteEventDialogOpen, setDeleteEventDialogOpen] = useState(false);
  const [deleteSessionDialogOpen, setDeleteSessionDialogOpen] = useState(false);
  const [deleteStudioDialogOpen, setDeleteStudioDialogOpen] = useState(false);
  const [isDeletingStudio, setIsDeletingStudio] = useState(false);
  const [studioMenuAnchorEl, setStudioMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [editEventDialogOpen, setEditEventDialogOpen] = useState(false);
  // "Duplicate" opens the event form in create-mode pre-filled from selectedEvent (not live).
  const [duplicateEventDialogOpen, setDuplicateEventDialogOpen] = useState(false);
  const [editSessionDialogOpen, setEditSessionDialogOpen] = useState(false);
  const [editClassDialogOpen, setEditClassDialogOpen] = useState(false);
  const [editInstructorDialogOpen, setEditInstructorDialogOpen] = useState(false);
  
  // Studio photos state
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [photoUploadError, setPhotoUploadError] = useState<string | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const studioDetailsFetchIdRef = useRef(0);
  const eventsFetchIdRef = useRef(0);
  const sessionsFetchIdRef = useRef(0);
  const daySessionsFetchIdRef = useRef(0);
  const monthSessionsFetchIdRef = useRef<Record<string, number>>({});
  const loadedMonthsRef = useRef<Set<string>>(new Set());
  const pastDayFetchIdRef = useRef<Record<string, number>>({});
  const loadedDaysRef = useRef<Set<string>>(new Set());
  const classesFetchIdRef = useRef(0);
  const instructorsFetchIdRef = useRef(0);
  const revenueFetchIdRef = useRef(0);
  const advertisementsFetchIdRef = useRef(0);

  // Advertisements state
  const [advertisements, setAdvertisements] = useState<any[]>([]);
  const [advertisementsLoading, setAdvertisementsLoading] = useState(false);
  const [adFormDialogOpen, setAdFormDialogOpen] = useState(false);
  const [selectedAd, setSelectedAd] = useState<any>(null);
  const [adAnchorEl, setAdAnchorEl] = useState<null | HTMLElement>(null);
  const [adTogglingId, setAdTogglingId] = useState<string | null>(null);

  const [sessionSearchTerm, setSessionSearchTerm] = useState('');

  // Sessions tab: selected calendar date
  const [selectedCategoryDate, setSelectedCategoryDate] = useState<string>(() => getISTDateString());
  // Sessions tab: Workout | Wellness view. 'workout' (default) keeps behaviour unchanged.
  const [sessionCategory, setSessionCategory] = useState<'workout' | 'wellness'>('workout');
  // Wellness toggle shows ONLY for wellness-offering studios; workout-only studios
  // (Purple Pilates, HoP, P&P, Laya) never see it — Sessions tab unchanged.
  const sessionsOfferWellness =
    (studio as any)?.studioPermissions?.enableWellness === true ||
    (studio as any)?.studioType === 'wellness' ||
    (Array.isArray((studio as any)?.studioTypes) &&
      (studio as any).studioTypes.includes('wellness'));
  const [copyDayDialogOpen, setCopyDayDialogOpen] = useState(false);
  // Sessions tab: Copy Day, Bulk Edit / Delete and Import live in one menu —
  // the same two controls as the Sessions page.
  const [sessionBulkMenuAnchor, setSessionBulkMenuAnchor] = useState<HTMLElement | null>(null);
  const [bulkDeleteSessionDialogOpen, setBulkDeleteSessionDialogOpen] = useState(false);
  const [bulkSessionWorkspaceOpen, setBulkSessionWorkspaceOpen] = useState(false);
  
  // Permissions state
  const [permissions, setPermissions] = useState({
    isAdmin: false,
    allowClassCreation: false,
    allowEventCreation: false,
    canCreateManualBookings: false,
    enableTrialClasses: false,
    packOnlyBooking: false,
    isActive: false,
    isSubscribed: false,
    enableSessionTypes: false,
    studioQrEnabled: false,
    oneDayOneSession: false,
    oneSessionPerTimeSlot: false,
    enableWellness: false,
    enableMerchandise: false,
    enableSessionWaitlist: false,
    enableSeatSelection: false,
    enableBulkPackBooking: false,
    enableGuestBooking: false,
    enableMessaging: false,
    enableReferrals: false,
    enableMilestones: false,
    // Defaults ON, like the schema: a studio is listed unless it opts out.
    listOnSweatSocial: true,
    enableNoShowPackPenalty: false,
    bookingCutoffHours: 2,
    bookingCutoffSchedule: [] as { startTime: string; endTime: string; cutoffHours: number }[],
    sessionReminderEnabled: false,
    sessionReminderHoursBefore: 2.5,
  });

  const [permissionsChanged, setPermissionsChanged] = useState(false);
  // Drives the floating "unsaved changes" bar so a save button is visible
  // without scrolling, no matter how far down the tab the edited field is.
  const [savingAllChanges, setSavingAllChanges] = useState(false);
  // Toggle: use time-of-day booking cutoffs (ranges) vs a single flat value.
  // Shared across the super-admin Permissions tab and the owner Settings tab
  // since both edit the same permissions.bookingCutoffSchedule array.
  const [useBookingSchedule, setUseBookingSchedule] = useState(false);

  // Freeze policy state
  const [freezePolicy, setFreezePolicy] = useState({ enabled: false, maxDaysPerMonth: 7, rolloverFreezedays: false, usePackDurationFreeze: false });
  const [freezePolicyChanged, setFreezePolicyChanged] = useState(false);
  // Retail policy. Defaults mirror the schema's deliberately conservative ones:
  // no self-service cancellation, and refunds are an explicit admin action.
  const [merchPolicy, setMerchPolicy] = useState({ allowUserCancellation: false, cancellableUntilStatus: 'PACKED', refundMode: 'manual' });
  const [merchPolicyChanged, setMerchPolicyChanged] = useState(false);

  // Cancellation policy state
  const [cancellationPolicy, setCancellationPolicy] = useState({
    enabled: true,
    session: {
      type: 'standard' as 'standard' | 'custom',
      customHours: 24,
      schedule: [] as { startTime: string; endTime: string; cutoffHours: number }[],
    },
    event: { type: 'standard' as 'standard' | 'custom', customHours: 24 },
  });
  const [cancellationPolicyChanged, setCancellationPolicyChanged] = useState(false);
  // Refund policy travels with the cancellation tab: the cutoff decides WHETHER
  // a refund is owed, this decides how it is paid.
  const [refundPolicy, setRefundPolicy] = useState({
    // Matches the backend default, so the form never briefly shows a mode the
    // studio is not actually on while the fetch is in flight.
    mode: 'manual' as 'auto' | 'manual' | 'none',
    autoOnStudioCancel: false,
  });
  // Toggle: use time-of-day cancellation cutoffs (ranges) for sessions vs the flat window.
  const [useCancellationSchedule, setUseCancellationSchedule] = useState(false);

  // GST settings state
  const [gstSettings, setGstSettings] = useState({
    isGstRegistered: true,
    gstNumber: '',
    gstType: 'exclusive' as 'exclusive' | 'inclusive',
  });
  const [gstSettingsChanged, setGstSettingsChanged] = useState(false);

  // Bank details state
  const [editBankDetailsDialogOpen, setEditBankDetailsDialogOpen] = useState(false);
  const [bankDetailsPopupFrequency, setBankDetailsPopupFrequency] = useState<'one-time' | 'weekly' | 'monthly'>('weekly');
  const [isUpdatingBankDetails, setIsUpdatingBankDetails] = useState(false);

  // Location (lat/long) state
  const [editLocationDialogOpen, setEditLocationDialogOpen] = useState(false);
  const [latitude, setLatitude] = useState<string>('');
  const [longitude, setLongitude] = useState<string>('');
  const [isUpdatingLocation, setIsUpdatingLocation] = useState(false);

  // Revenue state
  const [revenueDetails, setRevenueDetails] = useState<any>(null);
  const [isLoadingRevenue, setIsLoadingRevenue] = useState(false);
  const [revenuePeriod, setRevenuePeriod] = useState<'all' | 'month' | 'week' | 'year' | 'custom'>('all');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedWeek, setSelectedWeek] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string>('all');

  const fetchStudioDetails = useCallback(async () => {
    const fetchId = ++studioDetailsFetchIdRef.current;
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiService.getStudioById(studioId!);
      if (fetchId !== studioDetailsFetchIdRef.current) return;
      setStudio(response.studio);
      // Set permissions state
      const studioPerms = response.studio.studioPermissions || {};
      const nextPermissions = {
        isAdmin: studioPerms.isAdmin || false,
        allowClassCreation: studioPerms.allowClassCreation || false,
        allowEventCreation: studioPerms.allowEventCreation || false,
        canCreateManualBookings: studioPerms.canCreateManualBookings || false,
        enableTrialClasses: response.studio.enableTrialClasses ?? false,
        packOnlyBooking: response.studio.packOnlyBooking ?? false,
        isActive: response.studio.isActive ?? false,
        isSubscribed: response.studio.isSubscribed ?? false,
        enableSessionTypes: studioPerms.enableSessionTypes || false,
        studioQrEnabled: studioPerms.studioQrEnabled || false,
        oneDayOneSession: studioPerms.oneDayOneSession || false,
        oneSessionPerTimeSlot: studioPerms.oneSessionPerTimeSlot || false,
        enableWellness: studioPerms.enableWellness || false,
        enableMerchandise: studioPerms.enableMerchandise || false,
        enableSessionWaitlist: studioPerms.enableSessionWaitlist || false,
        enableSeatSelection: studioPerms.enableSeatSelection ?? false,
        enableBulkPackBooking: studioPerms.enableBulkPackBooking ?? false,
        enableGuestBooking: studioPerms.enableGuestBooking ?? false,
        enableMessaging: studioPerms.enableMessaging ?? false,
        enableReferrals: studioPerms.enableReferrals ?? false,
        enableMilestones: studioPerms.enableMilestones ?? false,
        listOnSweatSocial: studioPerms.listOnSweatSocial ?? true,
        enableNoShowPackPenalty: studioPerms.enableNoShowPackPenalty ?? false,
        bookingCutoffHours: studioPerms.bookingCutoffHours ?? 2,
        bookingCutoffSchedule: Array.isArray(studioPerms.bookingCutoffSchedule) ? studioPerms.bookingCutoffSchedule : [],
        sessionReminderEnabled: studioPerms.sessionReminderEnabled || false,
        sessionReminderHoursBefore: studioPerms.sessionReminderHoursBefore ?? 2.5,
      };
      setPermissions(nextPermissions);
      originalPermissionsRef.current = nextPermissions;
      // Derive the booking-cutoff toggle from whether ranges exist.
      setUseBookingSchedule(
        Array.isArray(studioPerms.bookingCutoffSchedule) && studioPerms.bookingCutoffSchedule.length > 0
      );
      const nextBookingWebsite = response.studio.whitelabelConfig?.enableBookingWebsite === true;
      setBookingWebsite(nextBookingWebsite);
      originalBookingWebsiteRef.current = nextBookingWebsite;
      setBookingWebsiteChanged(false);
      const nextSlug = response.studio.whitelabelConfig?.bookingSlug || '';
      const nextDomain = response.studio.whitelabelConfig?.bookingDomain || '';
      originalBookingAddressRef.current = { slug: nextSlug, domain: nextDomain };
      setBookingSlug(nextSlug);
      setBookingDomain(nextDomain);
      setShowBookingDomain(!!nextDomain);
      // The stored value is an instant; the input wants an IST calendar day.
      const rawFloor = response.studio.whitelabelConfig?.packStartFloorDate;
      setPackStartFloor(rawFloor ? istDayKeyFromIso(rawFloor) : '');
      setPackStartFloorChanged(false);

      const fp = response.studio.freezePolicy;
      const nextFreezePolicy = {
        enabled: fp?.enabled ?? false,
        maxDaysPerMonth: fp?.maxDaysPerMonth ?? 7,
        rolloverFreezedays: fp?.rolloverFreezedays ?? false,
        usePackDurationFreeze: fp?.usePackDurationFreeze ?? false,
      };
      setFreezePolicy(nextFreezePolicy);
      originalFreezePolicyRef.current = nextFreezePolicy;
      const mp = response.studio.merchandisePolicy;
      const nextMerchPolicy = {
        allowUserCancellation: mp?.allowUserCancellation ?? false,
        cancellableUntilStatus: mp?.cancellableUntilStatus ?? 'PACKED',
        refundMode: mp?.refundMode ?? 'manual',
      };
      setMerchPolicy(nextMerchPolicy);
      originalMerchPolicyRef.current = nextMerchPolicy;
      // Set cancellation policy state
      const cp = response.studio.cancellationPolicy;
      const cpSessionSchedule = Array.isArray(cp?.session?.schedule) ? cp.session.schedule : [];
      const nextCancellationPolicy = {
        enabled: cp?.enabled ?? true,
        session: {
          type: cp?.session?.type ?? 'standard',
          customHours: cp?.session?.customHours ?? 24,
          schedule: cpSessionSchedule,
        },
        event: { type: cp?.event?.type ?? 'standard', customHours: cp?.event?.customHours ?? 24 },
      };
      setCancellationPolicy(nextCancellationPolicy);
      // Derive the cancellation-cutoff toggle from whether session ranges exist.
      const nextUseCancellationSchedule = cpSessionSchedule.length > 0;
      setUseCancellationSchedule(nextUseCancellationSchedule);

      // Show the EFFECTIVE refund mode, not a raw (usually absent) field.
      // Mirrors resolveRefundMode on the backend: unset means 'manual', and a
      // studio that blocks every self-service cancellation reads as 'none' —
      // so the panel never tells an admin that Purple Pilates refunds.
      const rp = response.studio.refundPolicy;
      const notifPrefs = response.studio.whitelabelConfig?.ownerNotifPrefs;
      const blocksAllCancellation =
        !!notifPrefs?.preventDropInCancellation && !!notifPrefs?.preventEventCancellation;
      const nextRefundPolicy = {
        mode:
          rp?.mode === 'auto' || rp?.mode === 'manual' || rp?.mode === 'none'
            ? rp.mode
            : blocksAllCancellation
              ? 'none'
              : 'manual' as 'auto' | 'manual' | 'none',
        autoOnStudioCancel: rp?.autoOnStudioCancel === true,
      };
      setRefundPolicy(nextRefundPolicy);
      originalCancellationRef.current = {
        cancellationPolicy: nextCancellationPolicy,
        refundPolicy: nextRefundPolicy,
        useCancellationSchedule: nextUseCancellationSchedule,
      };
      // Set GST settings state
      const nextGstSettings = {
        isGstRegistered: response.studio.isGstRegistered ?? true,
        gstNumber: response.studio.gstNumber ?? '',
        gstType: response.studio.gstType ?? 'exclusive' as 'exclusive' | 'inclusive',
      };
      setGstSettings(nextGstSettings);
      originalGstSettingsRef.current = nextGstSettings;
      setGstSettingsChanged(false);

      // Set location state if available
      if (response.studio.location?.coordinates) {
        setLatitude(response.studio.location.coordinates[1].toString());
        setLongitude(response.studio.location.coordinates[0].toString());
      } else {
        setLatitude('');
        setLongitude('');
      }
    } catch (err: any) {
      if (fetchId !== studioDetailsFetchIdRef.current) return;
      setError(err.message || 'Failed to fetch studio details');
    } finally {
      if (fetchId !== studioDetailsFetchIdRef.current) return;
      setIsLoading(false);
    }
  }, [studioId]);

  const fetchEvents = useCallback(async (options?: { silent?: boolean }) => {
    const fetchId = ++eventsFetchIdRef.current;
    if (!options?.silent) {
      setIsLoadingEvents(true);
      setEventsError(null);
    }
    try {
      const response = await apiService.getStudioEvents(studioId!, undefined, pagePin);
      if (fetchId !== eventsFetchIdRef.current) return;
      setEvents(response.events || []);
    } catch (err: any) {
      if (fetchId !== eventsFetchIdRef.current) return;
      console.error('Error fetching events:', err);
      setEventsError('Failed to load events due to a server timeout or network issue.');
    } finally {
      if (fetchId !== eventsFetchIdRef.current) return;
      if (!options?.silent) setIsLoadingEvents(false);
    }
  }, [studioId, pagePin]);

  const fetchSessions = useCallback(async (options?: { silent?: boolean }) => {
    const fetchId = ++sessionsFetchIdRef.current;
    if (!options?.silent) {
      setIsLoadingSessions(true);
      setSessionsError(null);
    }
    try {
      const response = await apiService.getStudioSessions(
        studioId!,
        sessionCategory === 'wellness' ? { category: 'wellness' } : undefined,
        pagePin,
      );
      if (fetchId !== sessionsFetchIdRef.current) return;
      setSessions(response.sessions || []);
    } catch (err: any) {
      if (fetchId !== sessionsFetchIdRef.current) return;
      console.error('Error fetching sessions:', err);
      setSessionsError('Failed to load sessions due to a server timeout or network issue.');
    } finally {
      if (fetchId !== sessionsFetchIdRef.current) return;
      if (!options?.silent) setIsLoadingSessions(false);
    }
  }, [studioId, pagePin, sessionCategory]);

  const fetchSessionsForMonth = useCallback(async (monthStr: string) => {
    if (!studioId) return;

    loadedMonthsRef.current.add(monthStr);

    const fetchId = (monthSessionsFetchIdRef.current[monthStr] ?? 0) + 1;
    monthSessionsFetchIdRef.current[monthStr] = fetchId;

    const todayStr = getISTDateString();
    const [year, month] = monthStr.split('-');
    const monthStart = `${year}-${month}-01`;
    const lastDay = new Date(parseInt(year, 10), parseInt(month, 10), 0).getDate();
    const monthEnd = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;
    // Never bulk-fetch days that are already in the past — clamp the range start to
    // today; if the whole month is in the past, skip the network call entirely. Past
    // days are instead lazily fetched one at a time when actually clicked (see
    // fetchSessionsForPastDate).
    const startDate = monthStart < todayStr ? (todayStr <= monthEnd ? todayStr : null) : monthStart;

    if (startDate === null) {
      setMonthSessions((prev) => ({ ...prev, [monthStr]: [] }));
      return;
    }

    try {
      const response = await apiService.getStudioSessions(studioId, {
        startDate,
        endDate: monthEnd,
        ...(sessionCategory === 'wellness' ? { category: 'wellness' } : {}),
      }, pagePin);
      if (fetchId !== monthSessionsFetchIdRef.current[monthStr]) return;
      setMonthSessions((prev) => ({ ...prev, [monthStr]: response.sessions || [] }));
    } catch (err: any) {
      if (fetchId !== monthSessionsFetchIdRef.current[monthStr]) return;
      console.error('Error fetching sessions for month:', monthStr, err);
      loadedMonthsRef.current.delete(monthStr);
    }
  }, [studioId, pagePin, sessionCategory]);

  const fetchSessionsForPastDate = useCallback(async (dateStr: string) => {
    if (!studioId) return;
    loadedDaysRef.current.add(dateStr);
    const fetchId = (pastDayFetchIdRef.current[dateStr] ?? 0) + 1;
    pastDayFetchIdRef.current[dateStr] = fetchId;
    try {
      const response = await apiService.getStudioSessions(studioId, {
        date: dateStr,
        ...(sessionCategory === 'wellness' ? { category: 'wellness' } : {}),
      }, pagePin);
      if (fetchId !== pastDayFetchIdRef.current[dateStr]) return;
      setPastDaySessions((prev) => ({ ...prev, [dateStr]: response.sessions || [] }));
    } catch (err: any) {
      if (fetchId !== pastDayFetchIdRef.current[dateStr]) return;
      console.error('Error fetching sessions for past date:', dateStr, err);
      loadedDaysRef.current.delete(dateStr);
    }
  }, [studioId, pagePin, sessionCategory]);

  const refreshMonthSessionDots = useCallback((dateStr: string) => {
    const todayStr = getISTDateString();
    if (dateStr < todayStr) {
      loadedDaysRef.current.delete(dateStr);
      setPastDaySessions((prev) => {
        if (!(dateStr in prev)) return prev;
        const next = { ...prev };
        delete next[dateStr];
        return next;
      });
      void fetchSessionsForPastDate(dateStr);
      return;
    }
    const monthStr = dateStr.slice(0, 7);
    loadedMonthsRef.current.delete(monthStr);
    setMonthSessions((prev) => {
      if (!(monthStr in prev)) return prev;
      const next = { ...prev };
      delete next[monthStr];
      return next;
    });
    void fetchSessionsForMonth(monthStr);
  }, [fetchSessionsForMonth, fetchSessionsForPastDate]);

  const fetchSessionsForDate = useCallback(async (dateStr: string) => {
    if (!studioId) return;
    const fetchId = ++daySessionsFetchIdRef.current;
    setIsLoadingDaySessions(true);
    setDaySessionsError(null);
    try {
      const response = await apiService.getStudioSessions(studioId, {
        date: dateStr,
        ...(sessionCategory === 'wellness' ? { category: 'wellness' } : {}),
      }, pagePin);
      if (fetchId !== daySessionsFetchIdRef.current) return;
      setDaySessions(response.sessions || []);

      const todayStr = getISTDateString();
      if (dateStr < todayStr) {
        loadedDaysRef.current.add(dateStr);
        setPastDaySessions((prev) => ({ ...prev, [dateStr]: response.sessions || [] }));
      }

      const monthStr = dateStr.slice(0, 7);
      if (!loadedMonthsRef.current.has(monthStr)) {
        void fetchSessionsForMonth(monthStr);
      }
    } catch (err: any) {
      if (fetchId !== daySessionsFetchIdRef.current) return;
      console.error('Error fetching sessions for date:', err);
      setDaySessionsError('Failed to load sessions due to a server timeout or network issue.');
    } finally {
      if (fetchId !== daySessionsFetchIdRef.current) return;
      setIsLoadingDaySessions(false);
    }
  }, [studioId, pagePin, fetchSessionsForMonth, sessionCategory]);

  // Fetch a specific past date's sessions the first time it's viewed (clicked).
  useEffect(() => {
    const todayStr = getISTDateString();
    if (
      studioId &&
      activeTab === 2 &&
      selectedCategoryDate < todayStr &&
      !loadedDaysRef.current.has(selectedCategoryDate)
    ) {
      fetchSessionsForPastDate(selectedCategoryDate);
    }
  }, [studioId, activeTab, selectedCategoryDate, fetchSessionsForPastDate]);

  const fetchClasses = useCallback(async (options?: { silent?: boolean }) => {
    const fetchId = ++classesFetchIdRef.current;
    if (!options?.silent) {
      setIsLoadingClasses(true);
      setClassesError(null);
    }
    try {
      const response = await apiService.getStudioClasses(studioId!, {});
      if (fetchId !== classesFetchIdRef.current) return;
      console.log('Classes response:', response);
      setClasses(response.classes || []);
    } catch (err: any) {
      if (fetchId !== classesFetchIdRef.current) return;
      console.error('Error fetching classes:', err);
      setClasses([]);
      setClassesError('Failed to load classes due to a server timeout or network issue.');
    } finally {
      if (fetchId !== classesFetchIdRef.current) return;
      if (!options?.silent) setIsLoadingClasses(false);
    }
  }, [studioId]);

  const closeRemoveInstructor = () => {
    setRemoveInstructorOpen(false);
    setInstructorToRemove(null);
    setRemoveWork(null);
    setRemoveReplacementId('');
    setRemoveNotifyMembers(false);
    setRemoveError(null);
  };

  /**
   * Remove an instructor from this studio.
   *
   * Removal is a soft-delete — too many sessions reference an instructor for a
   * hard delete to be safe — so this calls setInstructorDisabled, the same
   * guarded path the Instructors page uses. The backend refuses while the
   * instructor still has upcoming work and returns the counts with the refusal;
   * that refusal is the cue for the picker, not an error to display.
   */
  const confirmRemoveInstructor = async () => {
    if (!instructorToRemove?._id) return;
    try {
      setIsRemovingInstructor(true);
      setRemoveError(null);
      await apiService.setInstructorDisabled(
        instructorToRemove._id,
        true,
        removeReplacementId || undefined,
        removeNotifyMembers,
        studioId,
      );
      closeRemoveInstructor();
      await fetchInstructors();
    } catch (err: any) {
      // The refusal carries the counts of what still points at this instructor,
      // and the picker is built from them. Read `err.data`: the response
      // interceptor flattens the axios error, so `err.response` is undefined
      // and the old `err?.response?.data` made this branch unreachable — the
      // operator saw "Choose who takes them over" and no control to do it with.
      const data = err?.data ?? err?.response?.data;
      if (data?.code === 'has_future_assignments') {
        setRemoveWork({
          sessionCount: data.sessionCount ?? 0,
          classCount: data.classCount ?? 0,
          eventCount: data.eventCount ?? 0,
          wellnessPoolCount: data.wellnessPoolCount ?? 0,
          serviceCount: data.serviceCount ?? 0,
          bookingCount: data.bookingCount ?? 0,
          memberCount: data.memberCount ?? 0,
          nextSessionAt: data.nextSessionAt ?? null,
        });
      } else {
        setRemoveError(data?.message || err?.message || 'Failed to remove instructor');
      }
    } finally {
      setIsRemovingInstructor(false);
    }
  };

  /**
   * Re-enable a previously removed instructor. No guard/reassignment needed
   * here — the backend only checks future-work on disable, re-enabling is
   * always safe (see setInstructorDisabled).
   */
  const handleEnableInstructor = async (instructor: Instructor) => {
    try {
      setIsEnablingInstructor(true);
      await apiService.setInstructorDisabled(instructor._id, false, undefined, undefined, studioId);
      await fetchInstructors();
    } catch (err: any) {
      setError(err?.data?.message || err?.message || 'Failed to enable instructor');
    } finally {
      setIsEnablingInstructor(false);
    }
  };

  const fetchInstructors = useCallback(async (options?: { silent?: boolean }) => {
    const fetchId = ++instructorsFetchIdRef.current;
    if (!options?.silent) {
      setIsLoadingInstructors(true);
      setInstructorsError(null);
    }
    try {
      // Active first, then the ones whose access was revoked here (dimmed, with
      // Reactivate). Removal takes the studio off the instructor, so they are
      // no longer in the plain list — `status` asks for each group.
      const [active, removed]: any[] = await Promise.all([
        apiService.getInstructorsByStudio(studioId!, { status: 'active' }),
        apiService.getInstructorsByStudio(studioId!, { status: 'removed' }).catch(() => null),
      ]);
      if (fetchId !== instructorsFetchIdRef.current) return;
      const split = splitInstructorLists(active?.instructors || [], removed?.instructors || []);
      setInstructors([
        ...split.active,
        ...split.deactivated.map((i: any) => ({ ...i, isDisabled: true })),
      ]);
    } catch (err: any) {
      if (fetchId !== instructorsFetchIdRef.current) return;
      console.error('Error fetching instructors:', err);
      setInstructorsError('Failed to load instructors due to a server timeout or network issue.');
    } finally {
      if (fetchId !== instructorsFetchIdRef.current) return;
      if (!options?.silent) setIsLoadingInstructors(false);
    }
  }, [studioId]);

  useEffect(() => {
    if (studioId) {
      fetchStudioDetails();
    }
  }, [studioId, fetchStudioDetails]);

  const getCurrentYear = () => {
    return new Date().getFullYear().toString();
  };

  const getCurrentMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  const getCurrentWeek = () => {
    const now = new Date();
    const year = now.getFullYear();
    // Get the first day of the year
    const startOfYear = new Date(year, 0, 1);
    // Calculate days since start of year
    const daysSinceStart = Math.floor((now.getTime() - startOfYear.getTime()) / (1000 * 60 * 60 * 24));
    // Calculate week number (ISO week starts on Monday)
    // Adjust for the day of week of Jan 1st
    const jan1Day = startOfYear.getDay();
    const weekNumber = Math.ceil((daysSinceStart + jan1Day) / 7);
    // Ensure week number is between 01 and 53
    const week = Math.min(Math.max(weekNumber, 1), 53);
    return `${year}-W${String(week).padStart(2, '0')}`;
  };

  const getRevenueDateRange = useCallback(() => {
    let startDate = '';
    let endDate = '';

    if (revenuePeriod === 'month' && selectedMonth) {
      const [year, month] = selectedMonth.split('-');
      startDate = `${year}-${month}-01`;
      const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
      endDate = `${year}-${month}-${lastDay}`;
    } else if (revenuePeriod === 'week' && selectedWeek) {
      const [year, week] = selectedWeek.split('-W');
      const firstDay = new Date(parseInt(year), 0, 1);
      const days = (parseInt(week) - 1) * 7;
      const start = new Date(firstDay);
      start.setDate(firstDay.getDate() + days - firstDay.getDay());
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      
      startDate = start.toISOString().split('T')[0];
      endDate = end.toISOString().split('T')[0];
    } else if (revenuePeriod === 'year' && selectedYear) {
      startDate = `${selectedYear}-01-01`;
      endDate = `${selectedYear}-12-31`;
    } else if (revenuePeriod === 'custom') {
      startDate = customStartDate;
      endDate = customEndDate;
    }

    return { startDate, endDate };
  }, [revenuePeriod, selectedMonth, selectedWeek, selectedYear, customStartDate, customEndDate]);

  const handleRevenuePeriodChange = (newPeriod: 'all' | 'month' | 'week' | 'year' | 'custom') => {
    setRevenuePeriod(newPeriod);
    setRevenueDetails(null); // Clear previous data when changing period

    // Set default values based on period type
    if (newPeriod === 'month') {
      setSelectedMonth(getCurrentMonth());
    } else if (newPeriod === 'week') {
      setSelectedWeek(getCurrentWeek());
    } else if (newPeriod === 'year') {
      setSelectedYear(getCurrentYear());
    } else if (newPeriod === 'all') {
      setSelectedMonth('');
      setSelectedWeek('');
      setSelectedYear('');
      setCustomStartDate('');
      setCustomEndDate('');
    }
  };

  const fetchRevenueDetails = useCallback(async () => {
    if (!studioId) return;
    
    // If period requires selection but not selected, don't fetch
    if ((revenuePeriod === 'month' && !selectedMonth) ||
        (revenuePeriod === 'week' && !selectedWeek) ||
        (revenuePeriod === 'year' && !selectedYear) ||
        (revenuePeriod === 'custom' && (!customStartDate || !customEndDate))) {
      return;
    }

    const fetchId = ++revenueFetchIdRef.current;
    try {
      setIsLoadingRevenue(true);
      const { startDate, endDate } = getRevenueDateRange();
      const params: { startDate?: string; endDate?: string } = {};

      if (startDate) {
        params.startDate = new Date(startDate).toISOString();
      }
      if (endDate) {
        params.endDate = new Date(endDate + 'T23:59:59').toISOString();
      }

      const response = await apiService.getStudioRevenueDetails(studioId, params);
      if (fetchId !== revenueFetchIdRef.current) return;
      setRevenueDetails(response);
    } catch (err: any) {
      if (fetchId !== revenueFetchIdRef.current) return;
      console.error('Error fetching revenue details:', err);
      setError(err.message || 'Failed to fetch revenue details');
    } finally {
      if (fetchId !== revenueFetchIdRef.current) return;
      setIsLoadingRevenue(false);
    }
  }, [studioId, revenuePeriod, selectedMonth, selectedWeek, selectedYear, customStartDate, customEndDate, getRevenueDateRange]);

  useEffect(() => {
    if (activeTab === 5 && studioId) {
      fetchRevenueDetails();
    }
  }, [activeTab, studioId, fetchRevenueDetails]);

  const handleGenerateInvoice = async () => {
    if (!studioId || !revenueDetails) return;

    try {
      setIsLoadingRevenue(true);
      setError(null);
      const { startDate, endDate } = getRevenueDateRange();
      const params: { startDate?: string; endDate?: string } = {};

      if (startDate) {
        params.startDate = new Date(startDate).toISOString();
      }
      if (endDate) {
        params.endDate = new Date(endDate + 'T23:59:59').toISOString();
      }

      // Download invoice PDF using apiService
      const blob = await apiService.generateStudioInvoice(studioId, params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${studio?.studioName.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      console.error('Error generating invoice:', err);
      setError(err.message || 'Failed to generate invoice');
    } finally {
      setIsLoadingRevenue(false);
    }
  };

  useEffect(() => {
    if (studioId && activeTab === 1 && events.length === 0) {
      fetchEvents();
    }
  }, [studioId, activeTab, events.length, fetchEvents]);

  useEffect(() => {
    setSessions([]);
    setDaySessions([]);
    setMonthSessions({});
    setPastDaySessions({});
    loadedMonthsRef.current.clear();
    loadedDaysRef.current.clear();
  }, [studioId]);

  useEffect(() => {
    if (studioId && activeTab === 0 && sessions.length === 0) {
      fetchSessions();
    }
  }, [studioId, activeTab, sessions.length, fetchSessions]);

  useEffect(() => {
    if (studioId && activeTab === 2 && !loadedMonthsRef.current.has(selectedCategoryDate.slice(0, 7))) {
      fetchSessionsForDate(selectedCategoryDate);
    }
  }, [studioId, activeTab, selectedCategoryDate, fetchSessionsForDate]);

  useEffect(() => {
    if (studioId && activeTab === 3 && classes.length === 0) {
      fetchClasses();
    }
  }, [studioId, activeTab, classes.length, fetchClasses]);

  useEffect(() => {
    if (studioId && activeTab === 4 && instructors.length === 0) {
      fetchInstructors();
    }
  }, [studioId, activeTab, instructors.length, fetchInstructors]);

  const fetchAdvertisements = useCallback(async () => {
    if (!studioId) return;
    const fetchId = ++advertisementsFetchIdRef.current;
    try {
      setAdvertisementsLoading(true);
      const response = await apiService.getAdvertisements({ studioId });
      if (fetchId !== advertisementsFetchIdRef.current) return;
      setAdvertisements(response.advertisements || []);
    } catch (err: any) {
      if (fetchId !== advertisementsFetchIdRef.current) return;
      console.error('Failed to fetch advertisements:', err);
    } finally {
      if (fetchId !== advertisementsFetchIdRef.current) return;
      setAdvertisementsLoading(false);
    }
  }, [studioId]);

  useEffect(() => {
    if (studioId && activeTab === 8) {
      fetchAdvertisements();
    }
  }, [studioId, activeTab, fetchAdvertisements]);

  useEffect(() => {
    if (studio?.bankDetailsPopupFrequency) {
      setBankDetailsPopupFrequency(studio.bankDetailsPopupFrequency);
    }
  }, [studio]);

  const handleDeleteEvent = async () => {
    if (selectedEvent) {
      try {
        // Pinned to this page's studio — under "All locations" the
        // switcher sends __all__, which the server refuses for writes.
        await apiService.deleteEvent(
          selectedEvent._id,
          studioIdOf((selectedEvent as any).studio) || studioId,
        );
        // Refresh events from API to get fresh data
        await fetchEvents({ silent: true });
        setDeleteEventDialogOpen(false);
        setSelectedEvent(null);
      } catch (err: any) {
        setError(err.message || 'Failed to delete event');
      }
    }
  };

  const handleDeleteSession = async () => {
    if (selectedSession) {
      try {
        // Pinned to this page's studio — under "All locations" the switcher
        // sends __all__, which the server refuses for writes.
        await apiService.deleteSession(
          selectedSession._id,
          studioIdOf((selectedSession as any).studio) || studioId,
        );
        // Refresh sessions from API to get fresh data
        await fetchSessionsForDate(selectedCategoryDate);
        refreshMonthSessionDots(selectedCategoryDate);
        void fetchSessions({ silent: true }); // Overview tab's aggregate list/count — separate cache
        setDeleteSessionDialogOpen(false);
        setSelectedSession(null);
      } catch (err: any) {
        setError(err.message || 'Failed to delete session');
      }
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!studio) return;
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingPhotos(true);
    setPhotoUploadError(null);
    try {
      const res: any = await apiService.uploadFiles(files, 'studios');
      const newUrls: string[] = res?.fileLinks || [];
      const updatedImages = [...(studio.images || []), ...newUrls];
      await apiService.updateStudio(studio._id, { images: updatedImages });
      await fetchStudioDetails();
    } catch (err: any) {
      setPhotoUploadError(err.message || 'Failed to upload photos');
    } finally {
      setUploadingPhotos(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  const handleRemovePhoto = async (index: number) => {
    if (!studio) return;
    const updatedImages = (studio.images || []).filter((_: string, i: number) => i !== index);
    try {
      await apiService.updateStudio(studio._id, { images: updatedImages });
      await fetchStudioDetails();
    } catch (err: any) {
      setPhotoUploadError(err.message || 'Failed to remove photo');
    }
  };

  const handleStudioMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setStudioMenuAnchorEl(event.currentTarget);
  };

  const handleStudioMenuClose = () => {
    setStudioMenuAnchorEl(null);
  };

  const handleDeleteStudio = () => {
    setDeleteStudioDialogOpen(true);
    handleStudioMenuClose();
  };

  const confirmDeleteStudio = async () => {
    if (studio) {
      try {
        setIsDeletingStudio(true);
        setError(null);
        await apiService.deleteStudio(studio._id);
        // Navigate back to studios list after successful deletion
        navigate('/studios');
      } catch (err: any) {
        setError(err.message || 'Failed to delete studio');
      } finally {
        setIsDeletingStudio(false);
      }
    }
  };

  // Kept out of `permissions`: this flag lives on whitelabelConfig, not
  // studioPermissions, and is sent as its own top-level field — see the save
  // handler for why a partial whitelabelConfig must never be sent.
  const [bookingWebsite, setBookingWebsite] = useState(false);
  const [bookingWebsiteChanged, setBookingWebsiteChanged] = useState(false);
  // The website's address: a slug (<slug>.book.sweatsocial.club) and/or the
  // studio's own domain. Sent as their own top-level fields, like the switch.
  const [bookingSlug, setBookingSlug] = useState('');
  const [bookingDomain, setBookingDomain] = useState('');
  const [showBookingDomain, setShowBookingDomain] = useState(false);
  const [bookingLinkCopied, setBookingLinkCopied] = useState(false);
  const originalBookingAddressRef = useRef({ slug: '', domain: '' });
  const bookingSlugChanged = bookingSlug.trim().toLowerCase() !== originalBookingAddressRef.current.slug;
  const bookingDomainChanged = bookingDomain.trim().toLowerCase() !== originalBookingAddressRef.current.domain;
  const bookingAddressChanged = bookingSlugChanged || bookingDomainChanged;
  // Opening day. '' means "no floor" — which is also how it is cleared.
  const [packStartFloor, setPackStartFloor] = useState('');
  const [packStartFloorChanged, setPackStartFloorChanged] = useState(false);

  // Snapshots of the last-saved values, used to detect a genuine edit vs. a
  // toggle flipped back to its saved value — so the "unsaved changes" bar and
  // inline Save buttons clear themselves the moment there's nothing to save,
  // instead of staying lit just because a change happened at some point.
  const originalPermissionsRef = useRef(permissions);
  const originalFreezePolicyRef = useRef(freezePolicy);
  const originalMerchPolicyRef = useRef(merchPolicy);
  const originalCancellationRef = useRef({ cancellationPolicy, refundPolicy, useCancellationSchedule });
  const originalGstSettingsRef = useRef(gstSettings);
  const originalBookingWebsiteRef = useRef(bookingWebsite);

  useEffect(() => {
    setPermissionsChanged(JSON.stringify(permissions) !== JSON.stringify(originalPermissionsRef.current));
  }, [permissions]);

  useEffect(() => {
    setFreezePolicyChanged(JSON.stringify(freezePolicy) !== JSON.stringify(originalFreezePolicyRef.current));
  }, [freezePolicy]);

  useEffect(() => {
    setMerchPolicyChanged(JSON.stringify(merchPolicy) !== JSON.stringify(originalMerchPolicyRef.current));
  }, [merchPolicy]);

  useEffect(() => {
    setCancellationPolicyChanged(
      JSON.stringify({ cancellationPolicy, refundPolicy, useCancellationSchedule }) !==
        JSON.stringify(originalCancellationRef.current)
    );
  }, [cancellationPolicy, refundPolicy, useCancellationSchedule]);

  useEffect(() => {
    setGstSettingsChanged(JSON.stringify(gstSettings) !== JSON.stringify(originalGstSettingsRef.current));
  }, [gstSettings]);

  useEffect(() => {
    setBookingWebsiteChanged(bookingWebsite !== originalBookingWebsiteRef.current);
  }, [bookingWebsite]);

  const handlePermissionsChange = (
    field: 'isAdmin' | 'allowClassCreation' | 'allowEventCreation' | 'canCreateManualBookings' | 'enableTrialClasses' | 'packOnlyBooking' | 'isActive' | 'isSubscribed' | 'enableSessionTypes' | 'studioQrEnabled' | 'oneDayOneSession' | 'oneSessionPerTimeSlot' | 'enableWellness' | 'enableMerchandise' | 'enableSeatSelection' | 'enableBulkPackBooking' | 'enableGuestBooking' | 'enableMessaging' | 'enableReferrals' | 'enableMilestones' | 'listOnSweatSocial' | 'enableSessionWaitlist' | 'sessionReminderEnabled' | 'enableNoShowPackPenalty',
    value: boolean
  ) => {
    setPermissions(prev => ({ ...prev, [field]: value }));
    setPermissionsChanged(true);
  };

  const handlePermissionsNumberChange = (
    field: 'bookingCutoffHours' | 'sessionReminderHoursBefore',
    value: number
  ) => {
    setPermissions(prev => ({ ...prev, [field]: value }));
    setPermissionsChanged(true);
  };

  // Range maths lives in src/utils/timeRanges.ts.

  const addCutoffRange = () => {
    setPermissions(prev => ({
      ...prev,
      bookingCutoffSchedule: appendRange(prev.bookingCutoffSchedule || [], 2),
    }));
    setPermissionsChanged(true);
  };

  const updateCutoffRange = (
    index: number,
    field: 'startTime' | 'endTime' | 'cutoffHours',
    value: string | number
  ) => {
    setPermissions(prev => ({
      ...prev,
      bookingCutoffSchedule: knitRanges(prev.bookingCutoffSchedule || [], index, field, value),
    }));
    setPermissionsChanged(true);
  };

  const removeCutoffRange = (index: number) => {
    setPermissions(prev => ({
      ...prev,
      bookingCutoffSchedule: (prev.bookingCutoffSchedule || []).filter((_, i) => i !== index),
    }));
    setPermissionsChanged(true);
  };

  // Toggle between the flat booking cutoff and time-of-day ranges. Turning the
  // toggle OFF clears the schedule so the backend falls back to the flat value.
  const toggleBookingSchedule = (on: boolean) => {
    setUseBookingSchedule(on);
    if (on) {
      setPermissions(prev =>
        (prev.bookingCutoffSchedule || []).length > 0
          ? prev
          : { ...prev, bookingCutoffSchedule: [{ startTime: '06:00', endTime: '12:00', cutoffHours: 2 }] }
      );
    } else {
      setPermissions(prev => ({ ...prev, bookingCutoffSchedule: [] }));
    }
    setPermissionsChanged(true);
  };

  // Session cancellation time-of-day range handlers (mirror the booking ones).
  const addCancellationRange = () => {
    setCancellationPolicy(prev => ({
      ...prev,
      session: {
        ...prev.session,
        schedule: appendRange(prev.session.schedule || [], 24),
      },
    }));
    setCancellationPolicyChanged(true);
  };

  const updateCancellationRange = (
    index: number,
    field: 'startTime' | 'endTime' | 'cutoffHours',
    value: string | number
  ) => {
    setCancellationPolicy(prev => ({
      ...prev,
      session: {
        ...prev.session,
        schedule: knitRanges(prev.session.schedule || [], index, field, value),
      },
    }));
    setCancellationPolicyChanged(true);
  };

  const removeCancellationRange = (index: number) => {
    setCancellationPolicy(prev => ({
      ...prev,
      session: {
        ...prev.session,
        schedule: (prev.session.schedule || []).filter((_, i) => i !== index),
      },
    }));
    setCancellationPolicyChanged(true);
  };

  // Toggle between the flat session cancellation window and time-of-day ranges.
  // Turning OFF clears the schedule so the flat custom/standard value applies.
  const toggleCancellationSchedule = (on: boolean) => {
    setUseCancellationSchedule(on);
    if (on) {
      setCancellationPolicy(prev =>
        (prev.session.schedule || []).length > 0
          ? prev
          : {
              ...prev,
              session: {
                ...prev.session,
                schedule: [{ startTime: '06:00', endTime: '12:00', cutoffHours: 24 }],
              },
            }
      );
    } else {
      setCancellationPolicy(prev => ({
        ...prev,
        session: { ...prev.session, schedule: [] },
      }));
    }
    setCancellationPolicyChanged(true);
  };

  const handleSavePermissions = async () => {
    if (!studio) return;
    try {
      const { isActive, isSubscribed, enableTrialClasses, packOnlyBooking, enableSessionTypes, studioQrEnabled, oneDayOneSession, oneSessionPerTimeSlot, enableWellness, enableMerchandise, enableSeatSelection, enableBulkPackBooking, enableGuestBooking, enableMessaging, enableReferrals, enableMilestones, listOnSweatSocial, enableSessionWaitlist, enableNoShowPackPenalty, bookingCutoffHours, bookingCutoffSchedule, sessionReminderEnabled, sessionReminderHoursBefore, ...studioPerms } = permissions;
      await apiService.updateStudio(studio._id, {
        studioPermissions: { ...studioPerms, enableSessionTypes, studioQrEnabled, oneDayOneSession, oneSessionPerTimeSlot, enableWellness, enableMerchandise, enableSeatSelection, enableBulkPackBooking, enableGuestBooking, enableMessaging, enableReferrals, enableMilestones, listOnSweatSocial, enableSessionWaitlist, enableNoShowPackPenalty, bookingCutoffHours, bookingCutoffSchedule, sessionReminderEnabled, sessionReminderHoursBefore },
        merchandisePolicy: merchPolicy,
        isActive: isActive,
        isSubscribed: isSubscribed,
        enableTrialClasses: enableTrialClasses,
        packOnlyBooking: packOnlyBooking,
        // Top-level, NOT inside a whitelabelConfig object. The backend
        // replaces that sub-document wholesale, so sending a partial one here
        // would wipe the studio's appSource and Razorpay credentials — and we
        // could not send a complete one either, because getStudioById redacts
        // the secrets. The server maps this to a single dot-notation key.
        ...(bookingWebsiteChanged ? { enableBookingWebsite: bookingWebsite } : {}),
        // '' clears it server-side; the server validates and checks clashes.
        ...(bookingSlugChanged ? { bookingSlug: bookingSlug.trim() } : {}),
        ...(bookingDomainChanged ? { bookingDomain: bookingDomain.trim() } : {}),
        // Same top-level shape, same reason. '' clears it server-side.
        ...(packStartFloorChanged ? { packStartFloorDate: packStartFloor || null } : {}),
      });
      setPermissionsChanged(false);
      setMerchPolicyChanged(false);
      setBookingWebsiteChanged(false);
      setPackStartFloorChanged(false);
      fetchStudioDetails();
    } catch (err: any) {
      setError(err.message || 'Failed to update permissions');
    }
  };

  // Focused save for the studio-owner Settings tab — only persists the booking
  // cutoff / session-reminder fields (a partial studioPermissions merge on the
  // backend), so owners can't touch super-admin-only permission flags.
  const handleSaveBookingCutoff = async () => {
    if (!studio) return;
    try {
      await apiService.updateStudio(studio._id, {
        studioPermissions: {
          bookingCutoffHours: permissions.bookingCutoffHours,
          bookingCutoffSchedule: permissions.bookingCutoffSchedule,
          sessionReminderEnabled: permissions.sessionReminderEnabled,
          sessionReminderHoursBefore: permissions.sessionReminderHoursBefore,
        },
      });
      setPermissionsChanged(false);
      fetchStudioDetails();
    } catch (err: any) {
      setError(err.message || 'Failed to update booking settings');
    }
  };

  const downloadQrCode = (studioId: string, studioName?: string, canvasId: string = 'studio-qr-canvas') => {
    const canvas = document.getElementById(canvasId) as HTMLCanvasElement;
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = url;
    link.download = `${studioName ?? studioId}-qr.png`;
    link.click();
  };

  const handleSaveFreezePolicy = async () => {
    if (!studio) return;
    try {
      await apiService.updateStudio(studio._id, { freezePolicy });
      setFreezePolicyChanged(false);
      fetchStudioDetails();
    } catch (err: any) {
      setError(err.message || 'Failed to update freeze policy');
    }
  };

  const handleSaveCancellationPolicy = async () => {
    if (!studio) return;
    try {
      // The backend replaces cancellationPolicy wholesale, so send every field.
      // session.schedule is the time-of-day ranges (or [] when the toggle is off).
      await apiService.updateStudio(studio._id, {
        cancellationPolicy: {
          enabled: cancellationPolicy.enabled,
          session: {
            type: cancellationPolicy.session.type,
            customHours: cancellationPolicy.session.customHours,
            schedule: useCancellationSchedule ? (cancellationPolicy.session.schedule || []) : [],
          },
          event: {
            type: cancellationPolicy.event.type,
            customHours: cancellationPolicy.event.customHours,
          },
        },
        refundPolicy: {
          mode: refundPolicy.mode,
          autoOnStudioCancel: refundPolicy.autoOnStudioCancel,
        },
      });
      setCancellationPolicyChanged(false);
      fetchStudioDetails();
    } catch (err: any) {
      setError(err.message || 'Failed to update cancellation policy');
    }
  };

  const handleSaveGstSettings = async () => {
    if (!studio) return;
    try {
      await apiService.updateStudio(studio._id, {
        isGstRegistered: gstSettings.isGstRegistered,
        gstNumber: gstSettings.isGstRegistered ? (gstSettings.gstNumber || null) : null,
        gstType: gstSettings.gstType,
      });
      setGstSettingsChanged(false);
      fetchStudioDetails();
    } catch (err: any) {
      setError(err.message || 'Failed to update GST settings');
    }
  };

  const handleUpdateBankDetailsPopupFrequency = async () => {
    if (!studio) return;
    try {
      setIsUpdatingBankDetails(true);
      setError(null);
      await apiService.updateStudio(studio._id, {
        bankDetailsPopupFrequency: bankDetailsPopupFrequency,
      });
      setEditBankDetailsDialogOpen(false);
      fetchStudioDetails();
    } catch (err: any) {
      setError(err.message || 'Failed to update bank details popup frequency');
    } finally {
      setIsUpdatingBankDetails(false);
    }
  };

  const handleUpdateLocation = async () => {
    if (!studio) return;
    
    // Validate inputs
    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);
    
    if (isNaN(lat) || isNaN(lng)) {
      setError('Please enter valid latitude and longitude values');
      return;
    }
    
    if (lat < -90 || lat > 90) {
      setError('Latitude must be between -90 and 90');
      return;
    }
    
    if (lng < -180 || lng > 180) {
      setError('Longitude must be between -180 and 180');
      return;
    }
    
    try {
      setIsUpdatingLocation(true);
      setError(null);
      await apiService.updateStudio(studio._id, {
        latitude: lat,
        longitude: lng,
      });
      setEditLocationDialogOpen(false);
      fetchStudioDetails();
    } catch (err: any) {
      setError(err.message || 'Failed to update location');
    } finally {
      setIsUpdatingLocation(false);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !studio) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mb: 2 }}>
          Back
        </Button>
        <Alert severity="error">{error || 'Studio not found'}</Alert>
      </Box>
    );
  }

  // A studio login waits for its location list, so a multi-location owner
  // never sees the single-location page flash up first.
  if (isStudioRole && !myLocationsLoaded) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Viewing "All locations": a profile belongs to one location, so choose it.
  if (brandProfile && !selectedLocationId) {
    return (
      <LocationProfileLanding
        locations={manageableLocations}
        roleByStudio={roleByStudio}
        brandName={studio.brand?.name}
      />
    );
  }
  const currentLocation = myLocations.find((l) => l._id === studioId);

  // Which save handlers apply to the currently open tab, filtered to only
  // the sections that actually have a pending edit. Powers the floating
  // "unsaved changes" bar below so a save action is always on-screen —
  // otherwise, on these long tabs, the inline Save button can be scrolled
  // far out of view from whichever field was just edited.
  const pendingSaveActions: { key: string; label: string; onSave: () => Promise<void> }[] = [];
  if (activeTab === 6) {
    if (permissionsChanged || merchPolicyChanged || bookingWebsiteChanged || bookingAddressChanged || packStartFloorChanged) {
      pendingSaveActions.push({ key: 'permissions', label: 'Permissions', onSave: handleSavePermissions });
    }
    if (freezePolicyChanged) {
      pendingSaveActions.push({ key: 'freeze', label: 'Freeze Policy', onSave: handleSaveFreezePolicy });
    }
    if (gstSettingsChanged) {
      pendingSaveActions.push({ key: 'gst', label: 'GST Settings', onSave: handleSaveGstSettings });
    }
  } else if (activeTab === 17) {
    if (permissionsChanged) {
      pendingSaveActions.push({ key: 'booking', label: 'Booking Settings', onSave: handleSaveBookingCutoff });
    }
    if (gstSettingsChanged) {
      pendingSaveActions.push({ key: 'gst', label: 'GST Settings', onSave: handleSaveGstSettings });
    }
    if (cancellationPolicyChanged) {
      pendingSaveActions.push({ key: 'cancellation', label: 'Cancellation Policy', onSave: handleSaveCancellationPolicy });
    }
  } else if (activeTab === 7) {
    if (cancellationPolicyChanged) {
      pendingSaveActions.push({ key: 'cancellation', label: 'Cancellation Policy', onSave: handleSaveCancellationPolicy });
    }
  }

  const handleSaveAllPending = async () => {
    setSavingAllChanges(true);
    try {
      for (const action of pendingSaveActions) {
        await action.onSave();
      }
    } finally {
      setSavingAllChanges(false);
    }
  };

  return (
    <Box>
      {/* Which location this profile is — brands with several locations. */}
      {brandProfile && (
        <LocationProfileBar
          current={currentLocation}
          currentName={currentLocation?.branchName || currentLocation?.studioName || studio.studioName}
          locations={manageableLocations}
        />
      )}

      {/* Header */}
      <Box mb={3}>
        {!brandProfile && (
          <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mb: 2 }}>
            Back
          </Button>
        )}
        
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar
              src={resolveStudioLogoUrl(studio)}
              sx={{
                width: 72,
                height: 72,
                backgroundColor: colors.primary,
                color: colors.white,
                fontSize: '2rem',
                fontWeight: 700,
              }}
            >
              {studio.studioName?.charAt(0)?.toUpperCase() || 'S'}
            </Avatar>
            <Box>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Typography variant="h4" fontWeight={700}>
                  {studio.studioName}
                </Typography>
                {studio.premiumStudio && (
                  <Chip label="Premium" size="small" sx={{ backgroundColor: colors.warning, color: colors.white }} />
                )}
                <Chip
                  label={studio.isActive ? 'Active' : 'Inactive'}
                  size="small"
                  color={studio.isActive ? 'success' : 'error'}
                />
                {/*
                  Was an HQ chip or a Branch chip, which told you this studio's
                  POSITION in a hierarchy — useful only if you already knew
                  which hierarchy. The brand's name answers the question people
                  were actually asking.
                */}
                {studio.brand && (
                  <Chip
                    label={studio.brand.name}
                    size="small"
                    icon={<AccountTree sx={{ fontSize: '14px !important' }} />}
                    // Clickable for super admins, so "which business is this,
                    // and where do I change it" is one hop from the studio —
                    // the direction an operator actually arrives from.
                    clickable={isSuperAdmin}
                    onClick={
                      isSuperAdmin
                        ? () => navigate(`/brands/${studio.brand!._id}`)
                        : undefined
                    }
                    sx={{ backgroundColor: '#764ba2', color: '#fff', fontWeight: 700, fontSize: '0.7rem' }}
                  />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {studio.city}, {studio.state}
              </Typography>
            </Box>
          </Box>
          {admin?.role !== 'manager' && (
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Edit />}
                onClick={() => setEditStudioDialogOpen(true)}
              >
                Edit
              </Button>
              {/* Its only item is Delete Studio — super admin only. */}
              {isSuperAdmin && (
                <IconButton
                  size="small"
                  onClick={handleStudioMenuOpen}
                >
                  <MoreVert />
                </IconButton>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Tabs */}
      <Card sx={{ borderRadius: 2, mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_e, newValue) => {
            setActiveTab(newValue);
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            borderBottom: `1px solid ${colors.divider}`,
            px: 1,
            minHeight: 56,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.9rem',
              minHeight: 48,
              minWidth: 'auto',
              borderRadius: 2,
              mx: 0.25,
              my: 1,
              color: 'text.secondary',
              transition: 'all 0.15s ease',
              '&:hover': { backgroundColor: `${colors.primary}0A`, color: colors.primary },
              '&.Mui-selected': { color: colors.primary, fontWeight: 700, backgroundColor: `${colors.primary}12` },
            },
            '& .MuiTabs-indicator': { height: 3, borderRadius: 3 },
          }}
        >
          <Tab label="Studio Info" icon={<Schedule />} iconPosition="start" value={0} />
          <Tab label="Events" icon={<Event />} iconPosition="start" value={1} sx={{ display: hiddenForBrand(1) ? 'none' : undefined }} />
          <Tab label="Sessions" icon={<Class />} iconPosition="start" value={2} sx={{ display: hiddenForBrand(2) ? 'none' : undefined }} />
          {/* Wellness is now managed as ordinary Classes + Sessions (category derived
              from the ClassType). Use the "Workout | Wellness" toggle on Sessions and
              the Classes tab instead. */}
          <Tab label="Classes" icon={<Class />} iconPosition="start" value={3} sx={{ display: hiddenForBrand(3) ? 'none' : undefined }} />
          <Tab
            label="Packs"
            icon={<Inventory />}
            iconPosition="start"
            value={18}
            sx={{ display: admin?.role === 'instructor' || hiddenForBrand(18) ? 'none' : undefined }}
          />
          <Tab label="Instructors" icon={<Person />} iconPosition="start" value={4} sx={{ display: hiddenForBrand(4) ? 'none' : undefined }} />
          <Tab
            label="Revenue"
            icon={<AttachMoney />}
            iconPosition="start"
            value={5}
            sx={{ display: admin?.role === 'manager' ? 'none' : undefined }}
          />
          <Tab
            label="Permissions"
            icon={<People />}
            iconPosition="start"
            value={6}
            sx={{ display: isStudioRole ? 'none' : undefined }}
          />
          <Tab
            label="Cancellation"
            icon={<EventBusy />}
            iconPosition="start"
            value={7}
            sx={{ display: isOwnerRole(admin?.role) ? 'none' : undefined }}
          />
          {/*
            Advertisements render inside a studio's OWN app. A studio with no
            whitelabel app has nowhere to show one — the SweatSocial app carries
            banners, which are SweatSocial's placement, not the studio's — so
            the tab would manage content that could never appear anywhere.
          */}
          <Tab
            label="Advertisements"
            icon={<Campaign />}
            iconPosition="start"
            value={8}
            sx={{
              display:
                isStudioRole || !studio?.whitelabelConfig?.appSource
                  ? 'none'
                  : undefined,
            }}
          />
          {/*
            Was hidden unless `hasBranches` was on AND the studio was not
            itself a branch — so a branch could never see its own siblings.
            A brand has no direction, so the tab shows wherever there is a
            brand to show.
          */}
          <Tab
            label="Locations"
            icon={<AccountTree />}
            iconPosition="start"
            value={9}
            disabled={!studio.brand}
            sx={{ display: !studio.brand || hiddenForBrand(9) ? 'none' : undefined }}
          />
          <Tab label="Staff" icon={<People />} iconPosition="start" value={10} sx={{ display: hiddenForBrand(10) ? 'none' : undefined }} />
          <Tab label="Announcements" icon={<AnnouncementIcon />} iconPosition="start" value={11} />
          <Tab label="Founders" icon={<Groups />} iconPosition="start" value={13} />
          <Tab label="FAQs" icon={<QuestionAnswer />} iconPosition="start" value={14} />
          <Tab label="Gallery" icon={<Collections />} iconPosition="start" value={15} />
          <Tab label="Holidays" icon={<BeachAccess />} iconPosition="start" value={16} />
          {/* Settings tab (panel value 17) — always the LAST tab. Every tab
              carries an explicit `value`, so its position does not change which
              panel it opens. Visible to studio owners; super-admins use the
              Permissions/Cancellation tabs instead. */}
          <Tab
            label="Settings"
            icon={<Settings />}
            iconPosition="start"
            value={17}
            sx={{ display: isOwnerRole(admin?.role) ? undefined : 'none' }}
          />
        </Tabs>

        {/* Tab Panels */}
        <Box sx={{ p: 3 }}>
          {activeTab === 0 && (
            /* Studio Info Tab */
            <Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
                {/* Left Column */}
                <Box>
                  {/* Contact Information */}
                  <Card sx={{ mb: 3, border: `1px solid ${colors.divider}` }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                        Contact Information
                      </Typography>
                      <Box display="flex" flexDirection="column" gap={2}>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 1.5,
                              backgroundColor: `${colors.primary}15`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: colors.primary,
                            }}
                          >
                            <Email fontSize="small" />
                          </Box>
                          <Box flex={1}>
                            <Typography variant="caption" color="text.secondary">
                              Email
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {studio.email || 'Not provided'}
                            </Typography>
                          </Box>
                        </Box>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 1.5,
                              backgroundColor: `${colors.secondary}15`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: colors.secondary,
                            }}
                          >
                            <Phone fontSize="small" />
                          </Box>
                          <Box flex={1}>
                            <Typography variant="caption" color="text.secondary">
                              Phone
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {studio.phoneNumber || 'Not provided'}
                            </Typography>
                          </Box>
                        </Box>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 1.5,
                              backgroundColor: `${colors.success}15`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: colors.success,
                            }}
                          >
                            <LocationOn fontSize="small" />
                          </Box>
                          <Box flex={1}>
                            <Typography variant="caption" color="text.secondary">
                              Address
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {studio.address || 'Not provided'}
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {studio.city}, {studio.state} - {studio.pincode}
                            </Typography>
                          </Box>
                        </Box>
                        {/* Location Coordinates */}
                        <Box display="flex" alignItems="center" gap={2} justifyContent="space-between">
                          <Box display="flex" alignItems="center" gap={2} flex={1}>
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 1.5,
                                backgroundColor: `${colors.primary}15`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: colors.primary,
                              }}
                            >
                              <LocationOn fontSize="small" />
                            </Box>
                            <Box flex={1}>
                              <Typography variant="caption" color="text.secondary">
                                Coordinates
                              </Typography>
                              <Typography variant="body2" fontWeight={500}>
                                {studio.location?.coordinates 
                                  ? `Lat: ${studio.location.coordinates[1]}, Lng: ${studio.location.coordinates[0]}`
                                  : 'Not set'}
                              </Typography>
                            </Box>
                          </Box>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Edit />}
                            onClick={() => {
                              if (studio.location?.coordinates) {
                                setLatitude(studio.location.coordinates[1].toString());
                                setLongitude(studio.location.coordinates[0].toString());
                              } else {
                                setLatitude('');
                                setLongitude('');
                              }
                              setEditLocationDialogOpen(true);
                            }}
                          >
                            Edit
                          </Button>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Working Hours */}
                  {studio.workingHoursFrom && studio.workingHoursTo && (
                    <Card sx={{ mb: 3, border: `1px solid ${colors.divider}` }}>
                      <CardContent>
                        <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                          Working Hours
                        </Typography>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Schedule sx={{ color: colors.textSecondary }} />
                          <Typography variant="body2" fontWeight={500}>
                            {(() => {
                              // Working hours stored as IST serialised as UTC — read UTC components directly
                              const f = new Date(studio.workingHoursFrom);
                              const fh = f.getUTCHours(), fm = f.getUTCMinutes();
                              const fPeriod = fh >= 12 ? 'PM' : 'AM';
                              const fDisplay = `${String(fh % 12 || 12).padStart(2,'0')}:${String(fm).padStart(2,'0')} ${fPeriod}`;
                              const t = new Date(studio.workingHoursTo);
                              const th = t.getUTCHours(), tm = t.getUTCMinutes();
                              const tPeriod = th >= 12 ? 'PM' : 'AM';
                              const tDisplay = `${String(th % 12 || 12).padStart(2,'0')}:${String(tm).padStart(2,'0')} ${tPeriod}`;
                              return `${fDisplay} – ${tDisplay} IST`;
                            })()}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  )}

                  {/* Description */}
                  {studio.description && (
                    <Card sx={{ mb: 3, border: `1px solid ${colors.divider}` }}>
                      <CardContent>
                        <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                          Description
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {studio.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  )}

                  {/* Studio Photos */}
                  <Card sx={{ mb: 3, border: `1px solid ${colors.divider}` }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Box>
                          <Typography variant="h6" fontWeight={700}>
                            Studio Photos
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {studio.images?.length || 0} photo{(studio.images?.length || 0) !== 1 ? 's' : ''} · first image used as cover
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={uploadingPhotos ? <CircularProgress size={14} /> : <AddPhotoAlternate />}
                          onClick={() => photoInputRef.current?.click()}
                          disabled={uploadingPhotos}
                        >
                          {uploadingPhotos ? 'Uploading…' : 'Add Photos'}
                        </Button>
                        <input
                          ref={photoInputRef}
                          type="file"
                          accept="image/*"
                          multiple
                          style={{ display: 'none' }}
                          onChange={handlePhotoUpload}
                        />
                      </Box>

                      {photoUploadError && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPhotoUploadError(null)}>
                          {photoUploadError}
                        </Alert>
                      )}

                      {studio.images && studio.images.length > 0 ? (
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                            gap: 1.5,
                          }}
                        >
                          {studio.images.map((url: string, i: number) => (
                            <Box
                              key={i}
                              sx={{
                                position: 'relative',
                                aspectRatio: '1',
                                borderRadius: 2,
                                overflow: 'hidden',
                                border: i === 0 ? `2px solid ${colors.primary}` : `1px solid ${colors.divider}`,
                                '&:hover .remove-photo-btn': { opacity: 1 },
                              }}
                            >
                              <img
                                src={url}
                                alt={`Studio ${i + 1}`}
                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                              />
                              {i === 0 && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    bottom: 4,
                                    left: 4,
                                    bgcolor: colors.primary,
                                    color: '#fff',
                                    fontSize: 10,
                                    fontWeight: 700,
                                    px: 0.75,
                                    py: 0.25,
                                    borderRadius: 0.75,
                                  }}
                                >
                                  Cover
                                </Box>
                              )}
                              <Tooltip title="Remove photo">
                                <IconButton
                                  className="remove-photo-btn"
                                  size="small"
                                  onClick={() => handleRemovePhoto(i)}
                                  disabled={uploadingPhotos}
                                  sx={{
                                    position: 'absolute',
                                    top: 4,
                                    right: 4,
                                    opacity: 0,
                                    transition: 'opacity 0.15s',
                                    bgcolor: 'rgba(0,0,0,0.55)',
                                    color: '#fff',
                                    p: 0.5,
                                    '&:hover': { bgcolor: 'rgba(180,0,0,0.8)' },
                                  }}
                                >
                                  <Delete sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Box
                          onClick={() => photoInputRef.current?.click()}
                          sx={{
                            border: `1px dashed ${colors.divider}`,
                            borderRadius: 2,
                            p: 4,
                            textAlign: 'center',
                            cursor: 'pointer',
                            color: colors.textSecondary,
                            '&:hover': { borderColor: colors.primary, color: colors.primary },
                            transition: 'all 0.15s',
                          }}
                        >
                          <AddPhotoAlternate sx={{ fontSize: 36, opacity: 0.5, mb: 1 }} />
                          <Typography variant="body2">
                            Click to upload studio photos
                          </Typography>
                          <Typography variant="caption">
                            JPG, PNG · multiple files supported
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>

                  {/* Bank Details */}
                  <Card sx={{ border: `1px solid ${colors.divider}` }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6" fontWeight={700}>
                          Bank Details
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditBankDetailsDialogOpen(true);
                          }}
                          sx={{ color: colors.primary }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Box>
                      <Box display="flex" flexDirection="column" gap={2}>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 1.5,
                              backgroundColor: `${colors.primary}15`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: colors.primary,
                            }}
                          >
                            <Person fontSize="small" />
                          </Box>
                          <Box flex={1}>
                            <Typography variant="caption" color="text.secondary">
                              Account Holder Name
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {studio.accountHolderName || 'Not provided'}
                            </Typography>
                          </Box>
                        </Box>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 1.5,
                              backgroundColor: `${colors.secondary}15`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: colors.secondary,
                            }}
                          >
                            <Payment fontSize="small" />
                          </Box>
                          <Box flex={1}>
                            <Typography variant="caption" color="text.secondary">
                              Account Number
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {studio.accountNumber
                                ? (admin?.role === 'super_admin' || admin?.role === 'admin')
                                  ? studio.accountNumber
                                  : `****${studio.accountNumber.slice(-4)}`
                                : 'Not provided'}
                            </Typography>
                          </Box>
                        </Box>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 1.5,
                              backgroundColor: `${colors.success}15`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: colors.success,
                            }}
                          >
                            <Business fontSize="small" />
                          </Box>
                          <Box flex={1}>
                            <Typography variant="caption" color="text.secondary">
                              IFSC Code
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {studio.ifscCode || 'Not provided'}
                            </Typography>
                          </Box>
                        </Box>
                        <Divider sx={{ my: 1 }} />
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 1.5,
                              backgroundColor: `${colors.warning}15`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: colors.warning,
                            }}
                          >
                            <Settings fontSize="small" />
                          </Box>
                          <Box flex={1}>
                            <Typography variant="caption" color="text.secondary">
                              Bank Details Popup Frequency
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                              <Typography variant="body2" fontWeight={500}>
                                {studio.bankDetailsPopupFrequency === 'one-time' ? 'One Time' :
                                 studio.bankDetailsPopupFrequency === 'weekly' ? 'Weekly' :
                                 studio.bankDetailsPopupFrequency === 'monthly' ? 'Monthly' :
                                 'Weekly (Default)'}
                              </Typography>
                              <Chip
                                label={studio.bankDetailsPopupFrequency || 'weekly'}
                                size="small"
                                color={
                                  studio.bankDetailsPopupFrequency === 'one-time' ? 'default' :
                                  studio.bankDetailsPopupFrequency === 'weekly' ? 'primary' :
                                  studio.bankDetailsPopupFrequency === 'monthly' ? 'secondary' :
                                  'default'
                                }
                                sx={{ ml: 1 }}
                              />
                            </Box>
                            {studio.lastBankDetailsPopupShown && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                Last shown: {new Date(studio.lastBankDetailsPopupShown).toLocaleDateString()}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>

                {/* Right Column */}
                <Box>
                  {/* Quick Stats */}
                  <Card sx={{ mb: 3, border: `1px solid ${colors.divider}` }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                        Quick Stats
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}>
                        {[
                          { icon: <Star fontSize="small" />, tint: colors.warning, label: 'Rating', value: studio.rating?.toFixed(1) || '0.0', sub: `${studio.totalReviews || 0} reviews` },
                          { icon: <Event fontSize="small" />, tint: colors.info, label: 'Events', value: events.length, sub: '' },
                          { icon: <Class fontSize="small" />, tint: colors.success, label: 'Sessions', value: sessions.length, sub: '' },
                        ].map((s, i) => (
                          <Box
                            key={i}
                            sx={{
                              textAlign: 'center',
                              p: 1.5,
                              borderRadius: 2,
                              border: `1px solid ${colors.divider}`,
                              backgroundColor: `${s.tint}0A`,
                            }}
                          >
                            <Box sx={{ color: s.tint, display: 'flex', justifyContent: 'center', mb: 0.5 }}>{s.icon}</Box>
                            <Typography variant="h6" fontWeight={700} lineHeight={1.1}>{s.value}</Typography>
                            <Typography variant="caption" color="text.secondary" display="block">{s.label}</Typography>
                            {s.sub && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem' }}>{s.sub}</Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Subscription Info */}
                  <Card sx={{ mb: 3, border: `1px solid ${colors.divider}` }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                        Subscription
                      </Typography>
                      <Box display="flex" flexDirection="column" gap={1.5}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">
                            Status
                          </Typography>
                          <Chip
                            label={studio.isSubscribed ? 'Active' : 'Inactive'}
                            size="small"
                            sx={{
                              backgroundColor: studio.isSubscribed ? colors.success : colors.textSecondary,
                              color: colors.white,
                            }}
                          />
                        </Box>
                        {studio.subscriptionEndDate && (
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">
                              Expires
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {new Date(studio.subscriptionEndDate).toLocaleDateString('en-IN', { 
                                day: '2-digit', 
                                month: 'short',
                                year: 'numeric'
                              })}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Permissions */}
                  <Card sx={{ border: `1px solid ${colors.divider}` }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                        Permissions
                      </Typography>
                      <Box display="flex" flexDirection="column" gap={1}>
                        {studio.studioPermissions?.allowClassCreation && (
                          <Chip
                            icon={<Class />}
                            label="Can Create Classes"
                            size="small"
                            sx={{ backgroundColor: `${colors.success}15`, color: colors.success }}
                          />
                        )}
                        {studio.studioPermissions?.allowEventCreation && (
                          <Chip
                            icon={<Event />}
                            label="Can Create Events"
                            size="small"
                            sx={{ backgroundColor: `${colors.info}15`, color: colors.info }}
                          />
                        )}
                        {studio.studioPermissions?.isAdmin && (
                          <Chip
                            icon={<People />}
                            label="Admin Access"
                            size="small"
                            sx={{ backgroundColor: `${colors.primary}15`, color: colors.primary }}
                          />
                        )}
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Sessions Summary */}
                  <Card sx={{ border: `1px solid ${colors.divider}` }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                        Sessions
                      </Typography>
                      {(() => {
                        const now = new Date();
                        const upcoming = sessions
                          .filter(s => new Date(s.date) >= now)
                          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
                        const passed = sessions
                          .filter(s => new Date(s.date) < now)
                          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

                        const SessionRow = ({ session }: { session: Session }) => {
                          const d = new Date(new Date(session.date).getTime() - 5.5 * 60 * 60 * 1000);
                          return (
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              py={0.75}
                              sx={{ borderBottom: `1px solid ${colors.divider}`, '&:last-child': { borderBottom: 'none' } }}
                            >
                              <Box flex={1} minWidth={0}>
                                <Typography variant="body2" fontWeight={600} noWrap>
                                  {session.className}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'Asia/Kolkata' })}
                                  {' · '}
                                  {d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' })}
                                </Typography>
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 1, whiteSpace: 'nowrap' }}>
                                {session.slots} slots
                              </Typography>
                            </Box>
                          );
                        };

                        return (
                          <Box display="flex" flexDirection="column" gap={2}>
                            {/* Upcoming */}
                            <Box>
                              <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <Chip
                                  label={`Upcoming · ${upcoming.length}`}
                                  size="small"
                                  sx={{ backgroundColor: `${colors.success}15`, color: colors.success, fontWeight: 600 }}
                                />
                              </Box>
                              {upcoming.length === 0 ? (
                                <Typography variant="caption" color="text.secondary">No upcoming sessions</Typography>
                              ) : (
                                upcoming.slice(0, 5).map(s => <SessionRow key={s._id} session={s} />)
                              )}
                              {upcoming.length > 5 && (
                                <Typography variant="caption" color="text.secondary">
                                  +{upcoming.length - 5} more
                                </Typography>
                              )}
                            </Box>

                            {/* Passed */}
                            <Box>
                              <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <Chip
                                  label={`Passed · ${passed.length}`}
                                  size="small"
                                  sx={{ backgroundColor: `${colors.textSecondary}15`, color: colors.textSecondary, fontWeight: 600 }}
                                />
                              </Box>
                              {passed.length === 0 ? (
                                <Typography variant="caption" color="text.secondary">No past sessions</Typography>
                              ) : (
                                passed.slice(0, 3).map(s => <SessionRow key={s._id} session={s} />)
                              )}
                              {passed.length > 3 && (
                                <Typography variant="caption" color="text.secondary">
                                  +{passed.length - 3} more
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        );
                      })()}
                    </CardContent>
                  </Card>
                </Box>
              </Box>
            </Box>
          )}

          {activeTab === 1 && (
            /* Events Tab - Horizontal Card Layout */
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" fontWeight={600}>
                  Studio Events ({events.length})
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setCreateEventDialogOpen(true)}
                  size="small"
                >
                  Create Event
                </Button>
              </Box>
              {isLoadingEvents ? (
                <TabListLoading count={4} />
              ) : eventsError ? (
                <Box alignItems="center" display="flex" flexDirection="column" gap={2} justifyContent="center" py={6}>
                  <Typography color="error.main" fontWeight={500} variant="body1">
                    {eventsError}
                  </Typography>
                  <Button color="primary" onClick={() => fetchEvents()} variant="outlined">
                    Retry Loading Events
                  </Button>
                </Box>
              ) : events.length === 0 ? (
                <Card sx={{ border: `1px solid ${colors.divider}`, p: 4, textAlign: 'center' }}>
                  <Event sx={{ fontSize: 48, color: colors.textSecondary, mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    No events found for this studio
                  </Typography>
                </Card>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {events.map((event) => (
                    <Card 
                      key={event._id}
                      sx={{ 
                        border: `1px solid ${colors.divider}`,
                        borderLeft: `4px solid ${colors.primary}`,
                        borderRadius: 2,
                        '&:hover': { 
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          borderLeftColor: colors.secondary,
                          transition: 'all 0.2s ease'
                        } 
                      }}
                    >
                      <CardContent sx={{ p: 2.5 }}>
                        <Box display="flex" alignItems="flex-start" gap={2}>
                          <Box
                            sx={{
                              width: 56,
                              height: 56,
                              borderRadius: 2,
                              backgroundColor: colors.backgroundTertiary,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: colors.primary,
                              flexShrink: 0,
                            }}
                          >
                            <Event sx={{ fontSize: 28 }} />
                          </Box>
                          <Box flex={1}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                              <Box display="flex" alignItems="center" gap={1.5}>
                                <Typography 
                                  variant="h6" 
                                  fontWeight={700} 
                                  color={colors.textPrimary}
                                  sx={{ cursor: 'pointer' }}
                                  onClick={() => handleEventClick(event._id)}
                                >
                                  {event.eventName}
                                </Typography>
                                {event.skillLevel && (
                                  <Chip
                                    label={event.skillLevel}
                                    size="small"
                                    sx={{ 
                                      backgroundColor: colors.backgroundTertiary,
                                      color: colors.textPrimary,
                                      fontWeight: 500,
                                      border: `1px solid ${colors.divider}`,
                                      height: 24,
                                    }}
                                  />
                                )}
                              </Box>
                              <IconButton 
                                size="small" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedEvent(event);
                                  setEventAnchorEl(e.currentTarget);
                                }}
                              >
                                <MoreVert />
                              </IconButton>
                            </Box>
                            <Box display="flex" alignItems="center" gap={1} mb={2}>
                              <Schedule sx={{ fontSize: 16, color: colors.textSecondary }} />
                              <Typography variant="body2" color="text.secondary">
                                {(() => {
                                  // Backend stores IST time values as UTC (e.g., 18:30 UTC represents 18:30 IST)
                                  // Extract UTC components directly and display as IST
                                  const dateObj = new Date(event.date);
                                  const weekdayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                                  const weekday = weekdayNames[dateObj.getUTCDay()];
                                  const day = String(dateObj.getUTCDate()).padStart(2, '0');
                                  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                  const month = monthNames[dateObj.getUTCMonth()];
                                  const year = dateObj.getUTCFullYear();
                                  const hours = String(dateObj.getUTCHours()).padStart(2, '0');
                                  const minutes = String(dateObj.getUTCMinutes()).padStart(2, '0');
                                  return `${weekday}, ${day} ${month}, ${year}, ${hours}:${minutes}`;
                                })()}
                              </Typography>
                            </Box>
                            <Box display="flex" gap={3}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Payments sx={{ fontSize: 18, color: colors.textSecondary }} />
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    Price
                                  </Typography>
                                  <Typography variant="body1" fontWeight={700} color={colors.textPrimary}>
                                    ₹{event.price?.toLocaleString()}
                                  </Typography>
                                </Box>
                              </Box>
                              <Box display="flex" alignItems="center" gap={1}>
                                <People sx={{ fontSize: 18, color: colors.textSecondary }} />
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    Slots Available
                                  </Typography>
                                  <Typography variant="body1" fontWeight={700} color={colors.textPrimary}>
                                    {event.slots}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {activeTab === 2 && (() => {
            if (isLoadingDaySessions) {
              return (
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h6" fontWeight={600}>
                      Sessions
                    </Typography>
                    <Box display="flex" gap={1}>
                      <Button variant="outlined" startIcon={<Tune />} endIcon={<KeyboardArrowDown />} size="small" disabled>
                        More
                      </Button>
                      <Button variant="contained" startIcon={<Add />} size="small" disabled>
                        Create Sessions
                      </Button>
                    </Box>
                  </Box>
                  <TabGridLoading count={6} variant="sessionCategory" />
                </Box>
              );
            }

            if (daySessionsError) {
              return (
                <Box alignItems="center" display="flex" flexDirection="column" gap={2} justifyContent="center" py={6}>
                  <Typography color="error.main" fontWeight={500} variant="body1">
                    {daySessionsError}
                  </Typography>
                  <Button color="primary" onClick={() => fetchSessionsForDate(selectedCategoryDate)} variant="outlined">
                    Retry Loading Sessions
                  </Button>
                </Box>
              );
            }

            const todayStr = getISTDateString();

            // Class-type sub-filter (Wellness / Pilates / Mat / … dropdown) — disabled.
            // Workout and Wellness tabs now list every session for that tab together.
            // const categoryMap = new Map<string, { title: string; sessions: any[] }>();
            // daySessions.forEach((s) => {
            //   const catId = s.classType?._id || 'uncategorised';
            //   const catTitle = s.classType?.title || 'Uncategorised';
            //   if (!categoryMap.has(catId)) categoryMap.set(catId, { title: catTitle, sessions: [] });
            //   categoryMap.get(catId)!.sessions.push(s);
            // });
            // const categories = Array.from(categoryMap.entries())
            //   .map(([id, val]) => ({ id, ...val }))
            //   .sort((a, b) => {
            //     const aFirst = Math.min(...a.sessions.map((s: any) => new Date(s.date).getTime()));
            //     const bFirst = Math.min(...b.sessions.map((s: any) => new Date(s.date).getTime()));
            //     return aFirst - bFirst;
            //   });
            // const activeCategoryId =
            //   selectedCategory && categoryMap.has(selectedCategory)
            //     ? selectedCategory
            //     : categories[0]?.id ?? null;
            // const categorySessions = activeCategoryId
            //   ? (categoryMap.get(activeCategoryId)?.sessions || [])
            //   : [];
            const categorySessions = daySessions;

            const toISTDateStr = (d: Date): string => {
              const ist = new Date(d.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
              return `${ist.getFullYear()}-${String(ist.getMonth() + 1).padStart(2, '0')}-${String(ist.getDate()).padStart(2, '0')}`;
            };

            const currentMonth = selectedCategoryDate.slice(0, 7);
            const monthSessionsList = monthSessions[currentMonth] || [];
            const isMonthSessionsLoading = monthSessions[currentMonth] === undefined;
            // const monthSessionsForCategory = activeCategoryId
            //   ? monthSessionsList.filter((s) => (s.classType?._id || 'uncategorised') === activeCategoryId)
            //   : monthSessionsList;
            const monthSessionsForCategory = monthSessionsList;

            const isSelectedDatePast = selectedCategoryDate < todayStr;
            const pastCachedSessions = pastDaySessions[selectedCategoryDate];
            const isPastDateLoading = isSelectedDatePast && pastCachedSessions === undefined;

            const sessionsOnSelectedDate = isSelectedDatePast
              ? (pastCachedSessions || [])
              : (isMonthSessionsLoading
                  ? categorySessions
                  : monthSessionsForCategory.filter(
                      (s) => toISTDateStr(new Date(s.date)) === selectedCategoryDate,
                    ));

            const sessionSearchLower = sessionSearchTerm.trim().toLowerCase();
            const filteredSessionsOnSelectedDate = sessionSearchLower
              ? sessionsOnSelectedDate.filter(
                  (s) =>
                    s.className?.toLowerCase().includes(sessionSearchLower) ||
                    s.instructor?.name?.toLowerCase().includes(sessionSearchLower) ||
                    s.classType?.title?.toLowerCase().includes(sessionSearchLower),
                )
              : sessionsOnSelectedDate;

            const datesWithSessions = new Set([
              ...monthSessionsForCategory.map((s) => toISTDateStr(new Date(s.date))),
              ...Object.entries(pastDaySessions)
                .filter(([d, s]) => d.slice(0, 7) === currentMonth && s.length > 0)
                .map(([d]) => d),
            ]);

            return (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {sessionsOfferWellness && (
                      <ToggleButtonGroup
                        value={sessionCategory}
                        exclusive
                        size="small"
                        onChange={(_e, val) => {
                          if (!val || val === sessionCategory) return;
                          setSessionCategory(val);
                          setDaySessions([]);
                          setMonthSessions({});
                          setPastDaySessions({});
                          loadedMonthsRef.current.clear();
                          loadedDaysRef.current.clear();
                        }}
                        sx={{ mr: 1 }}
                      >
                        <ToggleButton value="workout" sx={{ px: 1.5 }}>Workout</ToggleButton>
                        <ToggleButton value="wellness" sx={{ px: 1.5 }}>Wellness</ToggleButton>
                      </ToggleButtonGroup>
                    )}
                    {/* Class-type sub-filter dropdown — disabled; Workout/Wellness tabs show all sessions. */}
                    {/* {categories.length > 1 ? (
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <Select
                          value={activeCategoryId}
                          onChange={(e) => setSelectedCategory(e.target.value as string)}
                          sx={{
                            fontWeight: 600,
                            '& .MuiSelect-select': { py: 0.75 },
                          }}
                        >
                          {categories.map((cat) => (
                            <MenuItem key={cat.id} value={cat.id}>
                              {cat.title}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : categories.length === 1 ? (
                      <Typography variant="h6" fontWeight={600}>{categories[0].title}</Typography>
                    ) : (
                      <Typography variant="h6" fontWeight={600}>Sessions</Typography>
                    )} */}
                    <Typography variant="h6" fontWeight={600}>Sessions</Typography>
                    {categorySessions.length > 0 && (
                      <Chip label={`${categorySessions.length} session${categorySessions.length === 1 ? '' : 's'}`} size="small" color="primary" variant="outlined" />
                    )}
                  </Box>
                  {/* Two controls, the same as the Sessions page: Create Sessions
                      (one form for one session, a range, one location or
                      several — this location ticked) and a More menu. This
                      page IS one location, so More never asks which one. */}
                  <Box display="flex" gap={1}>
                    <Button
                      variant="outlined"
                      startIcon={<Tune />}
                      endIcon={<KeyboardArrowDown />}
                      onClick={(e) => setSessionBulkMenuAnchor(e.currentTarget)}
                      size="small"
                    >
                      More
                    </Button>
                    <Menu
                      anchorEl={sessionBulkMenuAnchor}
                      open={!!sessionBulkMenuAnchor}
                      onClose={() => setSessionBulkMenuAnchor(null)}
                    >
                      <MenuItem onClick={() => { setSessionBulkMenuAnchor(null); setCopyDayDialogOpen(true); }}>
                        <ListItemIcon><ContentCopy fontSize="small" /></ListItemIcon>
                        <ListItemText primary="Copy Day" secondary="Copy one day's schedule to other dates" />
                      </MenuItem>
                      <MenuItem onClick={() => { setSessionBulkMenuAnchor(null); setBulkSessionWorkspaceOpen(true); }}>
                        <ListItemIcon><Tune fontSize="small" /></ListItemIcon>
                        <ListItemText primary="Bulk Edit / Delete" secondary="Change or remove many sessions at once" />
                      </MenuItem>
                      {multiLocation && (
                        <MenuItem onClick={() => { setSessionBulkMenuAnchor(null); setMultiOpen('importSessions'); }}>
                          <ListItemIcon><FileDownloadOutlined fontSize="small" /></ListItemIcon>
                          <ListItemText primary="Import from a location" secondary="Copy another location's sessions here" />
                        </MenuItem>
                      )}
                    </Menu>
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={() => setCreateSessionDialogOpen(true)}
                      size="small"
                    >
                      Create Sessions
                    </Button>
                  </Box>
                </Box>

                <Box display="flex" gap={3} flexWrap="wrap" alignItems="flex-start">
                  <Card sx={{ border: `1px solid ${colors.divider}`, minWidth: 280, flex: '0 0 auto' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="body2" fontWeight={600} mb={1.5} color={colors.textSecondary}>
                        Select Date
                      </Typography>
                      {(() => {
                        const [calYear, calMonth] = selectedCategoryDate.split('-').map(Number);
                        const firstDay = new Date(calYear, calMonth - 1, 1).getDay();
                        const daysInMonth = new Date(calYear, calMonth, 0).getDate();
                        const prevMonth = () => {
                          const d = new Date(calYear, calMonth - 2, 1);
                          setSelectedCategoryDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`);
                        };
                        const nextMonth = () => {
                          const d = new Date(calYear, calMonth, 1);
                          setSelectedCategoryDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`);
                        };
                        const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                        const cells: React.ReactNode[] = [];
                        for (let i = 0; i < firstDay; i++) cells.push(<Box key={`e${i}`} />);
                        for (let day = 1; day <= daysInMonth; day++) {
                          const dateStr = `${calYear}-${String(calMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                          const isToday = dateStr === todayStr;
                          const isSelected = dateStr === selectedCategoryDate;
                          const hasSessions = datesWithSessions.has(dateStr);
                          cells.push(
                            <Box
                              key={day}
                              onClick={() => setSelectedCategoryDate(dateStr)}
                              sx={{
                                width: 34,
                                height: 34,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '50%',
                                cursor: 'pointer',
                                position: 'relative',
                                fontWeight: isToday ? 700 : 400,
                                fontSize: '0.82rem',
                                bgcolor: isSelected ? colors.primary : 'transparent',
                                color: isSelected ? '#fff' : isToday ? colors.primary : colors.textPrimary,
                                border: isToday && !isSelected ? `1.5px solid ${colors.primary}` : 'none',
                                '&:hover': { bgcolor: isSelected ? colors.primary : colors.backgroundTertiary },
                                '&::after': hasSessions && !isSelected ? {
                                  content: '""',
                                  position: 'absolute',
                                  bottom: 3,
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  width: 4,
                                  height: 4,
                                  borderRadius: '50%',
                                  bgcolor: colors.secondary,
                                } : {},
                              }}
                            >
                              {day}
                            </Box>
                          );
                        }
                        return (
                          <>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                              <IconButton size="small" onClick={prevMonth}><ChevronLeft fontSize="small" /></IconButton>
                              <Typography variant="body2" fontWeight={600}>{monthNames[calMonth - 1]} {calYear}</Typography>
                              <IconButton size="small" onClick={nextMonth} sx={{ transform: 'rotate(180deg)' }}><ChevronLeft fontSize="small" /></IconButton>
                            </Box>
                            <Box
                              display="grid"
                              sx={{
                                gridTemplateColumns: 'repeat(7, 34px)',
                                gap: 0.25,
                                justifyContent: 'center',
                                opacity: isMonthSessionsLoading ? 0.65 : 1,
                                transition: 'opacity 0.3s ease',
                                ...(isMonthSessionsLoading && {
                                  animation: 'sessionCalendarPulse 1.5s ease-in-out infinite',
                                  '@keyframes sessionCalendarPulse': {
                                    '0%, 100%': { opacity: 0.55 },
                                    '50%': { opacity: 0.8 },
                                  },
                                }),
                              }}
                            >
                              {['S','M','T','W','T','F','S'].map((d, i) => (
                                <Box key={i} sx={{ width: 34, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', color: colors.textSecondary, fontWeight: 600 }}>{d}</Box>
                              ))}
                              {cells}
                            </Box>
                          </>
                        );
                      })()}
                    </CardContent>
                  </Card>

                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      placeholder="Search by class name, instructor, or type..."
                      value={sessionSearchTerm}
                      onChange={(e) => setSessionSearchTerm(e.target.value)}
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': { borderRadius: 2 },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search sx={{ color: colors.textSecondary }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Typography variant="body1" fontWeight={600} mb={2}>
                      {selectedCategoryDate === todayStr ? "Today's Sessions" : `Sessions on ${selectedCategoryDate}`}
                      {' '}
                      <Typography component="span" variant="body2" color="text.secondary">
                        ({filteredSessionsOnSelectedDate.length}
                        {sessionSearchLower && sessionsOnSelectedDate.length !== filteredSessionsOnSelectedDate.length
                          ? ` of ${sessionsOnSelectedDate.length}`
                          : ''}{' '}
                        found)
                      </Typography>
                    </Typography>
                    {isPastDateLoading ? (
                      <Card sx={{ border: `1px solid ${colors.divider}`, p: 3, textAlign: 'center' }}>
                        <CircularProgress size={24} sx={{ mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">Loading sessions for this date…</Typography>
                      </Card>
                    ) : filteredSessionsOnSelectedDate.length === 0 ? (
                      <Card sx={{ border: `1px solid ${colors.divider}`, p: 3, textAlign: 'center' }}>
                        <CalendarMonth sx={{ fontSize: 36, color: colors.textSecondary, mb: 1, opacity: 0.4 }} />
                        <Typography variant="body2" color="text.secondary">
                          {sessionSearchLower
                            ? 'No sessions match your search on this date'
                            : 'No sessions on this date'}
                        </Typography>
                      </Card>
                    ) : (
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
                        {[...filteredSessionsOnSelectedDate].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()).map((session) => {
                          const dateObj = new Date(session.date);
                          const ist = new Date(dateObj.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
                          const hours = String(ist.getHours()).padStart(2, '0');
                          const minutes = String(ist.getMinutes()).padStart(2, '0');
                          return (
                            <Card
                              key={session._id}
                              sx={{
                                border: `1px solid ${colors.divider}`,
                                borderRadius: 2,
                                transition: 'all 0.2s ease',
                                '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.1)', borderColor: colors.secondary, transform: 'translateY(-2px)' },
                              }}
                            >
                              <CardContent sx={{ p: 2 }}>
                                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                  <Typography variant="body1" fontWeight={600} sx={{ cursor: 'pointer' }} onClick={() => handleSessionClick(session._id)}>
                                    {session.className}
                                  </Typography>
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); setSelectedSession(session); setSessionAnchorEl(e.currentTarget); }}>
                                    <MoreVert />
                                  </IconButton>
                                </Box>
                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                  <Schedule sx={{ fontSize: 14, color: colors.textSecondary }} />
                                  <Typography variant="caption" color="text.secondary">{hours}:{minutes} IST</Typography>
                                </Box>
                                <Box display="flex" justifyContent="flex-end">
                                  <Typography variant="caption" color="text.secondary">{session.duration} min · {session.availableSlots ?? session.slots} slots left</Typography>
                                </Box>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })()}

          {activeTab === 3 && (
            /* Classes Tab */
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" fontWeight={600}>
                  Studio Classes ({classes.length})
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {multiLocation && (
                    <>
                      <Button variant="outlined" size="small" startIcon={<FileDownloadOutlined />} onClick={() => setMultiOpen('importClasses')}>
                        Import from a location
                      </Button>
                      <Button variant="outlined" size="small" startIcon={<AddLocationAlt />} onClick={() => setMultiOpen('class')}>
                        Create at several locations
                      </Button>
                    </>
                  )}
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setCreateClassDialogOpen(true)}
                    size="small"
                  >
                    Create Class
                  </Button>
                </Box>
              </Box>
              {isLoadingClasses ? (
                <TabGridLoading count={6} variant="class" />
              ) : classesError ? (
                <Box alignItems="center" display="flex" flexDirection="column" gap={2} justifyContent="center" py={6}>
                  <Typography color="error.main" fontWeight={500} variant="body1">
                    {classesError}
                  </Typography>
                  <Button color="primary" onClick={() => fetchClasses()} variant="outlined">
                    Retry Loading Classes
                  </Button>
                </Box>
              ) : classes.length === 0 ? (
                <Card sx={{ border: `1px solid ${colors.divider}`, p: 4, textAlign: 'center' }}>
                  <Class sx={{ fontSize: 48, color: colors.textSecondary, mb: 2, opacity: 0.3 }} />
                  <Typography variant="body1" color="text.secondary">
                    No classes found for this studio
                  </Typography>
                </Card>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
                  {classes.map((classItem) => (
                    <Card
                      key={classItem._id}
                      onClick={() => handleClassClick(classItem._id)}
                      sx={{
                        border: `1px solid ${colors.divider}`,
                        borderRadius: 2,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          transform: 'translateY(-4px)',
                        },
                      }}
                    >
                      <CardImageWithPlaceholder
                        src={classItem.image}
                        alt={classItem.className}
                        height={160}
                        fallback={<Class sx={{ fontSize: 40, opacity: 0.35 }} />}
                      />
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                          <Avatar
                            src={classItem.instructor?.image}
                            sx={{
                              width: 40,
                              height: 40,
                              backgroundColor: colors.primary,
                            }}
                          >
                            {classItem.instructor?.name?.[0] || 'C'}
                          </Avatar>
                          <Box flex={1} minWidth={0}>
                            <Typography variant="h6" fontWeight={600} noWrap>
                              {classItem.className}
                            </Typography>
                            {classItem.classType && (
                              <Chip
                                label={classItem.classType.title}
                                size="small"
                                sx={{
                                  backgroundColor: `${colors.secondary}15`,
                                  color: colors.secondary,
                                  fontSize: '0.7rem',
                                  height: 20,
                                  mt: 0.5,
                                }}
                              />
                            )}
                          </Box>
                          <IconButton 
                            size="small" 
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedClass(classItem);
                              setClassAnchorEl(e.currentTarget);
                            }}
                          >
                            <MoreVert />
                          </IconButton>
                        </Box>
                        <Box display="flex" flexDirection="column" gap={1}>
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="caption" color="text.secondary">
                              Duration
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {classItem.duration} mins
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="caption" color="text.secondary">
                              Price
                            </Typography>
                            <Typography variant="body1" fontWeight={700} color={colors.primary}>
                              ₹{classItem.price.toLocaleString('en-IN')}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {activeTab === 18 && studioId && (
            <StudioPacksTab studioId={studioId} studioName={studio.studioName} />
          )}

          {activeTab === 5 && admin?.role !== 'manager' && (
            /* Revenue Tab */
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
                <Typography variant="h6" fontWeight={600}>
                  Revenue Breakdown
                </Typography>
                <Box display="flex" gap={2} flexWrap="wrap">
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Period</InputLabel>
                    <Select
                      value={revenuePeriod}
                      label="Period"
                      onChange={(e) => {
                        handleRevenuePeriodChange(e.target.value as 'all' | 'month' | 'week' | 'year' | 'custom');
                      }}
                    >
                      <MenuItem value="all">All Time</MenuItem>
                      <MenuItem value="month">Month</MenuItem>
                      <MenuItem value="week">Week</MenuItem>
                      <MenuItem value="year">Year</MenuItem>
                      <MenuItem value="custom">Custom Range</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Month Selector */}
                  {revenuePeriod === 'month' && (
                    <TextField
                      label="Select Month"
                      type="month"
                      value={selectedMonth}
                      onChange={(e) => {
                        setSelectedMonth(e.target.value);
                        setRevenueDetails(null);
                      }}
                      size="small"
                      sx={{ minWidth: 180 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}

                  {/* Week Selector */}
                  {revenuePeriod === 'week' && (
                    <TextField
                      label="Select Week"
                      type="week"
                      value={selectedWeek}
                      onChange={(e) => {
                        setSelectedWeek(e.target.value);
                        setRevenueDetails(null);
                      }}
                      size="small"
                      sx={{ minWidth: 180 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}

                  {/* Year Selector */}
                  {revenuePeriod === 'year' && (
                    <TextField
                      label="Select Year"
                      type="number"
                      value={selectedYear}
                      onChange={(e) => {
                        setSelectedYear(e.target.value);
                        setRevenueDetails(null);
                      }}
                      size="small"
                      sx={{ minWidth: 120 }}
                      inputProps={{ min: 2020, max: new Date().getFullYear() }}
                      InputLabelProps={{ shrink: true }}
                      placeholder="YYYY"
                    />
                  )}

                  {/* Custom Date Range */}
                  {revenuePeriod === 'custom' && (
                    <Box display="flex" gap={1}>
                      <TextField
                        label="Start Date"
                        type="date"
                        value={customStartDate}
                        onChange={(e) => {
                          setCustomStartDate(e.target.value);
                          setRevenueDetails(null);
                        }}
                        size="small"
                        sx={{ minWidth: 150 }}
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        label="End Date"
                        type="date"
                        value={customEndDate}
                        onChange={(e) => {
                          setCustomEndDate(e.target.value);
                          setRevenueDetails(null);
                        }}
                        size="small"
                        sx={{ minWidth: 150 }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Box>
                  )}
                </Box>
              </Box>

              {isLoadingRevenue ? (
                <Box display="flex" justifyContent="center" p={4}>
                  <CircularProgress />
                </Box>
              ) : revenueDetails ? (
                <>
                  {/* Generate Invoice Button */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<Description />}
                      onClick={handleGenerateInvoice}
                      disabled={isLoadingRevenue || !revenueDetails || (revenueDetails.bookingCount === 0 && revenueDetails.packCount === 0)}
                    >
                      Generate Invoice
                    </Button>
                  </Box>

                  {/* Revenue Summary Cards */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 3 }}>
                    <Card sx={{ borderLeft: `4px solid ${colors.warning}` }}>
                      <CardContent>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Total Revenue
                        </Typography>
                        <Typography variant="h5" fontWeight="bold" color={colors.warning}>
                          ₹{revenueDetails.totalRevenue.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </Typography>
                      </CardContent>
                    </Card>
                    <Card sx={{ borderLeft: `4px solid ${colors.primary}` }}>
                      <CardContent>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Base Revenue
                        </Typography>
                        <Typography variant="h5" fontWeight="bold" color={colors.primary}>
                          ₹{revenueDetails.baseRevenue.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </Typography>
                      </CardContent>
                    </Card>
                    <Card sx={{ borderLeft: `4px solid ${colors.info}` }}>
                      <CardContent>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          GST
                        </Typography>
                        <Typography variant="h5" fontWeight="bold" color={colors.info}>
                          ₹{revenueDetails.gst.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </Typography>
                      </CardContent>
                    </Card>
                    <Card sx={{ borderLeft: `4px solid ${colors.success}` }}>
                      <CardContent>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Bookings
                        </Typography>
                        <Typography variant="h5" fontWeight="bold" color={colors.success}>
                          {revenueDetails.bookingCount}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          ₹{revenueDetails.bookingRevenue.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </Typography>
                      </CardContent>
                    </Card>
                    <Card sx={{ borderLeft: `4px solid ${colors.error}` }}>
                      <CardContent>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Pack Purchases
                        </Typography>
                        <Typography variant="h5" fontWeight="bold" color={colors.error}>
                          {revenueDetails.packCount}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          ₹{revenueDetails.packRevenue.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>

                  {/* Source Breakdown */}
                  {(() => {
                    const allBookings = revenueDetails.bookings || [];
                    const sourceCounts: Record<string, { count: number; revenue: number }> = {};
                    allBookings.forEach((b: any) => {
                      const src = b.bookingSource || 'sweatsocial';
                      if (!sourceCounts[src]) sourceCounts[src] = { count: 0, revenue: 0 };
                      sourceCounts[src].count += 1;
                      sourceCounts[src].revenue += b.totalAmount || 0;
                    });
                    const sourceLabels: Record<string, string> = {
                      sweatsocial: 'SweatSocial App',
                      purple_pilates: 'Purple Pilates App',
                      partner_api: 'Partner API (Web)',
                      website: 'Website',
                      admin: 'Admin',
                    };
                    const sourceColors: Record<string, string> = {
                      sweatsocial: colors.primary,
                      purple_pilates: '#7b1fa2',
                      partner_api: '#f57c00',
                      website: colors.info,
                      admin: colors.success,
                    };
                    const entries = Object.entries(sourceCounts);
                    if (entries.length <= 1) return null;
                    return (
                      <Card sx={{ mb: 3 }}>
                        <CardContent>
                          <Typography variant="h6" fontWeight={600} mb={2}>Booking Sources</Typography>
                          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            {entries.map(([src, data]) => (
                              <Card
                                key={src}
                                variant="outlined"
                                sx={{
                                  borderLeft: `4px solid ${sourceColors[src] || colors.textPrimary}`,
                                  minWidth: 160, cursor: 'pointer',
                                  outline: sourceFilter === src ? `2px solid ${sourceColors[src] || colors.primary}` : 'none',
                                }}
                                onClick={() => setSourceFilter(sourceFilter === src ? 'all' : src)}
                              >
                                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                  <Typography variant="caption" color="textSecondary" display="block">
                                    {sourceLabels[src] || src}
                                  </Typography>
                                  <Typography variant="h6" fontWeight="bold" color={sourceColors[src] || colors.textPrimary}>
                                    {data.count}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    ₹{data.revenue.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                  </Typography>
                                </CardContent>
                              </Card>
                            ))}
                          </Box>
                          {sourceFilter !== 'all' && (
                            <Box sx={{ mt: 1 }}>
                              <Chip
                                label={`Filtered: ${sourceLabels[sourceFilter] || sourceFilter}`}
                                onDelete={() => setSourceFilter('all')}
                                size="small"
                                sx={{ backgroundColor: sourceColors[sourceFilter] || colors.primary, color: '#fff' }}
                              />
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })()}

                  {/* Bookings Table */}
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight={600} mb={2}>
                        Booking Transactions ({sourceFilter === 'all' ? revenueDetails.bookingCount : (revenueDetails.bookings || []).filter((b: any) => (b.bookingSource || 'sweatsocial') === sourceFilter).length})
                      </Typography>
                      {revenueDetails.bookings && revenueDetails.bookings.length > 0 ? (
                        <Box sx={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ borderBottom: `2px solid ${colors.divider}` }}>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Date</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>User</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Session/Event</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Source</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Base Price</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>GST</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Total</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Payment ID</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Discount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(revenueDetails.bookings || [])
                              .filter((b: any) => sourceFilter === 'all' || (b.bookingSource || 'sweatsocial') === sourceFilter)
                              .map((booking: any, index: number) => (
                                <tr key={index} style={{ borderBottom: `1px solid ${colors.divider}` }}>
                                  <td style={{ padding: '12px' }}>
                                    {new Date(booking.createdAt).toLocaleDateString('en-IN', {
                                      day: '2-digit',
                                      month: 'short',
                                      year: 'numeric',
                                    })}
                                  </td>
                                  <td style={{ padding: '12px' }}>
                                    <Box>
                                      <Typography variant="body2" fontWeight={500}>
                                        {booking.user.name}
                                      </Typography>
                                      <Typography variant="caption" color="textSecondary">
                                        {booking.user.email}
                                      </Typography>
                                    </Box>
                                  </td>
                                  <td style={{ padding: '12px' }}>
                                    {booking.session ? (
                                      <Box>
                                        <Typography variant="body2" fontWeight={500}>
                                          {booking.session.className}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                          {new Date(booking.session.date).toLocaleDateString('en-IN')}
                                        </Typography>
                                      </Box>
                                    ) : booking.event ? (
                                      <Box>
                                        <Typography variant="body2" fontWeight={500}>
                                          {booking.event.eventName}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                          {new Date(booking.event.date).toLocaleDateString('en-IN')}
                                        </Typography>
                                      </Box>
                                    ) : (
                                      'N/A'
                                    )}
                                  </td>
                                  <td style={{ padding: '12px' }}>
                                    {(() => {
                                      const src = booking.bookingSource || 'sweatsocial';
                                      const srcColors: Record<string, string> = {
                                        sweatsocial: '#1976d2', purple_pilates: '#7b1fa2',
                                        partner_api: '#f57c00', website: '#0288d1', admin: '#388e3c',
                                      };
                                      const srcLabels: Record<string, string> = {
                                        sweatsocial: 'SweatSocial', purple_pilates: 'Purple Pilates',
                                        partner_api: 'Partner API', website: 'Website', admin: 'Admin',
                                      };
                                      return (
                                        <Chip
                                          label={srcLabels[src] || src}
                                          size="small"
                                          sx={{ fontSize: '0.65rem', height: 20, backgroundColor: srcColors[src] || '#666', color: '#fff' }}
                                        />
                                      );
                                    })()}
                                  </td>
                                  <td style={{ padding: '12px' }}>
                                    ₹{booking.basePrice.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                  </td>
                                  <td style={{ padding: '12px' }}>
                                    ₹{booking.taxes.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                  </td>
                                  <td style={{ padding: '12px', fontWeight: 600 }}>
                                    ₹{booking.totalAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                  </td>
                                  <td style={{ padding: '12px' }}>
                                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                      {booking.paymentId}
                                    </Typography>
                                  </td>
                                  <td style={{ padding: '12px' }}>
                                    {booking.promoCode || booking.coupon ? (
                                      <Chip
                                        label={booking.promoCode || booking.coupon}
                                        size="small"
                                        sx={{ fontSize: '0.7rem', height: 20 }}
                                      />
                                    ) : (
                                      '-'
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
                          No booking transactions found
                        </Typography>
                      )}
                    </CardContent>
                  </Card>

                  {/* Pack Purchases Table */}
                  <Card>
                    <CardContent>
                      <Typography variant="h6" fontWeight={600} mb={2}>
                        Pack Purchase Transactions ({revenueDetails.packCount})
                      </Typography>
                      {revenueDetails.packPurchases && revenueDetails.packPurchases.length > 0 ? (
                        <Box sx={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ borderBottom: `2px solid ${colors.divider}` }}>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Date</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>User</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Pack</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Base Price</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>GST</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Total</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Payment ID</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Discount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {revenueDetails.packPurchases.map((pack: any, index: number) => (
                                <tr key={index} style={{ borderBottom: `1px solid ${colors.divider}` }}>
                                  <td style={{ padding: '12px' }}>
                                    {new Date(pack.createdAt).toLocaleDateString('en-IN', {
                                      day: '2-digit',
                                      month: 'short',
                                      year: 'numeric',
                                    })}
                                  </td>
                                  <td style={{ padding: '12px' }}>
                                    <Box>
                                      <Typography variant="body2" fontWeight={500}>
                                        {pack.user.name}
                                      </Typography>
                                      <Typography variant="caption" color="textSecondary">
                                        {pack.user.email}
                                      </Typography>
                                    </Box>
                                  </td>
                                  <td style={{ padding: '12px' }}>
                                    <Typography variant="body2" fontWeight={500}>
                                      {pack.pack.title}
                                    </Typography>
                                  </td>
                                  <td style={{ padding: '12px' }}>
                                    ₹{pack.basePrice.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                  </td>
                                  <td style={{ padding: '12px' }}>
                                    ₹{pack.taxes.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                  </td>
                                  <td style={{ padding: '12px', fontWeight: 600 }}>
                                    ₹{pack.totalAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                  </td>
                                  <td style={{ padding: '12px' }}>
                                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                      {pack.paymentId}
                                    </Typography>
                                  </td>
                                  <td style={{ padding: '12px' }}>
                                    {pack.promoCode || pack.coupon ? (
                                      <Chip
                                        label={pack.promoCode || pack.coupon}
                                        size="small"
                                        sx={{ fontSize: '0.7rem', height: 20 }}
                                      />
                                    ) : (
                                      '-'
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
                          No pack purchase transactions found
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </>
              ) : (
                <Alert severity="info">Select a period to view revenue details</Alert>
              )}
            </Box>
          )}

          {activeTab === 6 && (
            /* Permissions Tab */
            <Box>
              <Typography variant="h6" fontWeight={600} mb={3}>
                Studio Permissions & Settings
              </Typography>
              <Card sx={{ border: `1px solid ${colors.divider}` }}>
                <CardContent>
                  <Box display="flex" flexDirection="column" gap={3}>
                    {/* Studio Status */}
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600} mb={2} color="primary">
                        Studio Status
                      </Typography>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography variant="body1" fontWeight={600} mb={0.5}>
                            Is Active
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Activate or deactivate this studio account
                          </Typography>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={permissions.isActive}
                              onChange={(e) => handlePermissionsChange('isActive', e.target.checked)}
                              color="primary"
                            />
                          }
                          label=""
                        />
                      </Box>
                      <Divider sx={{ my: 2 }} />
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography variant="body1" fontWeight={600} mb={0.5}>
                            Is Subscribed
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Subscription status for this studio
                          </Typography>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={permissions.isSubscribed}
                              onChange={(e) => handlePermissionsChange('isSubscribed', e.target.checked)}
                              color="primary"
                            />
                          }
                          label=""
                        />
                      </Box>
                    </Box>

                    <Divider />

                    {/* Admin Permissions */}
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600} mb={2} color="primary">
                        Admin Permissions
                      </Typography>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography variant="body1" fontWeight={600} mb={0.5}>
                            Is Admin
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Grant admin privileges to this studio
                          </Typography>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={permissions.isAdmin}
                              onChange={(e) => handlePermissionsChange('isAdmin', e.target.checked)}
                              color="primary"
                            />
                          }
                          label=""
                        />
                      </Box>
                    </Box>

                    <Divider />

                    {/* Studio Permissions */}
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600} mb={2} color="primary">
                        Studio Permissions
                      </Typography>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography variant="body1" fontWeight={600} mb={0.5}>
                            Can Create Sessions/Classes
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Allow this studio to create and manage sessions and classes
                          </Typography>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={permissions.allowClassCreation}
                              onChange={(e) => handlePermissionsChange('allowClassCreation', e.target.checked)}
                              color="primary"
                            />
                          }
                          label=""
                        />
                      </Box>
                      <Divider sx={{ my: 2 }} />
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography variant="body1" fontWeight={600} mb={0.5}>
                            Can Create Events
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Allow this studio to create and manage events
                          </Typography>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={permissions.allowEventCreation}
                              onChange={(e) => handlePermissionsChange('allowEventCreation', e.target.checked)}
                              color="primary"
                            />
                          }
                          label=""
                        />
                      </Box>
                      <Divider sx={{ my: 2 }} />
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography variant="body1" fontWeight={600} mb={0.5}>
                            Can Create Manual Bookings
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Allow this studio to create manual bookings directly
                          </Typography>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={permissions.canCreateManualBookings}
                              onChange={(e) => handlePermissionsChange('canCreateManualBookings', e.target.checked)}
                              color="primary"
                            />
                          }
                          label=""
                        />
                      </Box>
                      <Divider sx={{ my: 2 }} />
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography variant="body1" fontWeight={600} mb={0.5}>
                            Enable Trial Classes
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Allow users to book trial classes at this studio
                          </Typography>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={permissions.enableTrialClasses}
                              onChange={(e) => handlePermissionsChange('enableTrialClasses', e.target.checked)}
                              color="primary"
                            />
                          }
                          label=""
                        />
                      </Box>
                      <Divider sx={{ my: 2 }} />
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography variant="body1" fontWeight={600} mb={0.5}>
                            Pack-Only Booking
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Require users to have an active class pack to book sessions
                          </Typography>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={permissions.packOnlyBooking}
                              onChange={(e) => handlePermissionsChange('packOnlyBooking', e.target.checked)}
                              color="primary"
                            />
                          }
                          label=""
                        />
                      </Box>
                      {/*
                        "Enable Multi-Branch" is gone. It gated a studio's
                        ability to create branches under itself — a permission
                        that no longer describes anything, because branches
                        belong to a brand and are created from the Brands page.
                      */}
                      <Divider sx={{ my: 2 }} />
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography variant="body1" fontWeight={600} mb={0.5}>
                            Enable Session Types (Group / Private)
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Allow classes to be tagged as Group or Private. Users see sessions based on their pack type.
                          </Typography>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={permissions.enableSessionTypes}
                              onChange={(e) => handlePermissionsChange('enableSessionTypes', e.target.checked)}
                              color="primary"
                            />
                          }
                          label=""
                        />
                      </Box>
                      <Divider sx={{ my: 2 }} />
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography variant="body1" fontWeight={600} mb={0.5}>
                            Studio QR Check-In{studio?.whitelabelConfig?.appName ? ` (${studio.whitelabelConfig.appName})` : ''}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Enable a static QR code for the studio. Members scan it to self check-in or book a session using their pack.
                          </Typography>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={permissions.studioQrEnabled}
                              onChange={(e) => handlePermissionsChange('studioQrEnabled', e.target.checked)}
                              color="primary"
                            />
                          }
                          label=""
                        />
                      </Box>
                      {permissions.studioQrEnabled && studio && (() => {
                        // Per-studio deep link: scheme + app name come from this
                        // studio's whitelabelConfig, NOT a hardcoded app.
                        const appName = studio.whitelabelConfig?.appName || studio.studioName;
                        const scheme = studio.whitelabelConfig?.deepLinkScheme;
                        if (!scheme) {
                          return (
                            <Box mt={2} p={3} border="1px solid" borderColor="divider" borderRadius={2} bgcolor="background.paper">
                              <Alert severity="info">
                                Set this studio's <strong>Deep Link Scheme</strong> in the Whitelabel settings to generate the check-in QR code.
                              </Alert>
                            </Box>
                          );
                        }
                        const qrValue = `${scheme}://checkin?studioId=${studio._id}`;
                        return (
                          <Box mt={2} p={3} border="1px solid" borderColor="divider" borderRadius={2} textAlign="center" bgcolor="background.paper">
                            <Typography variant="body2" color="text.secondary" mb={2}>
                              Display this QR code at the studio. Members scan it with the {appName} app to check in.
                            </Typography>
                            <QRCodeCanvas
                              id="studio-qr-canvas"
                              value={qrValue}
                              size={200}
                              level="M"
                              marginSize={2}
                            />
                            <Box mt={1}>
                              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                {qrValue}
                              </Typography>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => downloadQrCode(studio._id, studio.studioName)}
                              >
                                Download PNG
                              </Button>
                            </Box>
                          </Box>
                        );
                      })()}
                    </Box>

                    <Divider />

                    {/* One Day One Session */}
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>One Day, One Session</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Restrict members to a single session booking per day (QR scan check-in only).
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={permissions.oneDayOneSession ?? false}
                            onChange={(e) => handlePermissionsChange('oneDayOneSession', e.target.checked)}
                            color="primary"
                          />
                        }
                        label=""
                      />
                    </Box>

                    {/* One Class At A Time — a DIFFERENT rule from the one above */}
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>One Class At A Time</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Refuse a booking that overlaps one the member already holds.
                          Unlike “One Day, One Session”, an 8am and a 10am are both
                          allowed — only a genuine clash is blocked.
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={permissions.oneSessionPerTimeSlot ?? false}
                            onChange={(e) => handlePermissionsChange('oneSessionPerTimeSlot', e.target.checked)}
                            color="primary"
                          />
                        }
                        label=""
                      />
                    </Box>

                    {/*
                      "Share Packs Across Branches" moved to the brand, along
                      with the three sibling settings that used to have no
                      switch at all (benefits, referrals, announcements crossed
                      branch lines unconditionally). See /brands/:id → Sharing.
                    */}

                    <Divider />

                    {/* Wellness Feature */}
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="body1" fontWeight={600} mb={0.5}>
                          Enable Wellness Booking
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Unlock the Wellness tab — lets this studio publish services (massage, ice bath, etc.) with slot & instructor selection.
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={permissions.enableWellness ?? false}
                            onChange={(e) => handlePermissionsChange('enableWellness', e.target.checked)}
                            color="primary"
                          />
                        }
                        label=""
                      />
                    </Box>

                    <Divider />

                    {/* Seat selection / rooms */}
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="body1" fontWeight={600} mb={0.5}>
                          Enable Seat Selection
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Let this studio define rooms (bike/reformer layouts) and have members pick a
                          specific machine. Off by default — without it the Rooms page and every
                          seat-layout endpoint are refused. A class only gets seats once a room is
                          attached to it.
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={permissions.enableSeatSelection ?? false}
                            onChange={(e) => handlePermissionsChange('enableSeatSelection', e.target.checked)}
                            color="primary"
                          />
                        }
                        label=""
                      />
                    </Box>

                    {/* Bulk pack booking — super admin only (the server drops it for anyone else). */}
                    {admin?.role === 'super_admin' && (
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography variant="body1" fontWeight={600} mb={0.5}>
                            Enable Bulk Pack Booking
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Lets this studio's staff book one member onto many classes at once from
                            their pack (Pack Subscriptions → Bulk book). Off by default — without it
                            the option is hidden and the bulk booking endpoints are refused.
                          </Typography>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={permissions.enableBulkPackBooking ?? false}
                              onChange={(e) => handlePermissionsChange('enableBulkPackBooking', e.target.checked)}
                              color="primary"
                            />
                          }
                          label=""
                        />
                      </Box>
                    )}

                    {/* Guest booking — super admin only (the server drops it for anyone else). */}
                    {admin?.role === 'super_admin' && (
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography variant="body1" fontWeight={600} mb={0.5}>
                            Guest booking (book friends from own pack)
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Members can book friends onto a class using credits from their own pack.
                            Each pack must also allow it.
                          </Typography>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={permissions.enableGuestBooking ?? false}
                              onChange={(e) => handlePermissionsChange('enableGuestBooking', e.target.checked)}
                              color="primary"
                            />
                          }
                          label=""
                        />
                      </Box>
                    )}

                    {/* Studio booking website */}
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="body1" fontWeight={600} mb={0.5}>
                          Enable Booking Website
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Publishes this studio's own booking page at the address below
                          — the studio links to it from their own site. Off by default: the
                          address resolves for every studio the moment the site is deployed, so
                          without this a studio that never agreed to it would have a public page
                          taking payments in its name. Payments go to the studio's own Razorpay
                          account when it has one, and to SweatSocial's when it does not.
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={bookingWebsite}
                            onChange={(e) => {
                              setBookingWebsite(e.target.checked);
                              setBookingWebsiteChanged(true);
                            }}
                            color="primary"
                          />
                        }
                        label=""
                      />
                    </Box>

                    {/* Booking website address */}
                    {(() => {
                      const slug = bookingSlug.trim().toLowerCase();
                      const domain = bookingDomain.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '');
                      const fromApp = (studio?.whitelabelConfig?.appSource || '').toLowerCase().replace(/[^a-z0-9]/g, '');
                      const url = domain
                        ? `https://${domain}`
                        : slug
                          ? `https://${slug}.book.sweatsocial.club`
                          : fromApp
                            ? `https://${fromApp}.book.sweatsocial.club`
                            : '';
                      const previewUrl = `https://book.sweatsocial.club/?studio=${studio?._id}`;
                      const unsaved = bookingAddressChanged || bookingWebsiteChanged;
                      return (
                        <Box sx={{ border: `1px solid ${colors.divider}`, borderRadius: 2, p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          <Box>
                            <Typography variant="body2" fontWeight={600} mb={1}>Website address</Typography>
                            <TextField
                              size="small"
                              fullWidth
                              placeholder={fromApp || 'yourstudio'}
                              value={bookingSlug}
                              onChange={(e) => setBookingSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                              inputProps={{ maxLength: 40, spellCheck: false, 'aria-label': 'Website address' }}
                              InputProps={{ endAdornment: <InputAdornment position="end">.book.sweatsocial.club</InputAdornment> }}
                              helperText={
                                fromApp && !slug
                                  ? `Empty uses the app's address (${fromApp}). Letters, numbers and hyphens.`
                                  : 'Letters, numbers and hyphens, 3–40 characters. Leave empty to remove.'
                              }
                            />
                          </Box>
                          {showBookingDomain ? (
                            <TextField
                              size="small"
                              fullWidth
                              label="Studio's own domain (optional)"
                              placeholder="book.yourstudio.com"
                              value={bookingDomain}
                              onChange={(e) => setBookingDomain(e.target.value)}
                              inputProps={{ spellCheck: false }}
                              helperText="The studio adds a CNAME record for this host pointing to cname.vercel-dns.com, and it is added to the website project on Vercel. Takes priority over the address above. Leave empty to remove."
                            />
                          ) : (
                            <Box>
                              <Button size="small" onClick={() => setShowBookingDomain(true)} sx={{ textTransform: 'none', p: 0, minWidth: 0 }}>
                                Use the studio's own domain instead
                              </Button>
                            </Box>
                          )}
                          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap"
                            sx={{ bgcolor: url ? (bookingWebsite ? '#f1f8f1' : '#fafafa') : '#fff4e5', borderRadius: 1.5, px: 1.5, py: 1 }}>
                            {url ? (
                              <>
                                <Chip size="small" label={bookingWebsite && !unsaved ? 'Live' : unsaved ? 'Save to apply' : 'Off'}
                                  color={bookingWebsite && !unsaved ? 'success' : 'default'} />
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', flex: 1, minWidth: 0 }}>{url}</Typography>
                                <Tooltip title={bookingLinkCopied ? 'Copied' : 'Copy link'}>
                                  <IconButton size="small" aria-label="Copy link" onClick={() => {
                                    navigator.clipboard?.writeText(url).then(() => {
                                      setBookingLinkCopied(true);
                                      setTimeout(() => setBookingLinkCopied(false), 1500);
                                    }).catch(() => undefined);
                                  }}>
                                    <ContentCopy fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Button size="small" href={url} target="_blank" rel="noopener noreferrer" disabled={!bookingWebsite || unsaved} sx={{ textTransform: 'none' }}>
                                  Open
                                </Button>
                              </>
                            ) : (
                              <Typography variant="body2">
                                No address yet. Add one above; until then the site opens only from the preview link{' '}
                                <Box component="span" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{previewUrl}</Box>
                                {bookingWebsite ? '' : ' (once the website is switched on)'}.
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      );
                    })()}

                    {/* Messaging (WhatsApp) */}
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="body1" fontWeight={600} mb={0.5}>
                          Enable Messaging
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Gives this studio the Messaging console for WhatsApp delivery, templates
                          and member consent. Off by default. Turning it on does not start sending —
                          the studio still has to add a sender and approved templates.
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={permissions.enableMessaging ?? false}
                            onChange={(e) => handlePermissionsChange('enableMessaging', e.target.checked)}
                            color="primary"
                          />
                        }
                        label=""
                      />
                    </Box>

                    {/* Referrals — member-get-member */}
                    {/* SweatSocial aggregator listing */}
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="body1" fontWeight={600} mb={0.5}>
                          List on SweatSocial
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Show this studio in the SweatSocial consumer app and on
                          sweatsocial.club. On by default. Turn it OFF for a whitelabel studio
                          that wants bookings only through its own app — it is then hidden from
                          the studio list, the class browse, the pack browse and the events feed.
                          Everything else keeps working: its own app, staff logins, the partner
                          API and every admin screen are unaffected. This is not the same as
                          deactivating the studio.
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={permissions.listOnSweatSocial ?? true}
                            onChange={(e) => handlePermissionsChange('listOnSweatSocial', e.target.checked)}
                            color="primary"
                          />
                        }
                        label=""
                      />
                    </Box>

                    {/* Super-admin only: turning this on starts shortening packs
                        members have already paid for, so it is not a studio's own
                        switch to flip. */}
                    {admin?.role === 'super_admin' && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        {/* Opening day. Super-admin only: it decides when packs
                            members have already paid for begin, so it is not a
                            studio's own switch. */}
                        <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
                          <Box>
                            <Typography variant="body1" fontWeight={600} mb={0.5}>
                              Studio opening day
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              While this date is in the future, no pack bought here can start
                              before it — in the app and on the booking website the member is
                              told the pack becomes available when the studio opens, and the
                              earlier dates cannot be picked. Validity is counted from this day,
                              so a pack sold early does not burn down before anyone can book.
                              {' '}Leave empty for a studio that is already open; it also stops
                              applying on its own once the date passes, so there is nothing to
                              switch off afterwards.
                            </Typography>
                          </Box>
                          <TextField
                            type="date"
                            size="small"
                            value={packStartFloor}
                            onChange={(e) => {
                              setPackStartFloor(e.target.value);
                              setPackStartFloorChanged(true);
                            }}
                            InputLabelProps={{ shrink: true }}
                            sx={{ minWidth: 190, flexShrink: 0 }}
                            helperText={packStartFloor ? 'Clear to remove the floor' : 'No floor set'}
                          />
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box>
                            <Typography variant="body1" fontWeight={600} mb={0.5}>
                              Enable penalties for unlimited packs in case of no_show
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Brings the expiry date of an unlimited pack forward when the member
                              repeatedly books and does not attend. Off by default.
                            </Typography>
                          </Box>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={permissions.enableNoShowPackPenalty ?? false}
                                onChange={(e) =>
                                  handlePermissionsChange('enableNoShowPackPenalty', e.target.checked)
                                }
                                color="primary"
                              />
                            }
                            label=""
                          />
                        </Box>
                      </>
                    )}

                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="body1" fontWeight={600} mb={0.5}>
                          Enable Referrals
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Unlocks the Referrals tab here and Invite &amp; Rewards in the studio's app.
                          Off by default, and turning it on issues nothing on its own — the studio
                          still has to create a programme and switch it live. Every reward is funded
                          by the studio, so this is a commercial decision, not a display setting.
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={permissions.enableReferrals ?? false}
                            onChange={(e) => handlePermissionsChange('enableReferrals', e.target.checked)}
                            color="primary"
                          />
                        }
                        label=""
                      />
                    </Box>

                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="body1" fontWeight={600} mb={0.5}>
                          Enable Milestones
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Visit badges with a reward per milestone. Unlocks the Milestones tab;
                          nothing is awarded until the studio switches it live.
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={permissions.enableMilestones ?? false}
                            onChange={(e) => handlePermissionsChange('enableMilestones', e.target.checked)}
                            color="primary"
                          />
                        }
                        label=""
                      />
                    </Box>

                    {/* Merchandise / retail */}
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="body1" fontWeight={600} mb={0.5}>
                          Enable Merchandise
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Unlock the Shop in the app and allow retail orders. Off by default —
                          without it the studio's products are hidden and every order endpoint is refused.
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={permissions.enableMerchandise ?? false}
                            onChange={(e) => handlePermissionsChange('enableMerchandise', e.target.checked)}
                            color="primary"
                          />
                        }
                        label=""
                      />
                    </Box>

                    {/* Retail policy. Only shown once retail is on — these knobs
                        are meaningless for a studio that doesn't sell anything. */}
                    {permissions.enableMerchandise && (
                      <Box pl={2} borderLeft={2} borderColor="divider" display="flex" flexDirection="column" gap={2}>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              Let members cancel their own orders
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Off = orders can only be cancelled by the studio, from the Merchandise → Orders board.
                            </Typography>
                          </Box>
                          <Switch
                            checked={merchPolicy.allowUserCancellation}
                            onChange={(e) => {
                              setMerchPolicy((p) => ({ ...p, allowUserCancellation: e.target.checked }));
                              setMerchPolicyChanged(true);
                              setPermissionsChanged(true);
                            }}
                            color="primary"
                          />
                        </Box>

                        {merchPolicy.allowUserCancellation && (
                          <TextField
                            select size="small" label="Members can cancel up to"
                            value={merchPolicy.cancellableUntilStatus}
                            onChange={(e) => {
                              setMerchPolicy((p) => ({ ...p, cancellableUntilStatus: e.target.value }));
                              setMerchPolicyChanged(true);
                              setPermissionsChanged(true);
                            }}
                            helperText="Past this point the member must contact the studio"
                            sx={{ maxWidth: 320 }}
                          >
                            <MenuItem value="PLACED">Placed — before the studio packs it</MenuItem>
                            <MenuItem value="PACKED">Packed — until it's ready for pickup</MenuItem>
                            <MenuItem value="READY_FOR_PICKUP">Ready for pickup — right up to collection</MenuItem>
                          </TextField>
                        )}

                        <TextField
                          select size="small" label="Refunds"
                          value={merchPolicy.refundMode}
                          onChange={(e) => {
                            setMerchPolicy((p) => ({ ...p, refundMode: e.target.value }));
                            setMerchPolicyChanged(true);
                            setPermissionsChanged(true);
                          }}
                          helperText="Cancelling never moves money on its own — a refund is always deliberate"
                          sx={{ maxWidth: 320 }}
                        >
                          <MenuItem value="manual">Manual — studio refunds from the Orders board</MenuItem>
                          <MenuItem value="none">None — settled outside the app</MenuItem>
                        </TextField>
                      </Box>
                    )}

                    <Divider />

                    {/* Session Waitlist */}
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="body1" fontWeight={600} mb={0.5}>
                          Enable Session Waitlist
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Allow users to join a waitlist when a session is fully booked
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={permissions.enableSessionWaitlist ?? false}
                            onChange={(e) => handlePermissionsChange('enableSessionWaitlist', e.target.checked)}
                            color="primary"
                          />
                        }
                        label=""
                      />
                    </Box>

                    <Divider />

                    {/* Notifications */}
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600} mb={2} color="primary">
                        Notifications
                      </Typography>

                      {/* Booking cutoff mode toggle */}
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                        <Box>
                          <Typography variant="body1" fontWeight={600} mb={0.5}>
                            Use time-of-day cutoffs
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Different cutoff for morning vs evening instead of one fixed value
                          </Typography>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={useBookingSchedule}
                              onChange={(e) => toggleBookingSchedule(e.target.checked)}
                              color="primary"
                            />
                          }
                          label=""
                        />
                      </Box>

                      {!useBookingSchedule ? (
                        /* Booking Cutoff (flat) */
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                          <Box>
                            <Typography variant="body1" fontWeight={600} mb={0.5}>
                              Booking Cutoff (hours)
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Users cannot book or join waitlist within this many hours of session start
                            </Typography>
                          </Box>
                          <TextField
                            type="number"
                            size="small"
                            value={permissions.bookingCutoffHours}
                            onChange={(e) => {
                              const v = parseFloat(e.target.value);
                              if (!isNaN(v) && v >= 0) handlePermissionsNumberChange('bookingCutoffHours', v);
                            }}
                            inputProps={{ min: 0, max: 72, step: 0.5 }}
                            sx={{ width: 100 }}
                          />
                        </Box>
                      ) : (
                        /* Time-of-day booking cutoff schedule */
                        <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
                          <Box>
                            <Typography variant="body1" fontWeight={600} mb={0.5}>
                              Time-of-day Booking Cutoff
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Different cutoff for morning vs evening — overrides the flat value for matching times
                            </Typography>
                          </Box>
                          <Box display="flex" flexDirection="column" gap={1} sx={{ minWidth: 360 }}>
                            <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                              <Button size="small" startIcon={<Add />} onClick={addCutoffRange}>
                                Add time range
                              </Button>
                            </Box>
                            {permissions.bookingCutoffSchedule.length === 0 ? (
                              <Typography variant="caption" color="text.secondary">
                                No time ranges — using the fixed window above.
                              </Typography>
                            ) : (
                              permissions.bookingCutoffSchedule.map((r, index) => (
                                <Box key={index} display="flex" alignItems="center" gap={1}>
                                  <TextField
                                    type="time"
                                    size="small"
                                    label="From"
                                    value={r.startTime}
                                    onChange={e => updateCutoffRange(index, 'startTime', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ step: 60 }}
                                  />
                                  <TextField
                                    type="time"
                                    size="small"
                                    label="To"
                                    value={r.endTime}
                                    onChange={e => updateCutoffRange(index, 'endTime', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ step: 60 }}
                                  />
                                  <TextField
                                    type="number"
                                    size="small"
                                    label="Cutoff (hrs)"
                                    value={r.cutoffHours}
                                    onChange={e => {
                                      const v = parseFloat(e.target.value);
                                      updateCutoffRange(index, 'cutoffHours', isNaN(v) ? 0 : v);
                                    }}
                                    inputProps={{ min: 0, max: 72, step: 0.5 }}
                                    sx={{ width: 110 }}
                                  />
                                  <IconButton size="small" onClick={() => removeCutoffRange(index)}>
                                    <DeleteOutline fontSize="small" />
                                  </IconButton>
                                </Box>
                              ))
                            )}
                            <TimeRangeAudit
                              ranges={permissions.bookingCutoffSchedule || []}
                              fallbackHours={permissions.bookingCutoffHours}
                            />
                          </Box>
                        </Box>
                      )}

                      <Divider sx={{ my: 2 }} />

                      {/* Session Reminder Toggle */}
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                        <Box>
                          <Typography variant="body1" fontWeight={600} mb={0.5}>
                            Enable Session Reminders
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Send a push notification to booked users before their session starts
                          </Typography>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={permissions.sessionReminderEnabled}
                              onChange={(e) => handlePermissionsChange('sessionReminderEnabled', e.target.checked)}
                              color="primary"
                            />
                          }
                          label=""
                        />
                      </Box>

                      {/* Reminder Hours — only shown when enabled */}
                      {permissions.sessionReminderEnabled && (
                        <Box display="flex" alignItems="center" justifyContent="space-between" ml={2}>
                          <Box>
                            <Typography variant="body2" fontWeight={600} mb={0.5}>
                              Reminder timing (hours before session)
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              e.g. 2.5 means notification fires 2h 30m before start
                            </Typography>
                          </Box>
                          <TextField
                            type="number"
                            size="small"
                            value={permissions.sessionReminderHoursBefore}
                            onChange={(e) => {
                              const v = parseFloat(e.target.value);
                              if (!isNaN(v) && v >= 0.5) handlePermissionsNumberChange('sessionReminderHoursBefore', v);
                            }}
                            inputProps={{ min: 0.5, max: 48, step: 0.5 }}
                            sx={{ width: 100 }}
                          />
                        </Box>
                      )}
                    </Box>

                    <Divider />

                    {/* Pack Freeze Policy */}
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600} mb={2} color="primary">
                        Pack Freeze Policy
                      </Typography>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography variant="body1" fontWeight={600} mb={0.5}>
                            Enable Pack Freeze
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Allow users to temporarily pause (freeze) their packs
                          </Typography>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={freezePolicy.enabled}
                              onChange={(e) => {
                                setFreezePolicy(prev => ({ ...prev, enabled: e.target.checked }));
                                setFreezePolicyChanged(true);
                              }}
                              color="primary"
                            />
                          }
                          label=""
                        />
                      </Box>
                      {freezePolicy.enabled && (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                            <Box flex={1}>
                              <Typography variant="body1" fontWeight={600} mb={0.5}>
                                Max Freeze Days Per Month
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Maximum number of days a user can freeze their pack each calendar month
                              </Typography>
                            </Box>
                            <TextField
                              type="number"
                              size="small"
                              value={freezePolicy.maxDaysPerMonth}
                              onChange={(e) => {
                                const v = Math.max(1, parseInt(e.target.value) || 1);
                                setFreezePolicy(prev => ({ ...prev, maxDaysPerMonth: v }));
                                setFreezePolicyChanged(true);
                              }}
                              inputProps={{ min: 1, max: 31 }}
                              sx={{ width: 100 }}
                            />
                          </Box>
                          <Divider sx={{ my: 2 }} />
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box>
                              <Typography variant="body1" fontWeight={600} mb={0.5}>
                                Rollover Unused Freeze Days
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Unused freeze days carry forward to the next month (capped at 3× monthly limit)
                              </Typography>
                            </Box>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={freezePolicy.rolloverFreezedays}
                                  onChange={(e) => {
                                    setFreezePolicy(prev => ({ ...prev, rolloverFreezedays: e.target.checked }));
                                    setFreezePolicyChanged(true);
                                  }}
                                  color="primary"
                                />
                              }
                              label=""
                            />
                          </Box>
                          <Divider sx={{ my: 2 }} />
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box>
                              <Typography variant="body1" fontWeight={600} mb={0.5}>
                                Use Total Pack Duration Freeze Budget
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Instead of a per-month cap, the user's entire freeze budget (Max Days × Pack Duration) is available to use anytime within the pack lifetime. E.g. 7 days/month × 3 months = 21 days total, usable in any distribution.
                              </Typography>
                            </Box>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={freezePolicy.usePackDurationFreeze}
                                  onChange={(e) => {
                                    setFreezePolicy(prev => ({ ...prev, usePackDurationFreeze: e.target.checked }));
                                    setFreezePolicyChanged(true);
                                  }}
                                  color="primary"
                                />
                              }
                              label=""
                            />
                          </Box>
                        </>
                      )}
                    </Box>

                    <Divider />

                    {/* GST Settings */}
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600} mb={2} color="primary">
                        GST Settings
                      </Typography>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography variant="body1" fontWeight={600} mb={0.5}>
                            GST Registered
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Studio collects GST (5%) on bookings and packs
                          </Typography>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={gstSettings.isGstRegistered}
                              onChange={(e) => {
                                setGstSettings(prev => ({ ...prev, isGstRegistered: e.target.checked }));
                                setGstSettingsChanged(true);
                              }}
                              color="primary"
                            />
                          }
                          label=""
                        />
                      </Box>

                      {gstSettings.isGstRegistered && (
                        <>
                          <Box mt={2}>
                            <Typography variant="body2" fontWeight={600} mb={1}>
                              GST Number (optional)
                            </Typography>
                            <TextField
                              size="small"
                              fullWidth
                              placeholder="e.g. 29AABCU9603R1ZX"
                              value={gstSettings.gstNumber}
                              onChange={(e) => {
                                setGstSettings(prev => ({ ...prev, gstNumber: e.target.value }));
                                setGstSettingsChanged(true);
                              }}
                              sx={{ maxWidth: 300 }}
                            />
                          </Box>

                          <Box mt={2}>
                            <Typography variant="body2" fontWeight={600} mb={1}>
                              GST Type
                            </Typography>
                            <Box display="flex" gap={1}>
                              <Chip
                                label="Exclusive (add 5% on top)"
                                clickable
                                color={gstSettings.gstType === 'exclusive' ? 'primary' : 'default'}
                                onClick={() => {
                                  setGstSettings(prev => ({ ...prev, gstType: 'exclusive' }));
                                  setGstSettingsChanged(true);
                                }}
                                size="small"
                              />
                              <Chip
                                label="Inclusive (5% within price)"
                                clickable
                                color={gstSettings.gstType === 'inclusive' ? 'primary' : 'default'}
                                onClick={() => {
                                  setGstSettings(prev => ({ ...prev, gstType: 'inclusive' }));
                                  setGstSettingsChanged(true);
                                }}
                                size="small"
                              />
                            </Box>
                            <Typography variant="caption" color="text.secondary" mt={1} display="block">
                              {gstSettings.gstType === 'exclusive'
                                ? 'Price shown to users + 5% GST on top. E.g. ₹100 → ₹105 total'
                                : 'GST already included in the displayed price. E.g. ₹105 → ₹100 base + ₹5 GST'}
                            </Typography>
                          </Box>
                        </>
                      )}

                    </Box>

                  </Box>
                </CardContent>
              </Card>

              {/* Staff attendance saves on its own (separate collection), not
                  through the permissions Save above. Once on, the studio sees
                  it on each instructor's performance page. */}
              {studioId && (
                <Box mt={3}>
                  <AttendanceSettingsCard studioId={studioId} />
                </Box>
              )}
            </Box>
          )}

          {activeTab === 4 && (
            /* Instructors Tab */
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" fontWeight={600}>
                  Studio Instructors ({instructors.length})
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setCreateInstructorDialogOpen(true)}
                  size="small"
                >
                  Create Instructor
                </Button>
              </Box>
              {isLoadingInstructors ? (
                <TabGridLoading count={6} variant="instructor" />
              ) : instructorsError ? (
                <Box alignItems="center" display="flex" flexDirection="column" gap={2} justifyContent="center" py={6}>
                  <Typography color="error.main" fontWeight={500} variant="body1">
                    {instructorsError}
                  </Typography>
                  <Button color="primary" onClick={() => fetchInstructors()} variant="outlined">
                    Retry Loading Instructors
                  </Button>
                </Box>
              ) : instructors.length === 0 ? (
                <Card sx={{ border: `1px solid ${colors.divider}`, p: 4, textAlign: 'center' }}>
                  <Person sx={{ fontSize: 48, color: colors.textSecondary, mb: 2, opacity: 0.3 }} />
                  <Typography variant="body1" color="text.secondary">
                    No instructors found for this studio
                  </Typography>
                </Card>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
                  {instructors.map((instructor) => (
                    <Card
                      key={instructor._id}
                      // Same destination as the /instructors list — the card
                      // already carried cursor:pointer and a hover lift here
                      // too, so it was advertising an action it did not perform.
                      onClick={() => navigate(`/instructors/${instructor._id}`)}
                      sx={{
                        border: `1px solid ${colors.divider}`,
                        borderRadius: 2,
                        cursor: 'pointer',
                        opacity: instructor.isDisabled ? 0.6 : 1,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          transform: 'translateY(-4px)',
                          borderColor: colors.secondary,
                        },
                      }}
                    >
                      <CardContent>
                        <Box display="flex" alignItems="flex-start" gap={2}>
                          <Avatar
                            src={instructor.image}
                            sx={{
                              width: 72,
                              height: 72,
                              backgroundColor: colors.primary,
                              fontSize: '1.5rem',
                              fontWeight: 700,
                            }}
                          >
                            {instructor.name?.charAt(0) || 'I'}
                          </Avatar>
                          <Box flex={1}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                              <Typography variant="h6" fontWeight={600}>
                                {instructor.name}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedInstructor(instructor);
                                  setInstructorAnchorEl(e.currentTarget);
                                }}
                              >
                                <MoreVert />
                              </IconButton>
                            </Box>
                            <Box display="flex" alignItems="center" gap={0.5} mb={1.5}>
                              <Chip
                                label={instructor.gender}
                                size="small"
                                sx={{
                                  backgroundColor: `${colors.info}15`,
                                  color: colors.info,
                                  fontSize: '0.7rem',
                                  height: 20,
                                }}
                              />
                              {instructor.isDisabled && (
                                <Chip
                                  label="Deactivated"
                                  size="small"
                                  sx={{
                                    backgroundColor: `${colors.textSecondary}20`,
                                    color: colors.textSecondary,
                                    fontSize: '0.7rem',
                                    height: 20,
                                    fontWeight: 600,
                                  }}
                                />
                              )}
                            </Box>
                            <Box display="flex" flexDirection="column" gap={1}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Phone sx={{ fontSize: 16, color: colors.textSecondary }} />
                                <Typography variant="body2" color="text.secondary">
                                  {instructor.phoneNumber}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* Studio-owner Settings tab (index 17): Booking Cutoff + GST cards.
              The Cancellation card below also renders for this tab (its condition
              includes activeTab === 17), giving owners all three sections here. */}
          {activeTab === 17 && (
            <Box>
              <Typography variant="h6" fontWeight={600} mb={3}>
                Studio Settings
              </Typography>

              {/* Booking Cutoff */}
              <Card sx={{ border: `1px solid ${colors.divider}`, mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} mb={2} color="primary">
                    Booking
                  </Typography>
                  {/* Booking cutoff mode toggle */}
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Box>
                      <Typography variant="body1" fontWeight={600} mb={0.5}>
                        Use time-of-day cutoffs
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Different cutoff for morning vs evening instead of one fixed value
                      </Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={useBookingSchedule}
                          onChange={(e) => toggleBookingSchedule(e.target.checked)}
                          color="primary"
                        />
                      }
                      label=""
                    />
                  </Box>

                  {!useBookingSchedule ? (
                    /* Booking Cutoff (flat) */
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <Box>
                        <Typography variant="body1" fontWeight={600} mb={0.5}>
                          Booking Cutoff (hours)
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Users cannot book or join waitlist within this many hours of session start
                        </Typography>
                      </Box>
                      <TextField
                        type="number"
                        size="small"
                        value={permissions.bookingCutoffHours}
                        onChange={(e) => {
                          const v = parseFloat(e.target.value);
                          if (!isNaN(v) && v >= 0) handlePermissionsNumberChange('bookingCutoffHours', v);
                        }}
                        inputProps={{ min: 0, max: 72, step: 0.5 }}
                        sx={{ width: 100 }}
                      />
                    </Box>
                  ) : (
                    /* Time-of-day booking cutoff schedule */
                    <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
                      <Box>
                        <Typography variant="body1" fontWeight={600} mb={0.5}>
                          Time-of-day Booking Cutoff
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Different cutoff for morning vs evening — overrides the flat value for matching times
                        </Typography>
                      </Box>
                      <Box display="flex" flexDirection="column" gap={1} sx={{ minWidth: 360 }}>
                        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                          <Button size="small" startIcon={<Add />} onClick={addCutoffRange}>
                            Add time range
                          </Button>
                        </Box>
                        {permissions.bookingCutoffSchedule.length === 0 ? (
                          <Typography variant="caption" color="text.secondary">
                            No time ranges — using the fixed window above.
                          </Typography>
                        ) : (
                          permissions.bookingCutoffSchedule.map((r, index) => (
                            <Box key={index} display="flex" alignItems="center" gap={1}>
                              <TextField
                                type="time"
                                size="small"
                                label="From"
                                value={r.startTime}
                                onChange={e => updateCutoffRange(index, 'startTime', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ step: 60 }}
                              />
                              <TextField
                                type="time"
                                size="small"
                                label="To"
                                value={r.endTime}
                                onChange={e => updateCutoffRange(index, 'endTime', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ step: 60 }}
                              />
                              <TextField
                                type="number"
                                size="small"
                                label="Cutoff (hrs)"
                                value={r.cutoffHours}
                                onChange={e => {
                                  const v = parseFloat(e.target.value);
                                  updateCutoffRange(index, 'cutoffHours', isNaN(v) ? 0 : v);
                                }}
                                inputProps={{ min: 0, max: 72, step: 0.5 }}
                                sx={{ width: 110 }}
                              />
                              <IconButton size="small" onClick={() => removeCutoffRange(index)}>
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            </Box>
                          ))
                        )}
                        <TimeRangeAudit
                              ranges={permissions.bookingCutoffSchedule || []}
                              fallbackHours={permissions.bookingCutoffHours}
                            />
                      </Box>
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Box>
                      <Typography variant="body1" fontWeight={600} mb={0.5}>
                        Enable Session Reminders
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Send a push notification to booked users before their session starts
                      </Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={permissions.sessionReminderEnabled}
                          onChange={(e) => handlePermissionsChange('sessionReminderEnabled', e.target.checked)}
                          color="primary"
                        />
                      }
                      label=""
                    />
                  </Box>

                  {permissions.sessionReminderEnabled && (
                    <Box display="flex" alignItems="center" justifyContent="space-between" ml={2}>
                      <Box>
                        <Typography variant="body2" fontWeight={600} mb={0.5}>
                          Reminder timing (hours before session)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          e.g. 2.5 means notification fires 2h 30m before start
                        </Typography>
                      </Box>
                      <TextField
                        type="number"
                        size="small"
                        value={permissions.sessionReminderHoursBefore}
                        onChange={(e) => {
                          const v = parseFloat(e.target.value);
                          if (!isNaN(v) && v >= 0.5) handlePermissionsNumberChange('sessionReminderHoursBefore', v);
                        }}
                        inputProps={{ min: 0.5, max: 48, step: 0.5 }}
                        sx={{ width: 100 }}
                      />
                    </Box>
                  )}

                </CardContent>
              </Card>

              {/* GST Settings */}
              <Card sx={{ border: `1px solid ${colors.divider}`, mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} mb={2} color="primary">
                    GST Settings
                  </Typography>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="body1" fontWeight={600} mb={0.5}>
                        GST Registered
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Studio collects GST (5%) on bookings and packs
                      </Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={gstSettings.isGstRegistered}
                          onChange={(e) => {
                            setGstSettings(prev => ({ ...prev, isGstRegistered: e.target.checked }));
                            setGstSettingsChanged(true);
                          }}
                          color="primary"
                        />
                      }
                      label=""
                    />
                  </Box>

                  {gstSettings.isGstRegistered && (
                    <>
                      <Box mt={2}>
                        <Typography variant="body2" fontWeight={600} mb={1}>
                          GST Number (optional)
                        </Typography>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="e.g. 29AABCU9603R1ZX"
                          value={gstSettings.gstNumber}
                          onChange={(e) => {
                            setGstSettings(prev => ({ ...prev, gstNumber: e.target.value }));
                            setGstSettingsChanged(true);
                          }}
                          sx={{ maxWidth: 300 }}
                        />
                      </Box>

                      <Box mt={2}>
                        <Typography variant="body2" fontWeight={600} mb={1}>
                          GST Type
                        </Typography>
                        <Box display="flex" gap={1}>
                          <Chip
                            label="Exclusive (add 5% on top)"
                            clickable
                            color={gstSettings.gstType === 'exclusive' ? 'primary' : 'default'}
                            onClick={() => {
                              setGstSettings(prev => ({ ...prev, gstType: 'exclusive' }));
                              setGstSettingsChanged(true);
                            }}
                            size="small"
                          />
                          <Chip
                            label="Inclusive (5% within price)"
                            clickable
                            color={gstSettings.gstType === 'inclusive' ? 'primary' : 'default'}
                            onClick={() => {
                              setGstSettings(prev => ({ ...prev, gstType: 'inclusive' }));
                              setGstSettingsChanged(true);
                            }}
                            size="small"
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary" mt={1} display="block">
                          {gstSettings.gstType === 'exclusive'
                            ? 'Price shown to users + 5% GST on top. E.g. ₹100 → ₹105 total'
                            : 'GST already included in the displayed price. E.g. ₹105 → ₹100 base + ₹5 GST'}
                        </Typography>
                      </Box>
                    </>
                  )}

                </CardContent>
              </Card>

              {/* Check-In QR — read-only; shown only when QR is enabled for this studio.
                  Owners can view/download it but cannot toggle it (super-admin only). */}
              {permissions.studioQrEnabled && studio && (() => {
                const appName = studio.whitelabelConfig?.appName || studio.studioName;
                const scheme = studio.whitelabelConfig?.deepLinkScheme;
                const qrValue = scheme ? `${scheme}://checkin?studioId=${studio._id}` : '';
                return (
                  <Card sx={{ border: `1px solid ${colors.divider}`, mb: 3 }}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight={600} mb={2} color="primary">
                        Check-In QR Code
                      </Typography>
                      {!scheme ? (
                        <Alert severity="info">
                          Your studio's check-in QR isn't ready yet. Please contact support to enable it.
                        </Alert>
                      ) : (
                        <Box textAlign="center">
                          <Typography variant="body2" color="text.secondary" mb={2}>
                            Display this QR code at your studio. Members scan it with the {appName} app to check in.
                          </Typography>
                          <QRCodeCanvas
                            id="studio-qr-canvas-owner"
                            value={qrValue}
                            size={200}
                            level="M"
                            marginSize={2}
                          />
                          <Box mt={1}>
                            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                              {qrValue}
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => downloadQrCode(studio._id, studio.studioName, 'studio-qr-canvas-owner')}
                            >
                              Download PNG
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                );
              })()}
            </Box>
          )}

          {(activeTab === 7 || activeTab === 17) && (
            /* Cancellation Policy Tab */
            <Box>
              <Typography variant="h6" fontWeight={600} mb={3}>
                Cancellation Policy Settings
              </Typography>
              <Card sx={{ border: `1px solid ${colors.divider}` }}>
                <CardContent>
                  <Box display="flex" flexDirection="column" gap={3}>

                    {/* Master toggle */}
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="body1" fontWeight={600} mb={0.5}>
                          Enable Cancellation Policy
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          When enabled, users must cancel within the defined window to receive a full refund
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={cancellationPolicy.enabled}
                            onChange={(e) => {
                              setCancellationPolicy(prev => ({ ...prev, enabled: e.target.checked }));
                              setCancellationPolicyChanged(true);
                            }}
                            color="primary"
                          />
                        }
                        label=""
                      />
                    </Box>

                    <Divider />

                    {/* Two controls, split by WHO cancelled — that is the
                        distinction people miss, so each label names it. */}
                    <Box>
                      <Typography variant="body1" fontWeight={600} mb={0.5}>
                        Refunds
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={2}>
                        Only affects online payments. Pack bookings always get the class
                        credit back.
                      </Typography>
                      <TextField
                        select
                        size="small"
                        label="When a member cancels"
                        value={refundPolicy.mode}
                        onChange={(e) => {
                          setRefundPolicy((p) => ({ ...p, mode: e.target.value as 'auto' | 'manual' | 'none' }));
                          setCancellationPolicyChanged(true);
                        }}
                        sx={{ minWidth: 320 }}
                        helperText="Only if they cancelled in time. Late cancellations get nothing."
                      >
                        <MenuItem value="auto">Refund automatically</MenuItem>
                        <MenuItem value="manual">Approve each refund manually</MenuItem>
                        <MenuItem value="none">Never refund</MenuItem>
                      </TextField>

                      {/* Hidden outside 'auto': in 'manual' a person already
                          approves everything, so this would change nothing, and
                          in 'none' nothing is ever refunded. */}
                      {refundPolicy.mode === 'auto' && (
                        <Box mt={2}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={refundPolicy.autoOnStudioCancel}
                                onChange={(e) => {
                                  setRefundPolicy((p) => ({ ...p, autoOnStudioCancel: e.target.checked }));
                                  setCancellationPolicyChanged(true);
                                }}
                                color="primary"
                              />
                            }
                            label="Do the same when the studio cancels"
                          />
                          <Typography variant="caption" color="text.secondary" display="block">
                            Off: the refund waits for approval in Bookings &rarr; Refunds.
                            The member is owed it either way, and the cutoff never applies
                            to a booking the studio cancelled.
                          </Typography>
                        </Box>
                      )}

                      {refundPolicy.mode === 'manual' && (
                        <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                          Refunds wait for approval in Bookings &rarr; Refunds. This includes
                          bookings the studio cancels.
                        </Typography>
                      )}

                      {refundPolicy.mode === 'none' && (
                        <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                          No money is ever returned, however the booking was cancelled.
                        </Typography>
                      )}
                    </Box>

                    {cancellationPolicy.enabled && (
                      <>
                        <Divider />

                        {/* Session Policy */}
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600} mb={2} color="primary">
                            Session / Class Cancellation
                          </Typography>
                          {/* Session cancellation mode toggle */}
                          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                            <Box>
                              <Typography variant="body1" fontWeight={600} mb={0.5}>
                                Use time-of-day cancellation cutoffs
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Different cancellation window for morning vs evening
                              </Typography>
                            </Box>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={useCancellationSchedule}
                                  onChange={(e) => toggleCancellationSchedule(e.target.checked)}
                                  color="primary"
                                />
                              }
                              label=""
                            />
                          </Box>

                          {!useCancellationSchedule ? (
                            <>
                              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                                <Box>
                                  <Typography variant="body1" fontWeight={600} mb={0.5}>
                                    Policy Type
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    Standard = 24-hour window; Custom = define your own cutoff
                                  </Typography>
                                </Box>
                                <FormControl size="small" sx={{ minWidth: 160 }}>
                                  <InputLabel>Type</InputLabel>
                                  <Select
                                    label="Type"
                                    value={cancellationPolicy.session.type}
                                    onChange={(e) => {
                                      setCancellationPolicy(prev => ({
                                        ...prev,
                                        session: { ...prev.session, type: e.target.value as 'standard' | 'custom' },
                                      }));
                                      setCancellationPolicyChanged(true);
                                    }}
                                  >
                                    <MenuItem value="standard">Standard (24h)</MenuItem>
                                    <MenuItem value="custom">Custom</MenuItem>
                                  </Select>
                                </FormControl>
                              </Box>
                              {cancellationPolicy.session.type === 'custom' && (
                                <Box display="flex" alignItems="center" gap={2}>
                                  <Typography variant="body2" color="text.secondary">
                                    Cancel at least
                                  </Typography>
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={cancellationPolicy.session.customHours}
                                    onChange={(e) => {
                                      const h = Math.max(1, parseInt(e.target.value) || 1);
                                      setCancellationPolicy(prev => ({
                                        ...prev,
                                        session: { ...prev.session, customHours: h },
                                      }));
                                      setCancellationPolicyChanged(true);
                                    }}
                                    inputProps={{ min: 1, max: 720 }}
                                    sx={{ width: 100 }}
                                  />
                                  <Typography variant="body2" color="text.secondary">
                                    hours before the session for a full refund
                                  </Typography>
                                </Box>
                              )}
                            </>
                          ) : (
                            /* Time-of-day session cancellation schedule */
                            <Box display="flex" flexDirection="column" gap={1}>
                              <Box display="flex" alignItems="center" justifyContent="flex-start" gap={1}>
                                <Button size="small" startIcon={<Add />} onClick={addCancellationRange}>
                                  Add time range
                                </Button>
                              </Box>
                              {cancellationPolicy.session.schedule.length === 0 ? (
                                <Typography variant="caption" color="text.secondary">
                                  No time ranges — using the fixed window above.
                                </Typography>
                              ) : (
                                cancellationPolicy.session.schedule.map((r, index) => (
                                  <Box key={index} display="flex" alignItems="center" gap={1}>
                                    <TextField
                                      type="time"
                                      size="small"
                                      label="From"
                                      value={r.startTime}
                                      onChange={e => updateCancellationRange(index, 'startTime', e.target.value)}
                                      InputLabelProps={{ shrink: true }}
                                      inputProps={{ step: 60 }}
                                    />
                                    <TextField
                                      type="time"
                                      size="small"
                                      label="To"
                                      value={r.endTime}
                                      onChange={e => updateCancellationRange(index, 'endTime', e.target.value)}
                                      InputLabelProps={{ shrink: true }}
                                      inputProps={{ step: 60 }}
                                    />
                                    <TextField
                                      type="number"
                                      size="small"
                                      label="Cutoff (hrs)"
                                      value={r.cutoffHours}
                                      onChange={e => {
                                        const v = parseFloat(e.target.value);
                                        updateCancellationRange(index, 'cutoffHours', isNaN(v) ? 0 : v);
                                      }}
                                      inputProps={{ min: 0, max: 72, step: 0.5 }}
                                      sx={{ width: 110 }}
                                    />
                                    <IconButton size="small" onClick={() => removeCancellationRange(index)}>
                                      <DeleteOutline fontSize="small" />
                                    </IconButton>
                                  </Box>
                                ))
                              )}
                              <TimeRangeAudit
                                  ranges={cancellationPolicy.session.schedule || []}
                                  fallbackHours={
                                    cancellationPolicy.session.type === 'custom'
                                      ? cancellationPolicy.session.customHours ?? 24
                                      : 24
                                  }
                                />
                            </Box>
                          )}
                        </Box>

                        <Divider />

                        {/* Event Policy */}
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600} mb={2} color="primary">
                            Event Cancellation
                          </Typography>
                          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                            <Box>
                              <Typography variant="body1" fontWeight={600} mb={0.5}>
                                Policy Type
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Standard = 24-hour window; Custom = define your own cutoff
                              </Typography>
                            </Box>
                            <FormControl size="small" sx={{ minWidth: 160 }}>
                              <InputLabel>Type</InputLabel>
                              <Select
                                label="Type"
                                value={cancellationPolicy.event.type}
                                onChange={(e) => {
                                  setCancellationPolicy(prev => ({
                                    ...prev,
                                    event: { ...prev.event, type: e.target.value as 'standard' | 'custom' },
                                  }));
                                  setCancellationPolicyChanged(true);
                                }}
                              >
                                <MenuItem value="standard">Standard (24h)</MenuItem>
                                <MenuItem value="custom">Custom</MenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                          {cancellationPolicy.event.type === 'custom' && (
                            <Box display="flex" alignItems="center" gap={2}>
                              <Typography variant="body2" color="text.secondary">
                                Cancel at least
                              </Typography>
                              <TextField
                                type="number"
                                size="small"
                                value={cancellationPolicy.event.customHours}
                                onChange={(e) => {
                                  const h = Math.max(1, parseInt(e.target.value) || 1);
                                  setCancellationPolicy(prev => ({
                                    ...prev,
                                    event: { ...prev.event, customHours: h },
                                  }));
                                  setCancellationPolicyChanged(true);
                                }}
                                inputProps={{ min: 1, max: 720 }}
                                sx={{ width: 100 }}
                              />
                              <Typography variant="body2" color="text.secondary">
                                hours before the event for a full refund
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </>
                    )}

                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}

          {activeTab === 8 && (
            /* Advertisements Tab */
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" fontWeight={600}>
                  Studio Advertisements ({advertisements.length})
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => { setSelectedAd(null); setAdFormDialogOpen(true); }}
                  size="small"
                  sx={{ backgroundColor: colors.primary }}
                >
                  Create Advertisement
                </Button>
              </Box>

              {advertisementsLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : advertisements.length === 0 ? (
                <Card sx={{ border: `1px solid ${colors.divider}`, p: 4, textAlign: 'center' }}>
                  <Campaign sx={{ fontSize: 48, color: colors.textSecondary, mb: 2, opacity: 0.3 }} />
                  <Typography variant="body1" color="text.secondary" mb={2}>
                    No advertisements for this studio
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => { setSelectedAd(null); setAdFormDialogOpen(true); }}
                    sx={{ backgroundColor: colors.primary }}
                  >
                    Create First Advertisement
                  </Button>
                </Card>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 2 }}>
                  {advertisements.map((ad) => (
                    <Card
                      key={ad._id}
                      sx={{
                        border: `1px solid ${colors.divider}`,
                        borderRadius: 2,
                        '&:hover': { boxShadow: 4 },
                      }}
                    >
                      <Box position="relative">
                        {ad.image ? (
                          <Box
                            component="img"
                            src={ad.image}
                            alt={ad.title || 'Advertisement'}
                            sx={{
                              width: '100%',
                              height: 160,
                              objectFit: 'cover',
                              borderTopLeftRadius: 8,
                              borderTopRightRadius: 8,
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: 160,
                              backgroundColor: colors.backgroundTertiary,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderTopLeftRadius: 8,
                              borderTopRightRadius: 8,
                            }}
                          >
                            <ImageIcon sx={{ fontSize: 40, color: colors.textSecondary, opacity: 0.4 }} />
                          </Box>
                        )}
                        <Box position="absolute" top={8} right={8} display="flex" gap={1}>
                          <Chip
                            label={ad.type === 'banner' ? 'Banner' : 'Ad'}
                            size="small"
                            sx={{ backgroundColor: ad.type === 'banner' ? colors.primary : colors.secondary, color: 'white', fontWeight: 600 }}
                          />
                          <Chip
                            label={ad.isLive ? 'Live' : 'Inactive'}
                            size="small"
                            sx={{ backgroundColor: ad.isLive ? colors.success : colors.greyText3, color: 'white', fontWeight: 600 }}
                          />
                        </Box>
                      </Box>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                          <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
                            {ad.title || 'Untitled'}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={(e) => { setAdAnchorEl(e.currentTarget); setSelectedAd(ad); }}
                          >
                            <MoreVert fontSize="small" />
                          </IconButton>
                        </Box>
                        {ad.buttonTitle && (
                          <Typography variant="body2" color="text.secondary" mt={0.5}>
                            Button: {ad.buttonTitle}
                          </Typography>
                        )}
                        <Box display="flex" justifyContent="space-between" alignItems="center" mt={1.5}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={ad.isLive}
                                size="small"
                                disabled={adTogglingId === ad._id}
                                onChange={async () => {
                                  setAdTogglingId(ad._id);
                                  try {
                                    await apiService.toggleAdvertisementStatus(ad._id, !ad.isLive);
                                    await fetchAdvertisements();
                                  } catch (err) {
                                    console.error(err);
                                  } finally {
                                    setAdTogglingId(null);
                                  }
                                }}
                              />
                            }
                            label={<Typography variant="caption">{ad.isLive ? 'Live' : 'Inactive'}</Typography>}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {new Date(ad.createdAt).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {activeTab === 9 && (
            /* Branches Tab */
            <BranchesTab
              studioId={studio._id}
              studioName={studio.studioName}
              brand={studio.brand}
            />
          )}

          {activeTab === 10 && (
            /* Staff Tab */
            <StudioUsersPage
              studioId={studio._id}
              studioOwner={{ name: studio.studioName, email: studio.email }}
            />
          )}
          {activeTab === 11 && studioId && (
            /* Announcements Tab */
            <AnnouncementsTab studioId={studioId} />
          )}

          {activeTab === 13 && studioId && (
            /* Founders Tab */
            <FoundersTab studioId={studioId} />
          )}

          {activeTab === 14 && studioId && (
            /* FAQs Tab */
            <FaqsTab studioId={studioId} />
          )}

          {activeTab === 15 && studioId && (
            /* Gallery Tab */
            <GalleryTab studioId={studioId} />
          )}

          {activeTab === 16 && studioId && (
            /* Holidays Tab */
            <HolidaysTab studioId={studioId} />
          )}
        </Box>
      </Card>

      {/* Advertisement Form Dialog */}
      <AdvertisementFormDialog
        key={selectedAd?._id || 'create-ad'}
        open={adFormDialogOpen}
        defaultStudioId={studioId}
        onClose={() => { setAdFormDialogOpen(false); setSelectedAd(null); }}
        onSuccess={() => { fetchAdvertisements(); setAdFormDialogOpen(false); setSelectedAd(null); }}
        advertisement={selectedAd ? {
          _id: selectedAd._id,
          type: selectedAd.type,
          title: selectedAd.title || '',
          buttonTitle: selectedAd.buttonTitle || '',
          description: selectedAd.description || '',
          footerMessage: selectedAd.footerMessage || '',
          image: selectedAd.image || '',
          link: selectedAd.link || '',
          studio: typeof selectedAd.studio === 'string' ? selectedAd.studio : selectedAd.studio?._id || studioId || '',
          event: typeof selectedAd.event === 'string' ? selectedAd.event : selectedAd.event?._id || '',
          session: typeof selectedAd.session === 'string' ? selectedAd.session : selectedAd.session?._id || '',
          isLive: selectedAd.isLive,
        } : undefined}
      />

      {/* Advertisement Actions Menu */}
      <Menu
        anchorEl={adAnchorEl}
        open={Boolean(adAnchorEl)}
        onClose={() => { setAdAnchorEl(null); setSelectedAd(null); }}
      >
        <MenuItem onClick={() => { setAdAnchorEl(null); setAdFormDialogOpen(true); }}>
          <Edit fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={async () => {
            if (!selectedAd) return;
            setAdAnchorEl(null);
            if (!window.confirm('Delete this advertisement?')) return;
            try {
              await apiService.deleteAdvertisement(selectedAd._id);
              await fetchAdvertisements();
            } catch (err) {
              console.error(err);
            } finally {
              setSelectedAd(null);
            }
          }}
          sx={{ color: 'error.main' }}
        >
          <Delete fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Create Event Dialog */}
      <EventFormDialog
        open={createEventDialogOpen}
        onClose={() => setCreateEventDialogOpen(false)}
        onSuccess={async () => {
          // Refresh events and studio details from API to get fresh data.
          // The dialog stays open to show the new event's share links and
          // closes itself via onClose (Done / X).
          await fetchEvents({ silent: true });
          await fetchStudioDetails();
          setActiveTab(1); // Switch to Events tab
        }}
        studioId={studioId}
      />

      {/* Create Sessions — one session, a range, this location or several */}
      {studioId && (
        <CreateSessionsDialog
          open={createSessionDialogOpen}
          onClose={() => setCreateSessionDialogOpen(false)}
          onSuccess={() => {
            void fetchSessionsForDate(selectedCategoryDate);
            refreshMonthSessionDots(selectedCategoryDate);
            void fetchSessions({ silent: true }); // Overview tab's aggregate list/count — separate cache
          }}
          studioId={studioId}
          preselect={[studioId]}
          defaultStartDate={selectedCategoryDate}
        />
      )}

      {/* Copy Day — this location's day to other dates */}
      {studioId && (
        <CopyDayDialog
          open={copyDayDialogOpen}
          onClose={() => setCopyDayDialogOpen(false)}
          targetDate={new Date(`${selectedCategoryDate}T00:00:00`)}
          studioId={studioId}
          onSuccess={() => {
            setCopyDayDialogOpen(false);
            void fetchSessionsForDate(selectedCategoryDate);
            refreshMonthSessionDots(selectedCategoryDate);
            void fetchSessions({ silent: true });
          }}
        />
      )}

      {/* Bulk Delete Sessions Dialog */}
      {studioId && (
        <BulkDeleteSessionDialog
          open={bulkDeleteSessionDialogOpen}
          onClose={() => setBulkDeleteSessionDialogOpen(false)}
          onSuccess={() => {
            fetchSessionsForDate(selectedCategoryDate);
            refreshMonthSessionDots(selectedCategoryDate);
            void fetchSessions({ silent: true }); // Overview tab's aggregate list/count — separate cache
          }}
          studioId={studioId}
        />
      )}

      {/* Bulk Sessions Workspace — find a range, review, then edit or delete */}
      {studioId && (
        <BulkSessionWorkspace
          open={bulkSessionWorkspaceOpen}
          onClose={() => setBulkSessionWorkspaceOpen(false)}
          onSuccess={() => {
            fetchSessionsForDate(selectedCategoryDate);
            refreshMonthSessionDots(selectedCategoryDate);
            void fetchSessions({ silent: true }); // Overview tab's aggregate list/count — separate cache
          }}
          studioId={studioId}
        />
      )}

      {/* Create Instructor Dialog */}
      <InstructorFormDialog
        open={createInstructorDialogOpen}
        onClose={() => setCreateInstructorDialogOpen(false)}
        onSuccess={async () => {
          // Refresh instructors from API to get fresh data
          await fetchInstructors({ silent: true });
          setCreateInstructorDialogOpen(false);
        }}
        studioId={studioId!}
      />

      {/* Several locations of this studio's brand */}
      {multiLocation && (
        <>
          <CreateClassAtLocationsDialog
            open={multiOpen === 'class'}
            onClose={() => { setMultiOpen(null); void fetchClasses({ silent: true }); }}
            onCreated={() => void fetchClasses({ silent: true })}
            anchorStudioId={studioId}
            preselect={studioId ? [studioId] : []}
          />
          <ImportClassesDialog
            open={multiOpen === 'importClasses'}
            onClose={() => { setMultiOpen(null); void fetchClasses({ silent: true }); }}
            onCreated={() => void fetchClasses({ silent: true })}
            anchorStudioId={studioId}
            preselectTarget={studioId}
          />
          <ImportSessionsDialog
            open={multiOpen === 'importSessions'}
            onClose={() => { setMultiOpen(null); void fetchSessionsForDate(selectedCategoryDate); refreshMonthSessionDots(selectedCategoryDate); void fetchSessions({ silent: true }); }}
            onCreated={() => undefined}
            anchorStudioId={studioId}
            preselectTarget={studioId}
          />
        </>
      )}

      {/* Create Class Dialog */}
      <ClassFormDialog
        open={createClassDialogOpen}
        onClose={() => setCreateClassDialogOpen(false)}
        onSuccess={async () => {
          // Refresh classes from API to get fresh data
          await fetchClasses({ silent: true });
          setCreateClassDialogOpen(false);
        }}
        studioId={studioId!}
        enableSessionTypes={studio.studioPermissions?.enableSessionTypes}
      />

      {/* Edit Studio Dialog */}
      <StudioFormDialog
        open={editStudioDialogOpen}
        onClose={() => setEditStudioDialogOpen(false)}
        onSuccess={() => {
          fetchStudioDetails();
        }}
        studio={studio}
      />

      {/* Edit Event Dialog */}
      <EventFormDialog
        open={editEventDialogOpen}
        onClose={() => {
          setEditEventDialogOpen(false);
          setSelectedEvent(null);
          setEventAnchorEl(null);
        }}
        onSuccess={async () => {
          // Refresh events and studio details from API to get fresh data
          await fetchEvents({ silent: true });
          await fetchStudioDetails();
          setEditEventDialogOpen(false);
          setSelectedEvent(null);
          setEventAnchorEl(null);
        }}
        event={selectedEvent}
        studioId={studioId}
      />

      {/* Duplicate Event Dialog — create-mode, pre-filled from selectedEvent (not live) */}
      {duplicateEventDialogOpen && selectedEvent && (
        <EventFormDialog
          open={duplicateEventDialogOpen}
          onClose={() => {
            setDuplicateEventDialogOpen(false);
            setSelectedEvent(null);
            setEventAnchorEl(null);
          }}
          onSuccess={async () => {
            await fetchEvents();
            await fetchStudioDetails();
            setDuplicateEventDialogOpen(false);
            setSelectedEvent(null);
            setEventAnchorEl(null);
            setActiveTab(1);
          }}
          duplicateFrom={selectedEvent}
          studioId={studioId}
        />
      )}

      {/* Edit Session Dialog */}
      <SessionFormDialog
        open={editSessionDialogOpen}
        onClose={() => {
          setEditSessionDialogOpen(false);
          setSelectedSession(null);
        }}
        onSuccess={async () => {
          // Refresh sessions and studio details from API to get fresh data
          await fetchSessionsForDate(selectedCategoryDate);
          refreshMonthSessionDots(selectedCategoryDate);
          void fetchSessions({ silent: true }); // Overview tab's aggregate list/count — separate cache
          await fetchStudioDetails();
          setEditSessionDialogOpen(false);
          setSelectedSession(null);
        }}
        session={selectedSession}
        studioId={studioId}
      />

      {/* Edit Class Dialog */}
      <ClassFormDialog
        open={editClassDialogOpen}
        onClose={() => {
          setEditClassDialogOpen(false);
          setSelectedClass(null);
          setClassAnchorEl(null);
        }}
        onSuccess={async () => {
          // Refresh classes from API to get fresh data
          await fetchClasses({ silent: true });
          setEditClassDialogOpen(false);
          setSelectedClass(null);
          setClassAnchorEl(null);
        }}
        classItem={selectedClass}
        studioId={studioId!}
        enableSessionTypes={studio.studioPermissions?.enableSessionTypes}
      />

      {/* Edit Instructor Dialog */}
      <InstructorFormDialog
        open={editInstructorDialogOpen}
        onClose={() => {
          setEditInstructorDialogOpen(false);
          setSelectedInstructor(null);
          setInstructorAnchorEl(null);
        }}
        onSuccess={async () => {
          // Refresh instructors from API to get fresh data
          await fetchInstructors({ silent: true });
          setEditInstructorDialogOpen(false);
          setSelectedInstructor(null);
          setInstructorAnchorEl(null);
        }}
        instructor={selectedInstructor}
        studioId={studioId!}
      />

      {/* Remove instructor — confirm, and hand the work over */}
      <Dialog
        open={removeInstructorOpen}
        onClose={isRemovingInstructor ? undefined : closeRemoveInstructor}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Revoke {instructorToRemove?.name || 'this instructor'}'s access here?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            They are taken off this studio and their login can no longer open it. Nothing is
            deleted: past classes keep their name, any other studio they teach at is not
            touched, and you can reactivate them here any time.
          </Typography>

          {removeError && <Alert severity="error" sx={{ mt: 2 }}>{removeError}</Alert>}

          {/* Only after the backend refuses: name who takes the work over. */}
          {removeWork && (
            <Box mt={2}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Still attached to:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {([
                    [removeWork.sessionCount, 'upcoming session', 'upcoming sessions'],
                    [removeWork.classCount, 'class', 'classes'],
                    [removeWork.eventCount, 'event', 'events'],
                    [removeWork.wellnessPoolCount + removeWork.serviceCount,
                      'wellness listing', 'wellness listings'],
                    [removeWork.bookingCount, 'member booking', 'member bookings'],
                  ] as [number, string, string][])
                    .filter(([n]) => n > 0)
                    .map(([n, one, many]) => (
                      <li key={many}><strong>{n}</strong> {n === 1 ? one : many}</li>
                    ))}
                </Box>
                {removeWork.bookingCount > 0 && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.75 }}>
                    {removeWork.memberCount > 0
                      ? `${removeWork.memberCount} ${removeWork.memberCount === 1 ? 'member has' : 'members have'} already booked those — they will see a different name on a class they paid for.`
                      : 'Members have already booked those — they will see a different name on a class they paid for.'}
                  </Typography>
                )}
                {removeWork.nextSessionAt && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    Next one:{' '}
                    {new Date(removeWork.nextSessionAt).toLocaleDateString('en-IN', {
                      weekday: 'short', day: 'numeric', month: 'short',
                    })}
                  </Typography>
                )}
              </Alert>

              <FormControl fullWidth size="small">
                <InputLabel>Who takes these over?</InputLabel>
                <Select
                  label="Who takes these over?"
                  value={removeReplacementId}
                  onChange={(e) => setRemoveReplacementId(e.target.value)}
                >
                  {instructors.filter(
                    (i) => i._id !== instructorToRemove?._id && !(i as any).isDisabled,
                  ).length === 0 && (
                    <MenuItem value="" disabled>
                      No other active instructor at this studio
                    </MenuItem>
                  )}
                  {instructors
                    .filter((i) => i._id !== instructorToRemove?._id && !(i as any).isDisabled)
                    .map((i) => (
                      <MenuItem key={i._id} value={i._id}>{i.name}</MenuItem>
                    ))}
                </Select>
              </FormControl>

              {/* Only worth offering when somebody is actually affected. */}
              {removeWork.bookingCount > 0 && (
                <FormControlLabel
                  sx={{ mt: 1.5, alignItems: 'flex-start' }}
                  control={
                    <Checkbox
                      checked={removeNotifyMembers}
                      onChange={(e) => setRemoveNotifyMembers(e.target.checked)}
                      sx={{ mt: -0.75 }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">
                        Tell{' '}
                        {removeWork.memberCount > 0
                          ? `${removeWork.memberCount} affected ${removeWork.memberCount === 1 ? 'member' : 'members'}`
                          : 'affected members'}{' '}
                        about the change
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        One push and email each, naming the new instructor. Their bookings
                        are not changed. Sent only after the handover succeeds.
                      </Typography>
                    </Box>
                  }
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRemoveInstructor} disabled={isRemovingInstructor}>
            Cancel
          </Button>
          <Button
            onClick={confirmRemoveInstructor}
            color="error"
            variant="contained"
            /* A handover cannot be confirmed until somebody is named. */
            disabled={isRemovingInstructor || (!!removeWork && !removeReplacementId)}
          >
            {isRemovingInstructor
              ? <CircularProgress size={20} />
              : removeWork ? 'Reassign & revoke' : 'Revoke access'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Instructor Menu */}
      <Menu
        anchorEl={instructorAnchorEl}
        open={Boolean(instructorAnchorEl)}
        onClose={() => setInstructorAnchorEl(null)}
      >
        <MenuItem onClick={() => {
          setInstructorAnchorEl(null);
          setEditInstructorDialogOpen(true);
        }}>
          <Edit sx={{ fontSize: 20, mr: 1 }} /> Edit
        </MenuItem>
        {selectedInstructor?.isDisabled ? (
          <MenuItem
            onClick={() => {
              setInstructorAnchorEl(null);
              if (selectedInstructor) void handleEnableInstructor(selectedInstructor);
            }}
            disabled={isEnablingInstructor}
            sx={{ color: 'success.main' }}
          >
            <CheckCircleOutline sx={{ fontSize: 20, mr: 1 }} /> Reactivate
          </MenuItem>
        ) : (
          <MenuItem onClick={() => {
            setInstructorAnchorEl(null);
            setInstructorToRemove(selectedInstructor);
            setRemoveWork(null);
            setRemoveReplacementId('');
            setRemoveNotifyMembers(false);
            setRemoveError(null);
            setRemoveInstructorOpen(true);
          }} sx={{ color: 'error.main' }}>
            <Delete sx={{ fontSize: 20, mr: 1 }} /> Revoke access
          </MenuItem>
        )}
      </Menu>

      {/* Class Menu */}
      <Menu
        anchorEl={classAnchorEl}
        open={Boolean(classAnchorEl)}
        onClose={() => setClassAnchorEl(null)}
      >
        <MenuItem onClick={() => {
          setClassAnchorEl(null);
          setEditClassDialogOpen(true);
        }}>
          <Edit sx={{ fontSize: 20, mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={() => {
          setClassAnchorEl(null);
          // Delete class
        }} sx={{ color: 'error.main' }}>
          <Delete sx={{ fontSize: 20, mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Event Menu */}
      <Menu
        anchorEl={eventAnchorEl}
        open={Boolean(eventAnchorEl)}
        onClose={() => {
          setEventAnchorEl(null);
          // Don't reset selectedEvent here - it's needed for edit/delete dialogs
        }}
      >
        <MenuItem onClick={() => {
          setEventAnchorEl(null);
          setEditEventDialogOpen(true);
        }}>
          <Edit sx={{ fontSize: 20, mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={() => {
          setEventAnchorEl(null);
          setDuplicateEventDialogOpen(true);
        }}>
          <ContentCopy sx={{ fontSize: 20, mr: 1 }} /> Duplicate
        </MenuItem>
        <MenuItem onClick={() => {
          setEventAnchorEl(null);
          setDeleteEventDialogOpen(true);
        }} sx={{ color: 'error.main' }}>
          <Delete sx={{ fontSize: 20, mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Session Menu */}
      <Menu
        anchorEl={sessionAnchorEl}
        open={Boolean(sessionAnchorEl)}
        onClose={() => {
          setSessionAnchorEl(null);
          setSelectedSession(null);
        }}
      >
        <MenuItem onClick={() => {
          setSessionAnchorEl(null);
          setEditSessionDialogOpen(true);
        }}>
          <Edit sx={{ fontSize: 20, mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={() => {
          setSessionAnchorEl(null);
          setDeleteSessionDialogOpen(true);
        }} sx={{ color: 'error.main' }}>
          <Delete sx={{ fontSize: 20, mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Delete Event Confirmation Dialog */}
      <Dialog open={deleteEventDialogOpen} onClose={() => setDeleteEventDialogOpen(false)}>
        <DialogTitle>Delete Event</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedEvent?.eventName}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteEventDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteEvent} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Session Confirmation Dialog */}
      <Dialog open={deleteSessionDialogOpen} onClose={() => setDeleteSessionDialogOpen(false)}>
        <DialogTitle>Delete Session</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedSession?.className}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteSessionDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteSession} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Studio Menu */}
      <Menu
        anchorEl={studioMenuAnchorEl}
        open={Boolean(studioMenuAnchorEl)}
        onClose={handleStudioMenuClose}
      >
        {isSuperAdmin && (
          <MenuItem onClick={handleDeleteStudio} sx={{ color: colors.error }}>
            <Delete sx={{ fontSize: 20, mr: 1 }} /> Delete Studio
          </MenuItem>
        )}
      </Menu>

      {/* Delete Studio Confirmation Dialog */}
      <Dialog 
        open={deleteStudioDialogOpen} 
        onClose={() => !isDeletingStudio && setDeleteStudioDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        disableEscapeKeyDown={isDeletingStudio}
      >
        <DialogTitle sx={{ color: colors.error, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ fontSize: '1.5rem' }}>⚠️</Box>
          Delete Studio - Critical Action
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              ⚠️ WARNING: This will permanently delete ALL associated data:
            </Typography>
            <Typography component="ul" variant="body2" sx={{ pl: 2, mb: 0, mt: 1 }}>
              <li><strong>All Events</strong> created by this studio</li>
              <li><strong>All Classes</strong> associated with this studio</li>
              <li><strong>All Sessions</strong> and bookings</li>
              <li><strong>All Instructors</strong> linked to this studio</li>
              <li><strong>All Reviews</strong> and ratings</li>
              <li><strong>All Promo Codes</strong>, transactions, and payout history</li>
              <li><strong>The Studio Account</strong> and all its data</li>
            </Typography>
          </Alert>
          
          <Box sx={{ bgcolor: colors.error + '10', p: 2, borderRadius: 1, mb: 2 }}>
            <Typography variant="body1" fontWeight={700} color={colors.error} gutterBottom>
              Studio to be deleted:
            </Typography>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              "{studio?.studioName}"
            </Typography>
            {studio?.email && (
              <Typography variant="body2" color="text.secondary">
                Email: {studio.email}
              </Typography>
            )}
          </Box>

          <Typography variant="body1" fontWeight={600} gutterBottom>
            Are you absolutely certain you want to proceed?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action <strong>CANNOT BE UNDONE</strong>. All events, classes, and related data will be permanently removed from the system.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setDeleteStudioDialogOpen(false)} 
            variant="outlined"
            disabled={isDeletingStudio}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDeleteStudio} 
            color="error" 
            variant="contained"
            disabled={isDeletingStudio}
            startIcon={isDeletingStudio ? <CircularProgress size={16} color="inherit" /> : <Delete />}
            sx={{ fontWeight: 600 }}
          >
            {isDeletingStudio ? 'Deleting...' : 'Yes, Delete Permanently'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Bank Details Popup Frequency Dialog */}
      <Dialog
        open={editBankDetailsDialogOpen}
        onClose={() => setEditBankDetailsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={700}>
              Configure Bank Details Popup Frequency
            </Typography>
            <IconButton
              size="small"
              onClick={() => setEditBankDetailsDialogOpen(false)}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Popup Frequency</InputLabel>
              <Select
                value={bankDetailsPopupFrequency}
                onChange={(e) => setBankDetailsPopupFrequency(e.target.value as 'one-time' | 'weekly' | 'monthly')}
                label="Popup Frequency"
              >
                <MenuItem value="one-time">One Time</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              This setting controls how often the bank details popup will be shown to the studio partner in the partner app.
              <br />
              • <strong>One Time:</strong> Show only once until bank details are filled
              <br />
              • <strong>Weekly:</strong> Show once per week if bank details are not filled
              <br />
              • <strong>Monthly:</strong> Show once per month if bank details are not filled
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setEditBankDetailsDialogOpen(false)}
            variant="outlined"
            disabled={isUpdatingBankDetails}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateBankDetailsPopupFrequency}
            variant="contained"
            disabled={isUpdatingBankDetails}
            startIcon={isUpdatingBankDetails ? <CircularProgress size={16} color="inherit" /> : <Settings />}
            sx={{ fontWeight: 600 }}
          >
            {isUpdatingBankDetails ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Location (Lat/Long) Dialog */}
      <Dialog
        open={editLocationDialogOpen}
        onClose={() => setEditLocationDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={700}>
              Update Location Coordinates
            </Typography>
            <IconButton
              size="small"
              onClick={() => setEditLocationDialogOpen(false)}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Latitude"
              type="number"
              value={latitude}
              onChange={(e) => setLatitude(e.target.value)}
              placeholder="e.g., 13.060303"
              helperText="Latitude must be between -90 and 90"
              sx={{ mb: 2 }}
              InputProps={{
                inputProps: {
                  step: 'any',
                  min: -90,
                  max: 90,
                },
              }}
            />
            <TextField
              fullWidth
              label="Longitude"
              type="number"
              value={longitude}
              onChange={(e) => setLongitude(e.target.value)}
              placeholder="e.g., 77.587373"
              helperText="Longitude must be between -180 and 180"
              InputProps={{
                inputProps: {
                  step: 'any',
                  min: -180,
                  max: 180,
                },
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              <strong>Note:</strong> Coordinates are stored in GeoJSON format [longitude, latitude].
              <br />
              • Latitude: -90 to 90 (North/South)
              <br />
              • Longitude: -180 to 180 (East/West)
              <br />
              • Example: Bangalore coordinates are approximately Lat: 12.9716, Lng: 77.5946
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setEditLocationDialogOpen(false)}
            variant="outlined"
            disabled={isUpdatingLocation}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateLocation}
            variant="contained"
            disabled={isUpdatingLocation}
            startIcon={isUpdatingLocation ? <CircularProgress size={16} color="inherit" /> : <LocationOn />}
            sx={{ fontWeight: 600 }}
          >
            {isUpdatingLocation ? 'Updating...' : 'Update Location'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating "unsaved changes" bar — stays on screen regardless of scroll
          position, so a save action is visible as soon as a field changes
          instead of only at the bottom of a long tab. */}
      <Fade in={pendingSaveActions.length > 0}>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSaveAllPending}
          disabled={savingAllChanges}
          startIcon={savingAllChanges ? <CircularProgress size={14} color="inherit" /> : <Save fontSize="small" />}
          sx={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1300,
            whiteSpace: 'nowrap',
            boxShadow: 8,
          }}
        >
          {savingAllChanges
            ? 'Saving...'
            : pendingSaveActions.length > 1
              ? `Save Changes (${pendingSaveActions.length})`
              : 'Save Changes'}
        </Button>
      </Fade>
    </Box>
  );
};

export default StudioDetailPage;

interface HolidayItem {
  date: string;
  reason: string;
  declaredBy: string | null;
  affectedBookingsCount: number;
  status: 'pending' | 'processed' | 'cancelled';
}

const HolidaysTab: React.FC<{ studioId: string }> = ({ studioId }) => {
  const [holidays, setHolidays] = useState<HolidayItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ date: '', reason: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const todayIST = new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
  // Studio roles: pin writes to THIS studio — under "All locations" the
  // switcher sends __all__, which the server refuses for writes.
  const writePin = apiService.isStudio() ? apiService.atStudio(studioId) : undefined;

  const fetchHolidays = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.get(`/api/v1/studios/${studioId}/holidays?all=true`);
      const data = res?.data ?? res;
      setHolidays(data?.holidays ?? []);
    } catch {
      setError('Failed to load holidays');
    } finally {
      setLoading(false);
    }
  }, [studioId]);

  useEffect(() => { fetchHolidays(); }, [fetchHolidays]);

  const handleDeclare = async () => {
    if (!form.date) { setError('Please pick a date'); return; }
    setError('');
    setSuccess('');
    try {
      // Dry-run first so the owner sees how many bookings will be cancelled.
      const dry = await apiService.post(`/api/v1/studios/${studioId}/holidays`, {
        date: form.date, reason: form.reason.trim(), dryRun: true,
      }, writePin);
      const count = (dry?.data ?? dry)?.affectedBookingsCount ?? 0;
      if (!window.confirm(
        `Declare a holiday on ${form.date}?\n\n${count} active booking(s) will be cancelled (refunds/credits per your studio policy; non-refunded trials/drop-ins can be rescheduled by users).`,
      )) return;
      setSubmitting(true);
      await apiService.post(`/api/v1/studios/${studioId}/holidays`, {
        date: form.date, reason: form.reason.trim(),
      }, writePin);
      setForm({ date: '', reason: '' });
      setSuccess('Holiday declared. Affected bookings have been processed.');
      fetchHolidays();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to declare holiday');
    } finally {
      setSubmitting(false);
    }
  };

  const dateKey = (iso: string) => new Date(new Date(iso).getTime() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);

  const handleDelete = async (date: string) => {
    if (!window.confirm('Remove this holiday? Already-cancelled bookings will NOT be reopened.')) return;
    try {
      // Send the IST calendar date (matches how the backend stores/matches the day).
      await apiService.delete(`/api/v1/studios/${studioId}/holidays/${dateKey(date)}`, writePin);
      fetchHolidays();
    } catch {
      setError('Failed to remove holiday');
    }
  };

  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });

  return (
    <Box>
      <Card sx={{ mb: 3, border: `1px solid ${colors.divider}` }}>
        <CardContent>
          <Typography variant="h6" fontWeight={700} gutterBottom>Declare Holiday</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Close the studio for a date. All that day's session bookings are cancelled — pack credits restored and online refunds issued per your cancellation policy. Trials/drop-ins that aren't refunded can be rescheduled by users from their app.
          </Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Date" type="date" value={form.date}
              onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
              fullWidth size="small" InputLabelProps={{ shrink: true }}
              inputProps={{ min: todayIST }}
            />
            <TextField
              label="Reason (optional)" value={form.reason}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
              fullWidth size="small" placeholder="e.g. Diwali Holiday" inputProps={{ maxLength: 120 }}
            />
            <Box display="flex" justifyContent="flex-end">
              <Button variant="contained" onClick={handleDeclare} disabled={submitting}
                startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <BeachAccess />}>
                {submitting ? 'Declaring…' : 'Declare Holiday'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Typography variant="h6" fontWeight={700} gutterBottom>Declared Holidays</Typography>
      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress size={28} sx={{ color: colors.primary }} /></Box>
      ) : holidays.length === 0 ? (
        <Box display="flex" flexDirection="column" alignItems="center" py={6} sx={{ color: colors.textSecondary }}>
          <BeachAccess sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
          <Typography variant="body1">No holidays declared</Typography>
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" gap={2}>
          {holidays.map((h) => (
            <Card key={dateKey(h.date)} sx={{ border: `1px solid ${colors.divider}` }}>
              <CardContent>
                <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <Typography variant="subtitle1" fontWeight={700}>{fmt(h.date)}</Typography>
                      <Chip label={h.status} size="small"
                        color={h.status === 'processed' ? 'success' : h.status === 'pending' ? 'warning' : 'default'} />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {h.reason || 'No reason given'} · {h.affectedBookingsCount} booking(s) cancelled
                    </Typography>
                  </Box>
                  <Button size="small" color="error" onClick={() => handleDelete(h.date)}>Remove</Button>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
};

// ─── Announcements Tab ────────────────────────────────────────────────────────

interface AnnouncementItem {
  _id: string;
  title: string;
  body: string;
  imageUrl: string | null;
  readCount: number;
  createdAt: string;
}

const AnnouncementsTab: React.FC<{ studioId: string }> = ({ studioId }) => {
  const announcementsFetchIdRef = useRef(0);
  const [announcements, setAnnouncements] = useState<AnnouncementItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [form, setForm] = useState({ title: '', body: '', imageUrl: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Studio roles: pin writes to THIS studio (see HolidaysTab).
  const writePin = apiService.isStudio() ? apiService.atStudio(studioId) : undefined;

  const fetchAnnouncements = useCallback(async () => {
    const fetchId = ++announcementsFetchIdRef.current;
    setLoading(true);
    try {
      const res = await apiService.get(`/api/v1/announcements/admin/studio/${studioId}`);
      if (fetchId !== announcementsFetchIdRef.current) return;
      setAnnouncements(Array.isArray(res) ? res : res.data ?? []);
    } catch {
      if (fetchId !== announcementsFetchIdRef.current) return;
      setError('Failed to load announcements');
    } finally {
      if (fetchId !== announcementsFetchIdRef.current) return;
      setLoading(false);
    }
  }, [studioId]);

  useEffect(() => { fetchAnnouncements(); }, [fetchAnnouncements]);

  const handlePublish = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      setError('Title and message are required');
      return;
    }
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      await apiService.post('/api/v1/announcements/admin', {
        studioId,
        title: form.title.trim(),
        body: form.body.trim(),
        ...(form.imageUrl.trim() ? { imageUrl: form.imageUrl.trim() } : {}),
      }, writePin);
      setForm({ title: '', body: '', imageUrl: '' });
      setSuccess('Announcement published successfully');
      fetchAnnouncements();
    } catch {
      setError('Failed to publish announcement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this announcement? This cannot be undone.')) return;
    setDeletingId(id);
    try {
      await apiService.delete(`/api/v1/announcements/admin/${id}?studioId=${studioId}`, writePin);
      setAnnouncements((prev) => prev.filter((a) => a._id !== id));
    } catch {
      setError('Failed to delete announcement');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box>
      {/* Create form */}
      <Card sx={{ mb: 3, border: `1px solid ${colors.divider}` }}>
        <CardContent>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            New Announcement
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Publish an announcement to all users at this studio immediately.
          </Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Title"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              fullWidth
              size="small"
              inputProps={{ maxLength: 100 }}
            />
            <TextField
              label="Message"
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              fullWidth
              multiline
              minRows={3}
              size="small"
              inputProps={{ maxLength: 1000 }}
            />
            <TextField
              label="Image URL (optional)"
              value={form.imageUrl}
              onChange={(e) => setForm((f) => ({ ...f, imageUrl: e.target.value }))}
              fullWidth
              size="small"
              placeholder="https://..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ImageIcon fontSize="small" sx={{ color: colors.textSecondary }} />
                  </InputAdornment>
                ),
              }}
            />
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <Send />}
                onClick={handlePublish}
                disabled={submitting}
                sx={{ backgroundColor: colors.primary, '&:hover': { backgroundColor: colors.primaryDark ?? colors.primary } }}
              >
                {submitting ? 'Publishing...' : 'Publish Now'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Announcements list */}
      <Typography variant="h6" fontWeight={700} gutterBottom>
        Published Announcements
      </Typography>
      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} sx={{ color: colors.primary }} />
        </Box>
      ) : announcements.length === 0 ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          py={6}
          sx={{ color: colors.textSecondary }}
        >
          <AnnouncementIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
          <Typography variant="body1">No announcements yet</Typography>
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" gap={2}>
          {announcements.map((a) => (
            <Card key={a._id} sx={{ border: `1px solid ${colors.divider}` }}>
              <CardContent>
                <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {a.title}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, whiteSpace: 'pre-wrap' }}>
                      {a.body}
                    </Typography>
                    {a.imageUrl && (
                      <Box
                        component="img"
                        src={a.imageUrl}
                        alt="announcement"
                        sx={{ width: '100%', maxWidth: 320, borderRadius: 1, mb: 1.5, objectFit: 'cover' }}
                      />
                    )}
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box display="flex" alignItems="center" gap={0.5} sx={{ color: colors.textSecondary }}>
                        <Visibility sx={{ fontSize: 15 }} />
                        <Typography variant="caption">{a.readCount} read</Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(a.createdAt).toLocaleString('en-IN', {
                          day: 'numeric', month: 'short', year: 'numeric',
                          hour: '2-digit', minute: '2-digit',
                        })}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(a._id)}
                    disabled={deletingId === a._id}
                    sx={{ color: colors.error ?? '#d32f2f', flexShrink: 0 }}
                  >
                    {deletingId === a._id
                      ? <CircularProgress size={16} />
                      : <DeleteOutline fontSize="small" />}
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Studio "Studio page" content tabs (Founders / FAQs / Gallery)
// Each tab loads the studio, edits an embedded array locally, and saves the
// whole array back via apiService.updateStudio (PATCH editStudio).
// ─────────────────────────────────────────────────────────────────────────────

interface FounderItem {
  name: string;
  role: string;
  image: string;
  bio: string;
  story: string;
  instagram: string;
  order: number;
}

const FoundersTab: React.FC<{ studioId: string }> = ({ studioId }) => {
  const foundersFetchIdRef = useRef(0);
  const [founders, setFounders] = useState<FounderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchFounders = useCallback(async () => {
    const fetchId = ++foundersFetchIdRef.current;
    setLoading(true);
    try {
      const res: any = await apiService.getStudioById(studioId);
      if (fetchId !== foundersFetchIdRef.current) return;
      const studio = res?.studio ?? res;
      const list: any[] = Array.isArray(studio?.founders) ? studio.founders : [];
      setFounders(
        list.map((f, i) => ({
          name: f?.name || '',
          role: f?.role || '',
          image: f?.image || '',
          bio: f?.bio || '',
          story: f?.story || '',
          instagram: f?.instagram || '',
          order: typeof f?.order === 'number' ? f.order : i,
        })),
      );
    } catch {
      if (fetchId !== foundersFetchIdRef.current) return;
      setError('Failed to load founders');
    } finally {
      if (fetchId !== foundersFetchIdRef.current) return;
      setLoading(false);
    }
  }, [studioId]);

  useEffect(() => { fetchFounders(); }, [fetchFounders]);

  const updateItem = (index: number, field: keyof FounderItem, value: any) => {
    setFounders((prev) => prev.map((it, i) => (i === index ? { ...it, [field]: value } : it)));
  };

  const addFounder = () => {
    setFounders((prev) => [
      ...prev,
      { name: '', role: '', image: '', bio: '', story: '', instagram: '', order: prev.length },
    ]);
  };

  const removeFounder = (index: number) => {
    setFounders((prev) => prev.filter((_, i) => i !== index));
  };

  const handleImageUpload = async (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError(`File "${file.name}" is not an image`);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size should be less than 5MB');
      return;
    }
    try {
      setUploadingIndex(index);
      setError('');
      const res = await apiService.uploadFiles([file], 'founders');
      const urls = res.fileLinks || [];
      if (urls.length > 0) updateItem(index, 'image', urls[0]);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to upload image');
    } finally {
      setUploadingIndex(null);
    }
  };

  const handleSave = async () => {
    // Only name and description (bio) are required; everything else is optional.
    const missing = founders.find((f) => !f.name.trim() || !f.bio.trim());
    if (missing) {
      setError('Each founder needs a name and a description.');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = founders
        .map((f) => ({
          name: f.name.trim(),
          role: f.role.trim(),
          image: f.image.trim(),
          bio: f.bio.trim(),
          story: f.story.trim(),
          instagram: f.instagram.trim(),
          order: Number.isFinite(f.order) ? f.order : 0,
        }))
        .sort((a, b) => a.order - b.order);
      await apiService.updateStudio(studioId, { founders: payload });
      setSuccess('Founders saved successfully');
      fetchFounders();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to save founders');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Founders</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Founders shown on the studio page in the mobile app. Lower order numbers appear first.
          </Typography>
        </Box>
        <Box display="flex" gap={1.5}>
          <Button variant="outlined" startIcon={<Add />} onClick={addFounder}>
            Add Founder
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Send />}
            onClick={handleSave}
            disabled={saving || loading}
            sx={{ backgroundColor: colors.primary, '&:hover': { backgroundColor: colors.primaryDark ?? colors.primary } }}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} sx={{ color: colors.primary }} />
        </Box>
      ) : founders.length === 0 ? (
        <Box display="flex" flexDirection="column" alignItems="center" py={6} sx={{ color: colors.textSecondary }}>
          <Groups sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
          <Typography variant="body1">No founders yet</Typography>
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" gap={2}>
          {founders.map((f, index) => (
            <Card key={index} sx={{ border: `1px solid ${colors.divider}` }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography variant="subtitle2" fontWeight={700} color={colors.textSecondary}>
                    Founder #{index + 1}
                  </Typography>
                  <IconButton size="small" onClick={() => removeFounder(index)} sx={{ color: colors.error ?? '#d32f2f' }}>
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Box>
                <Box display="flex" gap={2} flexWrap="wrap">
                  {/* Image */}
                  <Box sx={{ width: 140, flexShrink: 0 }}>
                    {f.image ? (
                      <Box position="relative">
                        <Box
                          component="img"
                          src={f.image}
                          alt={f.name || 'Founder'}
                          sx={{ width: 140, height: 140, objectFit: 'cover', borderRadius: 1, border: `1px solid ${colors.divider}` }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => updateItem(index, 'image', '')}
                          sx={{ position: 'absolute', top: 4, right: 4, backgroundColor: 'rgba(0,0,0,0.5)', color: 'white', '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' } }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<CloudUpload />}
                        disabled={uploadingIndex === index}
                        sx={{ width: 140, height: 140, borderStyle: 'dashed', flexDirection: 'column' }}
                      >
                        {uploadingIndex === index ? 'Uploading...' : 'Upload'}
                        <input type="file" hidden accept="image/*" onChange={(e) => handleImageUpload(index, e)} />
                      </Button>
                    )}
                  </Box>
                  {/* Fields */}
                  <Box flex={1} minWidth={260} display="flex" flexDirection="column" gap={2}>
                    <Box display="flex" gap={2} flexWrap="wrap">
                      <TextField
                        label="Name"
                        required
                        value={f.name}
                        onChange={(e) => updateItem(index, 'name', e.target.value)}
                        size="small"
                        sx={{ flex: 1, minWidth: 160 }}
                      />
                      <TextField
                        label="Role (optional)"
                        value={f.role}
                        onChange={(e) => updateItem(index, 'role', e.target.value)}
                        size="small"
                        sx={{ flex: 1, minWidth: 160 }}
                      />
                      <TextField
                        label="Order"
                        type="number"
                        value={f.order}
                        onChange={(e) => updateItem(index, 'order', parseInt(e.target.value, 10) || 0)}
                        size="small"
                        sx={{ width: 100 }}
                      />
                    </Box>
                    <TextField
                      label="Description"
                      required
                      value={f.bio}
                      onChange={(e) => updateItem(index, 'bio', e.target.value)}
                      size="small"
                      multiline
                      minRows={2}
                      fullWidth
                    />
                    <TextField
                      label="Story"
                      value={f.story}
                      onChange={(e) => updateItem(index, 'story', e.target.value)}
                      size="small"
                      multiline
                      minRows={3}
                      fullWidth
                    />
                    <TextField
                      label="Instagram"
                      value={f.instagram}
                      onChange={(e) => updateItem(index, 'instagram', e.target.value)}
                      size="small"
                      fullWidth
                      placeholder="@handle or URL"
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
};

interface FaqItem {
  question: string;
  answer: string;
  order: number;
}

const FaqsTab: React.FC<{ studioId: string }> = ({ studioId }) => {
  const faqsFetchIdRef = useRef(0);
  const [faqs, setFaqs] = useState<FaqItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchFaqs = useCallback(async () => {
    const fetchId = ++faqsFetchIdRef.current;
    setLoading(true);
    try {
      const res: any = await apiService.getStudioById(studioId);
      if (fetchId !== faqsFetchIdRef.current) return;
      const studio = res?.studio ?? res;
      const list: any[] = Array.isArray(studio?.faqs) ? studio.faqs : [];
      setFaqs(
        list.map((f, i) => ({
          question: f?.question || '',
          answer: f?.answer || '',
          order: typeof f?.order === 'number' ? f.order : i,
        })),
      );
    } catch {
      if (fetchId !== faqsFetchIdRef.current) return;
      setError('Failed to load FAQs');
    } finally {
      if (fetchId !== faqsFetchIdRef.current) return;
      setLoading(false);
    }
  }, [studioId]);

  useEffect(() => { fetchFaqs(); }, [fetchFaqs]);

  const updateItem = (index: number, field: keyof FaqItem, value: any) => {
    setFaqs((prev) => prev.map((it, i) => (i === index ? { ...it, [field]: value } : it)));
  };

  const addFaq = () => {
    setFaqs((prev) => [...prev, { question: '', answer: '', order: prev.length }]);
  };

  const removeFaq = (index: number) => {
    setFaqs((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = faqs
        .map((f) => ({
          question: f.question.trim(),
          answer: f.answer.trim(),
          order: Number.isFinite(f.order) ? f.order : 0,
        }))
        .sort((a, b) => a.order - b.order);
      await apiService.updateStudio(studioId, { faqs: payload });
      setSuccess('FAQs saved successfully');
      fetchFaqs();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to save FAQs');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h6" fontWeight={700}>FAQs</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Frequently asked questions shown on the studio page. Lower order numbers appear first.
          </Typography>
        </Box>
        <Box display="flex" gap={1.5}>
          <Button variant="outlined" startIcon={<Add />} onClick={addFaq}>
            Add FAQ
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Send />}
            onClick={handleSave}
            disabled={saving || loading}
            sx={{ backgroundColor: colors.primary, '&:hover': { backgroundColor: colors.primaryDark ?? colors.primary } }}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} sx={{ color: colors.primary }} />
        </Box>
      ) : faqs.length === 0 ? (
        <Box display="flex" flexDirection="column" alignItems="center" py={6} sx={{ color: colors.textSecondary }}>
          <QuestionAnswer sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
          <Typography variant="body1">No FAQs yet</Typography>
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" gap={2}>
          {faqs.map((f, index) => (
            <Card key={index} sx={{ border: `1px solid ${colors.divider}` }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography variant="subtitle2" fontWeight={700} color={colors.textSecondary}>
                    FAQ #{index + 1}
                  </Typography>
                  <IconButton size="small" onClick={() => removeFaq(index)} sx={{ color: colors.error ?? '#d32f2f' }}>
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Box>
                <Box display="flex" flexDirection="column" gap={2}>
                  <Box display="flex" gap={2} alignItems="flex-start">
                    <TextField
                      label="Question"
                      value={f.question}
                      onChange={(e) => updateItem(index, 'question', e.target.value)}
                      size="small"
                      fullWidth
                    />
                    <TextField
                      label="Order"
                      type="number"
                      value={f.order}
                      onChange={(e) => updateItem(index, 'order', parseInt(e.target.value, 10) || 0)}
                      size="small"
                      sx={{ width: 100, flexShrink: 0 }}
                    />
                  </Box>
                  <TextField
                    label="Answer"
                    value={f.answer}
                    onChange={(e) => updateItem(index, 'answer', e.target.value)}
                    size="small"
                    multiline
                    minRows={3}
                    fullWidth
                  />
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
};

interface GalleryItem {
  url: string;
  caption: string;
  order: number;
}

const GalleryTab: React.FC<{ studioId: string }> = ({ studioId }) => {
  const galleryFetchIdRef = useRef(0);
  const [gallery, setGallery] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchGallery = useCallback(async () => {
    const fetchId = ++galleryFetchIdRef.current;
    setLoading(true);
    try {
      const res: any = await apiService.getStudioById(studioId);
      if (fetchId !== galleryFetchIdRef.current) return;
      const studio = res?.studio ?? res;
      const list: any[] = Array.isArray(studio?.gallery) ? studio.gallery : [];
      setGallery(
        list.map((g, i) => ({
          url: g?.url || '',
          caption: g?.caption || '',
          order: typeof g?.order === 'number' ? g.order : i,
        })),
      );
    } catch {
      if (fetchId !== galleryFetchIdRef.current) return;
      setError('Failed to load gallery');
    } finally {
      if (fetchId !== galleryFetchIdRef.current) return;
      setLoading(false);
    }
  }, [studioId]);

  useEffect(() => { fetchGallery(); }, [fetchGallery]);

  const updateItem = (index: number, field: keyof GalleryItem, value: any) => {
    setGallery((prev) => prev.map((it, i) => (i === index ? { ...it, [field]: value } : it)));
  };

  const removeItem = (index: number) => {
    setGallery((prev) => prev.filter((_, i) => i !== index));
  };

  const moveItem = (index: number, dir: -1 | 1) => {
    setGallery((prev) => {
      const target = index + dir;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      // re-sequence order to reflect the new visual order
      return next.map((it, i) => ({ ...it, order: i }));
    });
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (files.length === 0) return;
    const invalid = files.find((f) => !f.type.startsWith('image/'));
    if (invalid) {
      setError(`File "${invalid.name}" is not an image`);
      return;
    }
    const tooBig = files.find((f) => f.size > 5 * 1024 * 1024);
    if (tooBig) {
      setError(`Image "${tooBig.name}" should be less than 5MB`);
      return;
    }
    try {
      setUploading(true);
      setError('');
      const res = await apiService.uploadFiles(files, 'gallery');
      const urls: string[] = res.fileLinks || [];
      setGallery((prev) => [
        ...prev,
        ...urls.map((url, i) => ({ url, caption: '', order: prev.length + i })),
      ]);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = gallery
        .map((g) => ({
          url: g.url.trim(),
          caption: g.caption.trim(),
          order: Number.isFinite(g.order) ? g.order : 0,
        }))
        .sort((a, b) => a.order - b.order);
      await apiService.updateStudio(studioId, { gallery: payload });
      setSuccess('Gallery saved successfully');
      fetchGallery();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to save gallery');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Gallery</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Photos shown in the studio page gallery. Use the arrows to reorder; lower order appears first.
          </Typography>
        </Box>
        <Box display="flex" gap={1.5}>
          <Button
            variant="outlined"
            component="label"
            startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <CloudUpload />}
            disabled={uploading}
          >
            {uploading ? 'Uploading...' : 'Upload Images'}
            <input type="file" hidden accept="image/*" multiple onChange={handleUpload} />
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Send />}
            onClick={handleSave}
            disabled={saving || loading}
            sx={{ backgroundColor: colors.primary, '&:hover': { backgroundColor: colors.primaryDark ?? colors.primary } }}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} sx={{ color: colors.primary }} />
        </Box>
      ) : gallery.length === 0 ? (
        <Box display="flex" flexDirection="column" alignItems="center" py={6} sx={{ color: colors.textSecondary }}>
          <Collections sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
          <Typography variant="body1">No gallery images yet</Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
            gap: 2,
          }}
        >
          {gallery.map((g, index) => (
            <Card key={index} sx={{ border: `1px solid ${colors.divider}` }}>
              <Box position="relative">
                <Box
                  component="img"
                  src={g.url}
                  alt={g.caption || `Gallery ${index + 1}`}
                  sx={{ width: '100%', height: 180, objectFit: 'cover', display: 'block' }}
                />
                <IconButton
                  size="small"
                  onClick={() => removeItem(index)}
                  sx={{ position: 'absolute', top: 4, right: 4, backgroundColor: 'rgba(0,0,0,0.5)', color: 'white', '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' } }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
              <CardContent sx={{ pb: '12px !important' }}>
                <TextField
                  label="Caption"
                  value={g.caption}
                  onChange={(e) => updateItem(index, 'caption', e.target.value)}
                  size="small"
                  fullWidth
                />
                <Box display="flex" alignItems="center" justifyContent="space-between" mt={1.5}>
                  <TextField
                    label="Order"
                    type="number"
                    value={g.order}
                    onChange={(e) => updateItem(index, 'order', parseInt(e.target.value, 10) || 0)}
                    size="small"
                    sx={{ width: 90 }}
                  />
                  <Box>
                    <IconButton size="small" onClick={() => moveItem(index, -1)} disabled={index === 0}>
                      <ArrowUpward fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => moveItem(index, 1)} disabled={index === gallery.length - 1}>
                      <ArrowDownward fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
};

